import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Button } from '../components/ui/Button'
import { Textarea } from '../components/ui/Textarea'
import {
  Loader2, Send, ChevronDown, ChevronRight, History, Plus,
  Sparkles, X, ArrowLeft, Clock,
  Target, Heart, BookOpen, Coffee, MessageSquare, Star,
  AlertTriangle, AlertCircle, Info, Check, Calendar, Map as MapIcon, ListTodo, RefreshCw,
  Mic, MicOff, Code2, Archive, Volume2, VolumeX, ScanFace
} from 'lucide-react'
import { useBranding } from '../hooks/useBranding'
import { HierarchyItemCard } from '../components/hierarchy/HierarchyItemCard'
import {
  acceptHierarchyItem,
  acceptV3EntireSubtree,
  type HierarchyItem
} from '../services/hierarchyItemService'
import { SimilarItemsDialog } from '../components/hierarchy/SimilarItemsDialog'
import type { SimilarItem } from '../services/similarityService'
import { PersonalitySelector } from '../components/modals/PersonalitySelector'
import { profileService } from '../services/profileService'
import { DreamService } from '../services/dreamService'
import { AspirationService } from '../services/aspirationService'
import { GoalService } from '../services/goalService'
import { TaskService } from '../services/taskService'
import { ActionItemService } from '../services/actionItemService'
import { SkillService } from '../services/skillService'
import { HabitService } from '../services/habitService'
import { RoadblockService } from '../services/roadblockService'
import { DEFAULT_APP_HOME } from '../config/routes'
import { API_BASE_URL } from '../config/api'

// Instantiate services
const dreamService = new DreamService()
const aspirationService = new AspirationService()
const goalService = new GoalService()
const taskService = new TaskService()
const actionItemService = new ActionItemService()
const skillService = new SkillService()
const habitService = new HabitService()
const roadblockService = new RoadblockService()
import {
  DreamHierarchyDisplay,
  type ItemState,
  type RejectedItem
} from '../components/ai/DreamHierarchyDisplay'
import { LifeMapView } from '../components/ai/LifeMapView'
import { LifecoachLifeMap } from '../components/ai/LifecoachLifeMap'
import { CostTracker } from '../components/ai/CostTracker'
import { MarkdownMessage } from '../components/ai/MarkdownMessage'
import { HortonFacePage } from '../components/ai/HortonFacePage'
import { HortonFacePager, type FacePagerPage } from '../components/ai/HortonFacePager'
import { DEFAULT_FACE, type FaceState } from '../presence/faces'
import { ConfirmationModal, useConfirmationModal, type ModalVariant } from '../components/ui/ConfirmationModal'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/Dialog'
import { CommitPreviewTree, convertToPreviewItems, convertFlatItemsToPreview } from '../components/ui/CommitPreviewTree'
import { hortonV3Service, type ComposedResponse, type DreamerRequest } from '../services/hortonV3Service'
import { BRAND } from '../config/brand'

// Lifecoach's original perpetual thread. Since 2026-08-25 lifecoach supports
// MULTIPLE chats (engine gists give each a title/summary; the chats table
// switches between them); this id stays the DEFAULT landing chat so the
// user's existing history remains where they left it.
const LIFECOACH_CHAT_ID = 'lifecoach-main'
// Which lifecoach chat is active, persisted across reloads/tabs so the same
// thread resumes everywhere. localStorage (not sessionStorage) on purpose:
// switching chats is an explicit act and should survive the browser closing.
const lifecoachActiveChatId = (): string => {
  try { return localStorage.getItem('horton_lifecoach_active_chat') || LIFECOACH_CHAT_ID } catch { return LIFECOACH_CHAT_ID }
}
const rememberLifecoachChat = (id: string) => {
  try { localStorage.setItem('horton_lifecoach_active_chat', id) } catch { /* ignore */ }
}
import { hortonGuidanceService, type OpenThread, type WorldCandidate, type HeldPerson } from '../services/hortonGuidanceService'
import { LifecoachChatsPanel } from '../components/ai/LifecoachChatsPanel'
import { WithYouChips } from '../components/with-you/WithYouChips'
import { withYouService } from '../services/withYouService'
import {
  acceptIntakeBatch,
  linkHierarchyItems,
  newIdempotencyKey,
  toRationingIntakeItem,
  type RationingHierarchyLink,
  type RationingIntakeItemInput,
} from '../services/rationingService'
import { useVoiceInput } from '../hooks/useVoiceInput'
import { speakTurn, speechSupported, type SpeechHandle } from '../services/compassSpeech'

interface ChatMessage {
  id: string
  type: 'user' | 'horton'
  content: string
  timestamp: Date
  thinking?: string  // Streamed thinking block
  thinking_summary?: string  // Summarized thinking (if available)
  lifeMap?: any  // Life map snapshot at this point
  suggestions?: any[]  // Suggestions for this message
  suggestion_versions?: any[]  // Different versions/snapshots
  classification?: any
  confidence?: number
  session_id?: string
  /** The engine's turn row id — what /compass/speak speaks. Absent on
   * engines that do not report one, which is how the play control knows
   * there is nothing to play. */
  engine_turn_id?: number
  // V3 Dream Engine data - stored with each message for historical access
  v3_composed_response?: ComposedResponse  // Full V3 response including hierarchy items
  v3_agent_outputs?: any  // Raw agent outputs (goal_updates, skill_updates, etc.)
  /** Pipeline / LLM debug from `response.debug` (external engine: `llm_calls`, etc.) */
  v3_debug?: Record<string, unknown>
  context_status?: ContextStatus  // Context window status for this message
}

interface ContextStatus {
  status: 'ok' | 'yellow' | 'red' | 'critical'
  current_tokens: number
  max_tokens: number
  remaining_tokens: number
  percentage_used: number
  can_continue: boolean
  user_message?: string
}

interface ChatSession {
  chat_id: string
  title: string
  message_count: number
  started_at?: string
  last_activity?: string
  current_life_map?: any
}

type ChatTab = 'chat' | 'items' | 'held' | 'lifemap'

// Hierarchy metadata matching index_enhanced.html
const hierarchyMetadata: Record<string, { emoji: string; name: string; order: number }> = {
  'VISION_PURPOSE': { emoji: '🌟', name: 'Dreams', order: 1 },
  'ASPIRATIONS_AMBITIONS': { emoji: '🎯', name: 'Aspirations', order: 2 },
  'GOALS_OBJECTIVES': { emoji: '📊', name: 'Goals', order: 3 },
  'PROJECTS_INITIATIVES': { emoji: '🎬', name: 'Projects', order: 4 },
  'TASKS_ACTIONS': { emoji: '📋', name: 'Tasks', order: 5 },
  'ACTION_ITEMS': { emoji: '✅', name: 'Action Items', order: 6 },
  'HABITS_ROUTINES': { emoji: '🔄', name: 'Habits & Routines', order: 7 },
  'EVENTS_TIMEBOUND': { emoji: '📅', name: 'Events', order: 8 },
  'OBSTACLES_CHALLENGES': { emoji: '🚧', name: 'Roadblocks', order: 9 },
  'KNOWLEDGE_SKILLS': { emoji: '🎓', name: 'Knowledge & Skills', order: 10 },
  'IDEAS_INSIGHTS': { emoji: '💡', name: 'Ideas', order: 11 },
  'OPPORTUNITIES_POSSIBILITIES': { emoji: '💫', name: 'Opportunities', order: 12 },
  'DECISIONS_CHOICES': { emoji: '🧭', name: 'Decisions', order: 13 },
  'RESOURCES_ASSETS': { emoji: '🛠️', name: 'Resources', order: 14 },
  'RELATIONSHIPS_NETWORK': { emoji: '👥', name: 'Relationships', order: 15 },
  'RISKS_THREATS': { emoji: '⚠️', name: 'Risks', order: 16 },
  'METRICS_MEASURES': { emoji: '📈', name: 'Metrics', order: 16 },
  'REVIEWS_FEEDBACK': { emoji: '🔍', name: 'Reviews', order: 17 },
  'FINANCIAL_ELEMENTS': { emoji: '💰', name: 'Financial', order: 18 },
  'ENERGY_MOMENTUM': { emoji: '🏃', name: 'Energy & Momentum', order: 19 },
  'CELEBRATIONS_REWARDS': { emoji: '🎉', name: 'Celebrations', order: 20 },
  'PERSONAL_DEVELOPMENT': { emoji: '🌱', name: 'Personal Development', order: 21 },
  'SCENARIOS_PLANNING': { emoji: '🔮', name: 'Scenarios', order: 22 },
  'URGENT_IMPORTANT': { emoji: '⚡', name: 'Urgent', order: 23 },
  'FLOW_PROCESS': { emoji: '🌊', name: 'Flow & Process', order: 24 },
  'DEPENDENCIES_REQUIREMENTS': { emoji: '🔗', name: 'Dependencies', order: 25 }
}

export function HortonChat() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const branding = useBranding()
  const fromPage = searchParams.get('from') || null
  const objectType = searchParams.get('type') || null
  /** e.g. `micro` — habit → micro-steps / action items (from Habits AI Assist). */
  const assistMode = searchParams.get('assist') || null
  const habitTitleParam = searchParams.get('habitTitle') || null
  const habitIdParam = searchParams.get('habitId') || null

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  // With You: when chat opened from a "yanamas is with you" push or
  // ?with_you=true link, show quick prompts and persist each user message
  // as a With You entry against the active session.
  const [withYouMode] = useState<boolean>(() => {
    try {
      return new URLSearchParams(window.location.search).get('with_you') === 'true'
    } catch {
      return false
    }
  })
  const [withYouSessionId, setWithYouSessionId] = useState<string | number | null>(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      return params.get('chat_session_id') || null
    } catch {
      return null
    }
  })
  useEffect(() => {
    if (!withYouMode || withYouSessionId) return
    let cancelled = false
    ;(async () => {
      try {
        const session = await withYouService.startSession({ entry_source: 'manual' })
        if (!cancelled) setWithYouSessionId(session.session_id)
      } catch (err) {
        console.warn('[with_you] startSession failed', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [withYouMode, withYouSessionId])
  const [isLoading, setIsLoading] = useState(false)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [chatId, setChatId] = useState<string | null>(null)
  
  // Streaming state
  const [currentThinking, setCurrentThinking] = useState<string>('')
  const [isThinking, setIsThinking] = useState(false)
  const [, setCurrentHortonMessageId] = useState<string | null>(null)
  
  // Phase progress state (shows what yanamas is doing)
  const [currentPhase, setCurrentPhase] = useState<{
    phase: number
    name: string
    icon: string
    description: string
    working?: boolean  // true once the simulated phases are done but the reply is still in flight
  } | null>(null)
  const [phaseHistory, setPhaseHistory] = useState<string[]>([]) // Track completed phases
  
  // Split-pane state (starts as single pane, splits after first message)
  const [hasSplitPane, setHasSplitPane] = useState(false)
  // Mobile: switches full-screen between tabs. Desktop: chat always visible, right pane toggles items/lifemap.
  const [activeTab, setActiveTab] = useState<ChatTab>('chat')
  const [rightPaneTab, setRightPaneTab] = useState<'items' | 'held' | 'lifemap'>('items')
  
  // Life map state (right pane - 65%)
  const [lifeMapData, setLifeMapData] = useState<any>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  
  // Chat history state
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([])
  const [showChatHistory, setShowChatHistory] = useState(false)
  // Lifecoach multi-chat table (gisted titles/summaries) — separate from the
  // compass chatSessions sidebar, which reads a different store.
  const [showLifecoachChats, setShowLifecoachChats] = useState(false)
  
  
  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  // One-time onboarding hint (G15: explain proposal-only data model)
  const [showOnboardingHint, setShowOnboardingHint] = useState(
    () => !localStorage.getItem('horton_onboarding_dismissed')
  )
  const dismissOnboarding = () => {
    localStorage.setItem('horton_onboarding_dismissed', '1')
    setShowOnboardingHint(false)
  }

  const voice = useVoiceInput({
    onInterim: (text) => setInput(text),
    onResult: (text) => setInput(prev => prev ? prev : text),
  })

  // ── Voice OUT: compass reads its replies (Piper, streamed) ───────────────
  // Per DEVICE, not per account: whether it is safe for a coach to speak your
  // health and money aloud depends on the room you are in, not on who you
  // are. A phone on a walk and a laptop in an office are different answers.
  // Speaking-voice picker (owner ask 2026-08-25): which installed Piper
  // voice reads the replies. Stored per ACCOUNT in the sidecar's settings
  // (validated there against the closed set of installed voices).
  const [voiceOptions, setVoiceOptions] = useState<Array<{ key: string; label: string; language: string }>>([])
  const [chosenVoice, setChosenVoice] = useState<string>('')
  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE_URL}/api/horton-v3/compass/voices`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (cancelled || !b?.success || !b?.speaking) return
        setVoiceOptions(Array.isArray(b.speaking.options) ? b.speaking.options : [])
        setChosenVoice(b.speaking.chosen || b.speaking.default || '')
      })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [])
  const pickVoice = (key: string) => {
    setChosenVoice(key)
    void fetch(`${API_BASE_URL}/api/horton-v3/compass/voice`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ voice: key }),
    }).catch(() => undefined)
  }

  const [voiceOn, setVoiceOn] = useState<boolean>(() => {
    try { return localStorage.getItem('compass.voice') === 'on' } catch { return false }
  })
  // The turn currently being spoken, and how to stop it.
  const [speakingTurn, setSpeakingTurn] = useState<number | null>(null)
  const [speechError, setSpeechError] = useState<string | null>(null)
  const speechRef = useRef<SpeechHandle | null>(null)

  const stopSpeaking = useCallback(() => {
    speechRef.current?.stop()
    speechRef.current = null
    setSpeakingTurn(null)
  }, [])

  const playReply = useCallback(async (turnId: number, auto = false) => {
    stopSpeaking()
    setSpeechError(null)
    if (!speechSupported()) { setSpeechError('this browser cannot play audio'); return }
    const handle = speakTurn(turnId, { auto })
    speechRef.current = handle
    setSpeakingTurn(turnId)
    try {
      await handle.done
    } catch (reason: any) {
      // A deliberate stop is not a failure. The fetch aborts, which surfaces
      // as an AbortError/DOMException here; showing "could not speak that"
      // every time someone presses stop would be noise, not honesty.
      if (reason?.name === 'AbortError' || String(reason).includes('abort')) {
        return
      }
      // Every refusal carries a machine reason from the engine. Show it —
      // silence with no explanation is the one outcome law 4 forbids.
      const r = String(reason)
      setSpeechError(
        r.startsWith('no_voice_for_locale:') ? `no voice installed for ${r.split(':')[1]}`
        : r === 'piper_not_installed' || r === 'speech_not_configured' ? 'speech is not set up on the server'
        : r === 'speech_unreachable' ? 'the engine is not reachable'
        : r === 'sensitive_not_spoken_automatically'
            ? 'not read aloud automatically — press listen if you want to hear it'
        : r === 'nothing_to_say' ? 'nothing in that reply to read aloud'
        : r === 'unsupported' ? 'this browser cannot play audio'
        : r === 'audio_blocked_until_you_tap'
            ? 'tap once anywhere to allow audio, then press listen'
        : r === 'sentence_too_long_to_speak' ? 'that reply is too long to read aloud'
        : r === 'speech_stalled' ? 'the audio stopped coming — try listen again'
        : r === 'too_many_listeners' ? 'the voice is busy right now — try again in a moment'
        : `could not speak that (${r})`)
    } finally {
      if (speechRef.current === handle) { speechRef.current = null; setSpeakingTurn(null) }
    }
  }, [stopSpeaking])

  const toggleVoice = useCallback(() => {
    setVoiceOn(prev => {
      const next = !prev
      try { localStorage.setItem('compass.voice', next ? 'on' : 'off') } catch { /* private mode */ }
      if (!next) stopSpeaking()
      return next
    })
    setSpeechError(null)
  }, [stopSpeaking])

  // Leaving the page mid-sentence must not keep talking.
  useEffect(() => () => { speechRef.current?.stop() }, [])
  
  /**
   * Build nested hierarchy structure from flat items_summary list (V4 format)
   * Each item has a parent reference: { type, title }
   */
  const buildHierarchyFromFlatItems = (flatItems: any[]): any[] => {
    console.log(`🏗️ buildHierarchyFromFlatItems called with ${flatItems.length} items:`, flatItems.map(i => `${i.type}:${i.title}`))

    // Identity/person items belong in My World, not the hierarchy tree
    const hierarchyItems = flatItems.filter(i => i.type !== 'identity' && i.type !== 'person')

    // CRITICAL FIX: Extract action_items from fields.action_items and add them to flatItems
    const expandedItems: any[] = []
    hierarchyItems.forEach(item => {
      expandedItems.push(item)
      
      // Check if this item has nested action_items in fields
      if (item.fields?.action_items && Array.isArray(item.fields.action_items)) {
        console.log(`  🔓 Extracting ${item.fields.action_items.length} action_items from ${item.type} "${item.title}"`)
        
        item.fields.action_items.forEach((actionItem: any) => {
          const expandedActionItem = {
            ...actionItem,
            type: 'action_item',
            parent: { type: item.type, title: item.title },
            life_area: item.life_area,
            description: actionItem.description || actionItem.title
          }
          expandedItems.push(expandedActionItem)
          console.log(`    ➕ Extracted action_item "${actionItem.title}"`)
        })
      }
    })
    
    console.log(`  📊 Expanded ${hierarchyItems.length} items to ${expandedItems.length} items (including nested action_items)`)

    // Create a map of items by type:title for quick lookup
    const itemsMap = new Map<string, any>()
    expandedItems.forEach(item => {
      const key = `${item.type}:${item.title}`
      itemsMap.set(key, { ...item, children: {} })
      console.log(`  📦 Mapped ${key}`)
    })

    // Build the tree structure
    const roots: any[] = []

    expandedItems.forEach(item => {
      const key = `${item.type}:${item.title}`
      const node = itemsMap.get(key)!

      if (!item.parent || !item.parent.title) {
        // Root item (dream, goal, or orphan)
        roots.push(node)
      } else {
        // Child item - find parent and attach
        const parentKey = `${item.parent.type}:${item.parent.title}`
        const parentNode = itemsMap.get(parentKey)

        if (parentNode) {
          // Organize children by type in children object
          const childType = item.type
          if (!parentNode.children[childType]) {
            parentNode.children[childType] = []
          }
          parentNode.children[childType].push(node)

          // CRITICAL: Add direct properties for compatibility
          // These are checked by hasChildren detection and parent chain extraction
          if (childType === 'skill') {
            if (!parentNode.skills) parentNode.skills = []
            parentNode.skills.push(node)
          } else if (childType === 'habit') {
            if (!parentNode.habits) parentNode.habits = []
            parentNode.habits.push(node)
          } else if (childType === 'aspiration') {
            if (!parentNode.aspirations) parentNode.aspirations = []
            parentNode.aspirations.push(node)
          } else if (childType === 'goal') {
            if (!parentNode.goals) parentNode.goals = []
            parentNode.goals.push(node)
          } else if (childType === 'task') {
            if (!parentNode.tasks) parentNode.tasks = []
            parentNode.tasks.push(node)
            console.log(`  ➕ Added task "${node.title}" to parent ${parentNode.type} "${parentNode.title}"`)
          } else if (childType === 'action_item') {
            if (!parentNode.action_items) parentNode.action_items = []
            parentNode.action_items.push(node)
            console.log(`  ➕ Added action_item "${node.title}" to parent ${parentNode.type} "${parentNode.title}"`)
          } else if (childType === 'roadblock') {
            if (!parentNode.roadblocks) parentNode.roadblocks = []
            parentNode.roadblocks.push(node)
          } else if (childType === 'knowledge_skill') {
            if (!parentNode.knowledge_skills) parentNode.knowledge_skills = []
            parentNode.knowledge_skills.push(node)
          }
          
          console.log(`  ✅ Linked ${childType} "${item.title}" to ${parentNode.type} "${parentNode.title}"`)
        } else {
          // Parent not found - treat as root
          console.log(`  ⚠️ Parent not found for ${item.type} "${item.title}" (parent: ${item.parent?.type}:${item.parent?.title}) - treating as root`)
          roots.push(node)
        }
      }
    })

    console.log(`🌳 Built ${roots.length} root items. Structure:`, roots.map(r => ({
      type: r.type,
      title: r.title,
      has_tasks: !!r.tasks,
      tasks_count: r.tasks?.length || 0,
      has_children: !!r.children,
      children_keys: r.children ? Object.keys(r.children) : []
    })))
    
    return roots
  }
  
  // Similar items dialog state
  const [showSimilarDialog, setShowSimilarDialog] = useState(false)
  const [similarItems, setSimilarItems] = useState<SimilarItem[]>([])
  const [pendingItem, setPendingItem] = useState<any>(null)
  
  // Personality selector state
  const [showPersonalitySelector, setShowPersonalitySelector] = useState(false)
  const [userEmotionalApproach, setUserEmotionalApproach] = useState<string>('context_first')
  const [userConversationStyle, setUserConversationStyle] = useState<string>('balanced')
  
  // yanamas V3 Dream Engine state
  const [useV3Engine] = useState(true) // Default to V3
  const [v3ComposedResponse, setV3ComposedResponse] = useState<ComposedResponse | null>(null)
  // Conversation agenda (owner UX 2026-07-18): the FIRST multi-area turn's
  // top-level items become a checklist pinned to that message; entries cross
  // off as the engine's direction spine visits them (agenda_trail statuses)
  // or their items are saved. In-memory per conversation (rebuilt on reload
  // from the next turn's trail).
  const [agendaRoots, setAgendaRoots] = useState<{ title: string; type: string }[] | null>(null)
  const [agendaMsgId, setAgendaMsgId] = useState<string | null>(null)
  const [agendaTrail, setAgendaTrail] = useState<{ title: string; key: string; status: string }[]>([])
  const [agendaCommitted] = useState<string[]>([])
  const agendaTokens = (t: string) => new Set(
    t.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 2)
  )
  const agendaMatches = (a: string, b: string) => {
    const ta = agendaTokens(a); const tb = agendaTokens(b)
    let n = 0; ta.forEach(w => { if (tb.has(w)) n += 1 })
    return n >= 2 || (n >= 1 && (ta.size <= 2 || tb.size <= 2))
  }
  const agendaStatusFor = (title: string): 'committed' | 'discussed' | 'current' | 'pending' => {
    const committedTitles = [...agendaCommitted, ...committedItems.map(ci => ci.title)]
    if (committedTitles.some(c => agendaMatches(c, title))) return 'committed'
    const hit = agendaTrail.filter(e => agendaMatches(e.title, title))
    if (hit.some(e => e.status === 'superseded' || e.status === 'rejected')) return 'discussed'
    if (hit.some(e => e.status === 'active')) return 'current'
    return 'pending'
  }
  const [v3ConversationId, setV3ConversationId] = useState<string | null>(null)
  const [v3TurnNumber, setV3TurnNumber] = useState(1)
  const [v3Persona, setV3Persona] = useState<string>('supportive_best_friend')

  // Which engine this user is routed to. Drives the chat SHELL: lifecoach users
  // get one perpetual conversation + a Consolidated Life Map panel; everyone
  // else keeps today's multi-chat compass UI. Resolved on mount (before the
  // first turn) and re-confirmed from each turn's `runtime` field.
  const [runtimeName, setRuntimeName] = useState<string>('')
  const isLifecoach = runtimeName === 'lifecoach'

  // The face (owner asks 2026-09-03/04). Lifecoach chats open on Horton's
  // face; the transcript is the page to its left, turned by a swipe or the
  // edge tabs (HortonFacePager). `faceOn` is the per-device preference (the
  // header toggle, like `voiceOn`); `facePage` is which page is showing.
  // Other engines and anyone who turns it off get the page exactly as it was.
  const [faceOn, setFaceOn] = useState<boolean>(() => {
    try { return localStorage.getItem('compass.face') !== 'off' } catch { return true }
  })
  const [facePage, setFacePage] = useState<FacePagerPage>('face')
  const showFace = isLifecoach && faceOn
  const faceShown = showFace && facePage === 'face'
  // `?face=flat-woman` swaps the character, for comparing them side by side.
  const faceId = searchParams.get('face') || DEFAULT_FACE
  const toggleFace = useCallback(() => {
    setFaceOn((on) => {
      const next = !on
      try { localStorage.setItem('compass.face', next ? 'on' : 'off') } catch { /* private mode */ }
      return next
    })
    setFacePage('face')
  }, [])
  // What the face should be doing, from what the chat already knows.
  const faceState: FaceState =
    speakingTurn !== null ? 'speaking'
    : (isLoading || isThinking) ? 'thinking'
    : voice.isListening ? 'listening'
    : 'idle'
  const latestReply = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m && m.type === 'horton') {
        return { content: m.content || '', turnId: m.engine_turn_id, error: Boolean((m as { error?: unknown }).error) }
      }
    }
    return null
  }, [messages])
  // Bumped after each lifecoach turn so the Life Map panel refetches (it fills
  // asynchronously via consolidation, so this mostly refreshes counts/state).
  const [lifeMapRefresh, setLifeMapRefresh] = useState(0)
  // Lifecoach spend on this user (USD), SERVER-owned: the sidecar keeps a
  // lifetime figure that survives page reloads, includes the nightly
  // consolidation the turn response never sees, and is cleared by
  // lifecoach-reset. Seeded from the Life Map fetch on mount, updated from each
  // turn's `cost_lifetime` — AND from the Life Map panel's poll (`onCost`),
  // which is the only surface that sees the consolidation spend: the run that
  // builds the map lands about a minute after the reply, so a header fed only
  // by turn responses shows the conversation's cost and omits the run that
  // produced the items on screen. The old value was a browser-memory sum of
  // per-turn `cost_total` that restarted at zero on every reload.
  const [lifecoachCost, setLifecoachCost] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    hortonV3Service.getLifeMap().then((lm) => {
      if (!cancelled && typeof lm.cost_lifetime === 'number') setLifecoachCost(lm.cost_lifetime)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])
  // Lifecoach always shows its Life Map alongside the chat; compass only splits
  // once it has items to show.
  const showSplit = hasSplitPane || isLifecoach
  useEffect(() => {
    let cancelled = false
    hortonV3Service
      .getRuntime()
      .then((rt) => { if (!cancelled && rt) setRuntimeName(rt) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // The moment we learn the user is on lifecoach, pin the conversation to
  // their ACTIVE lifecoach chat (default: the perpetual thread) — overriding
  // whatever the compass multi-chat resolver picked — and let the mount
  // loader restore its history. Switching chats (table row / New chat)
  // updates the marker FIRST, so this effect never snaps a switch back.
  useEffect(() => {
    if (!isLifecoach) return
    const target = lifecoachActiveChatId()
    if (chatId === target) return
    setChatId(target)
    setMessages([])
    try { sessionStorage.setItem('horton_current_chat_id', target) } catch { /* ignore */ }
  }, [isLifecoach, chatId])

  // Held threads + noticed people/places for THIS conversation, shown inline in
  // the chat while it's active. They graduate to yanamas' Inbox on abandon /
  // partial-Save (see the backend inbox gating).
  const [heldThreads, setHeldThreads] = useState<OpenThread[]>([])
  const [noticedCandidates, setNoticedCandidates] = useState<WorldCandidate[]>([])
  // People/places yanamas captured this turn that sit inside a not-yet-approved
  // patch (a MIXED turn bundles them with hierarchy proposals). They aren't My
  // People yet and never hit the noticed-candidate lane, so we show them
  // read-only here so the user can see them before approving.
  const [heldPeople, setHeldPeople] = useState<HeldPerson[]>([])
  const [, setExpandedHeldId] = useState<string | null>(null)
  // Shaping focus: "Make it real" opens an in-chat "let's shape this together"
  // conversation bound to ONE held thread. While set, every send carries
  // clarifying_thread_id so the engine ENRICHES this thread (blueprint §7
  // exploring) instead of spawning parallel items; "Add it now" finalizes it.
  const [clarifyingFocus, setClarifyingFocus] = useState<OpenThread | null>(null)
  // Proposal-refinement focus: the user picked a PROPOSED (unsaved) item —
  // the next turn carries clarifying_item so the engine enriches THAT
  // proposal instead of spawning duplicates (owner ask 2026-07-05).
  const [refiningProposal, setRefiningProposal] = useState<{ type: string; title: string } | null>(null)
  const refreshConversationHeld = async () => {
    if (!chatId || !useV3Engine) return
    try {
      const inbox = await hortonGuidanceService.getConversationInbox(chatId)
      setHeldThreads(inbox.threads || [])
      setNoticedCandidates(inbox.world_candidates || [])
      setHeldPeople(inbox.held_people || [])
    } catch { /* non-fatal: the chat still works */ }
  }
  useEffect(() => {
    if (chatId && useV3Engine) void refreshConversationHeld()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v3ComposedResponse, chatId, useV3Engine])
  // Connection surfacing (owner ask 2026-07-05): after a Save, ask which
  // held/saved thoughts the new items also move forward, and say so in the
  // chat — the personal-memory touch ("doing this would also help achieve X").
  // Fire-and-forget: silence on empty/error, never blocks the save flow.
  const connectionsInFlight = useRef(false)
  const surfaceConnectionsAfterSave = async (
    saved: Array<{ type: string; title: string }>,
  ) => {
    if (connectionsInFlight.current || saved.length === 0) return
    connectionsInFlight.current = true
    try {
      const connections = await hortonGuidanceService.getConnections(saved)
      if (!connections.length) return
      const lines = connections.slice(0, 5).map((cn) => {
        const savedTag = cn.thread.status === 'snoozed' ? ' *(from your Saved for later)*' : ''
        return `🧵 **${cn.thread.title}**${savedTag} — ${cn.why}`
      })
      const note: ChatMessage = {
        id: `horton_connections_${Date.now()}`,
        type: 'horton',
        content:
          `One more thing — saving these also moves forward what I've been holding for you:\n\n${lines.join('\n')}`,
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, note])
      void refreshConversationHeld()
    } catch { /* a missing nicety, never an error */ } finally {
      connectionsInFlight.current = false
    }
  }
  // "could become a …" label + the detail route to open AFTER converting, so the
  // user can edit fields + set scheduling on the created item's own page.
  const THREAD_TYPE_LABEL: Record<string, string> = {
    dream: 'a dream', aspiration: 'an aspiration', goal: 'a goal', milestone: 'a milestone',
    task: 'a task', habit: 'a habit', skill: 'a skill', roadblock: 'something in the way',
    blocker_annotation: 'something in the way', action_item: 'an action item',
  }
  // NOTE (owner decision 2026-07-05): blockers are NEVER items. A blocker
  // thread "made real" creates the SKILL that overcomes it (+ an annotation
  // attached to the blocked goal when known) — so roadblock/blocker types are
  // deliberately ABSENT from these maps and handled in finalizeClarifying.
  const THREAD_TYPE_ROUTE: Record<string, string> = {
    dream: 'dreams', aspiration: 'goals', goal: 'goals', milestone: 'goals',
    task: 'tasks', action_item: 'action-items', habit: 'habits', skill: 'skills',
  }
  // thread type → the hierarchy-service key that creates a real saas-core item.
  const THREAD_TYPE_HIERARCHY: Record<string, string> = {
    dream: 'VISION_PURPOSE', aspiration: 'ASPIRATIONS_AMBITIONS', goal: 'GOALS_OBJECTIVES',
    milestone: 'GOALS_OBJECTIVES', task: 'TASKS_ACTIONS', action_item: 'ACTION_ITEMS',
    habit: 'HABITS', skill: 'SKILLS',
  }
  // "Make it real" opens a SHAPING conversation bound to this thread instead of
  // instantly creating an item. Every subsequent send carries clarifying_thread_id
  // so the engine ENRICHES this one thread (details accumulate here, no parallel
  // items). The user finalizes with "Add it now" once it's shaped.
  const makeThreadReal = async (t: OpenThread) => {
    setClarifyingFocus(t)
    setExpandedHeldId(null)
    // Calibration breadcrumb (fire-and-forget, never blocks or throws).
    void hortonGuidanceService.sendProfileEvents([{ event_kind: 'map_now_clicked', chat_id: chatId ?? undefined }])
    // Move the thread into "exploring" so it reads as actively being shaped.
    try { await hortonGuidanceService.threadAction(t.id, 'explore') } catch { /* best-effort */ }
    // A warm, in-chat opener inviting more detail (client-composed so it lands
    // immediately and we control the tone). The engine handles the refine turns.
    const typeLabel = t.possible_lifemap_type
      ? (THREAD_TYPE_LABEL[t.possible_lifemap_type] || `a ${t.possible_lifemap_type}`)
      : 'something worth shaping'
    const isBlocker = t.possible_lifemap_type === 'blocker_annotation' || t.possible_lifemap_type === 'roadblock'
    const missing = t.missing_to_make_actionable && t.missing_to_make_actionable.length
      ? t.missing_to_make_actionable[0]
      : null
    const ask = isBlocker
      ? "What's this getting in the way of, and what does it look like day to day?"
      : (missing ? `Tell me a bit more — ${missing}?` : 'Tell me a bit more about it so we can shape it right.')
    const opener: ChatMessage = {
      id: `horton_shape_${Date.now()}`,
      type: 'horton',
      content: `Let's shape **${t.title}** together. From what I've heard, this feels like it could become ${typeLabel}. ${ask}`,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, opener])
  }
  const dismissThread = async (id: string) => {
    try { await hortonGuidanceService.threadAction(id, 'dismiss') } catch { /* non-fatal */ }
    // Calibration breadcrumb (fire-and-forget, never blocks or throws).
    void hortonGuidanceService.sendProfileEvents([{ event_kind: 'thread_dismissed', chat_id: chatId ?? undefined }])
    setHeldThreads(prev => prev.filter(t => t.id !== id))
    if (clarifyingFocus?.id === id) setClarifyingFocus(null)
  }
  const actOnNoticed = async (id: string, kind: 'confirm' | 'dismiss') => {
    try { await hortonGuidanceService.worldAction(id, kind) } catch { /* non-fatal */ }
    setNoticedCandidates(prev => prev.filter(c => c.id !== id))
  }
  // "Add now" on a captured (held) person/place: create the life_node
  // immediately via the graph API — the engine's held view drops entries
  // that already exist in My People/Places, so the next refresh clears it.
  const addHeldPersonNow = async (p: HeldPerson) => {
    try {
      const resp = await fetch('/api/horton-v3/life-graph/nodes', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_type: p.kind,
          title: p.name_or_alias,
          description: '',
          metadata: {
            ...(p.kind === 'person' && p.relationship_as_user_stated
              ? { relationship_type: p.relationship_as_user_stated }
              : {}),
            ...(p.kind === 'place' && p.place_kind_hint
              ? { place_type: p.place_kind_hint }
              : {}),
          },
        }),
      })
      if (resp.ok) {
        setHeldPeople(prev => prev.filter(x =>
          !(x.kind === p.kind && x.name_or_alias === p.name_or_alias)))
      }
    } catch { /* non-fatal: still addable via Save or My People */ }
  }
  // "Add it now": create the real item from the (engine-enriched) thread the user
  // has been shaping. Uses the freshest thread from heldThreads so the accumulated
  // detail rides into the item's description. Blocker → roadblock (attached to a
  // goal if shaping connected one; else standalone). Others → their own type.
  const finalizeClarifying = async () => {
    const focus = clarifyingFocus
    if (!focus) return
    const t = heldThreads.find(x => x.id === focus.id) || focus
    const type = t.possible_lifemap_type || ''
    const isBlocker = type === 'blocker_annotation' || type === 'roadblock'
    // Blockers are never items (owner decision 2026-07-05): "fear of talking"
    // becomes the SKILL that overcomes it, attached under the blocked goal
    // when known — plus a blocker annotation ON that goal (its "what's getting
    // in the way" surface). No roadblock node is ever created top-level.
    if (isBlocker) {
      try {
        const gid = Array.isArray(t.connected_node_ids)
          ? t.connected_node_ids.map(Number).find(n => Number.isInteger(n) && n > 0)
          : undefined
        const skillDetails: any = {
          clean_summary: `Overcoming ${t.title}`.slice(0, 80),
          description: t.what_horton_heard || t.title,
        }
        if (gid) skillDetails.parent_goal_id = gid
        const created: any = await acceptHierarchyItem(
          { type: 'skill', details: skillDetails } as any,
          'SKILLS',
          { checkDuplicates: false },
        )
        const skillId = created?.id ?? created?.data?.id ?? created?.item?.id
        // The annotation — ONLY when we know what it blocks (never orphaned).
        if (gid) {
          try {
            await fetch('/api/hierarchy/roadblocks', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                title: t.title,
                description: t.what_horton_heard || t.title,
                roadblock_type: 'personal',
                parent_goal_id: gid,
              }),
            })
          } catch { /* annotation is best-effort; the skill is the substance */ }
        }
        setHeldThreads(prev => prev.filter(x => x.id !== t.id))
        try { await hortonGuidanceService.threadAction(t.id, 'dismiss') } catch { /* best-effort */ }
        setClarifyingFocus(null)
        if (skillId != null) navigate(`/skills/${skillId}`)
        else { setToastMessage(`Added a skill to work on "${t.title}"`); setTimeout(() => setToastMessage(null), 3000) }
      } catch {
        setToastMessage('Could not add that just yet — please try again')
        setTimeout(() => setToastMessage(null), 3000)
      }
      return
    }
    const hierarchyType = THREAD_TYPE_HIERARCHY[type]
    const route = THREAD_TYPE_ROUTE[type]
    // Fail-safe: never mint a goal from an unknown/unmapped thread type.
    if (!hierarchyType || !route) {
      setToastMessage("Let's shape this one a little more before adding it.")
      setTimeout(() => setToastMessage(null), 3000)
      return
    }
    try {
      const details: any = { clean_summary: t.title, description: t.what_horton_heard || t.title }
      const created: any = await acceptHierarchyItem({ type, details } as any, hierarchyType, { checkDuplicates: false })
      const newId = created?.id ?? created?.data?.id ?? created?.item?.id
      setHeldThreads(prev => prev.filter(x => x.id !== t.id))
      try { await hortonGuidanceService.threadAction(t.id, 'dismiss') } catch { /* best-effort */ }
      setClarifyingFocus(null)
      if (type === 'habit' || type === 'task' || type === 'action_item') {
        sessionStorage.setItem('horton_refresh_todays_focus', 'true')
      }
      if (newId != null) navigate(`/${route}/${newId}`)   // open its page to edit fields + set scheduling
      else { setToastMessage(`Added "${t.title}" to your Life Map`); setTimeout(() => setToastMessage(null), 3000) }
    } catch {
      setToastMessage('Could not add that just yet — please try again')
      setTimeout(() => setToastMessage(null), 3000)
    }
  }
  // Stop shaping without creating anything — the thread stays held (now enriched).
  const stopClarifying = () => {
    setClarifyingFocus(null)
    setToastMessage("Okay — I'll keep holding this. We can shape it more anytime.")
    setTimeout(() => setToastMessage(null), 3000)
  }
  // Top-level things yanamas is holding right now (proposed roots + held threads),
  // deduped by title (a thread wins over a same-title proposal). Powers the
  // "here's what I'm holding" reassurance card so nothing ever feels lost.
  type HoldingItem = { source: 'proposed' | 'thread'; type: string; title: string; key: string; thread?: OpenThread }
  const getTopLevelHolding = (): HoldingItem[] => {
    const out: HoldingItem[] = []
    for (const it of (v3ComposedResponse?.items_summary || [])) {
      if (it.type === 'identity' || it.type === 'person' || it.type === 'place') continue
      if (!it.parent || !it.parent.title) {
        out.push({ source: 'proposed', type: it.type, title: it.title, key: `p:${it.type}:${it.title}` })
      }
    }
    for (const t of heldThreads) {
      out.push({ source: 'thread', type: t.possible_lifemap_type || 'unknown', title: t.title, key: `t:${t.id}`, thread: t })
    }
    const seen = new Map<string, HoldingItem>()
    for (const x of out) {
      const k = x.title.trim().toLowerCase()
      const cur = seen.get(k)
      if (!cur || (x.source === 'thread' && cur.source !== 'thread')) seen.set(k, x)
    }
    return Array.from(seen.values())
  }
  // Badge count for the Held tab (deliberate owner override of the
  // blueprint's no-inventory-counts guidance — explicit ask 2026-07-05).
  const heldCount = heldThreads.length + noticedCandidates.length + heldPeople.length
  // Refine payload (duplicate fix, owner ask 2026-07-05): send the FULL
  // top-down subtree for the picked item so the engine constrains the turn to
  // this hierarchy. lineage = walk parent refs upward (items_summary items
  // carry parent:{type,title}); children = items whose parent is this item.
  // Computed at SEND time so it reflects the freshest merged items_summary.
  const buildClarifyingItemPayload = (item: { type: string; title: string }) => {
    const items: any[] = v3ComposedResponse?.items_summary || []
    const byKey = new Map(items.map((i: any) => [`${i.type}:${i.title}`, i]))
    const self: any = byKey.get(`${item.type}:${item.title}`)
    const lineage: Array<{ type: string; title: string }> = []
    const seen = new Set<string>([`${item.type}:${item.title}`])
    let cur: any = self
    while (cur?.parent?.title && lineage.length < 10) {
      const pk = `${cur.parent.type}:${cur.parent.title}`
      if (seen.has(pk)) break            // cycle guard
      seen.add(pk)
      lineage.unshift({ type: cur.parent.type, title: cur.parent.title })  // top-down
      cur = byKey.get(pk)
    }
    const children = items
      .filter((i: any) => i.parent?.type === item.type && i.parent?.title === item.title)
      .slice(0, 20)
      .map((i: any) => ({ type: i.type, title: i.title }))
    return {
      type: item.type,
      title: item.title,
      ...(self?.description ? { description: String(self.description).slice(0, 500) } : {}),
      ...(lineage.length ? { lineage } : {}),
      ...(children.length ? { children } : {}),
    }
  }
  // "Pick one" from the Held tab: a thread opens the shaping conversation; a
  // proposed item binds the REFINE focus (clarifying_item) so the next turn
  // enriches THAT proposal instead of creating duplicates (owner ask
  // 2026-07-05), then returns the user to the composer with an opener.
  const focusHolding = (item: HoldingItem) => {
    if (item.source === 'thread' && item.thread) {
      void makeThreadReal(item.thread)
      return
    }
    setRefiningProposal({ type: item.type, title: item.title })
    setRightPaneTab('items')
    setActiveTab('chat')
    const opener: ChatMessage = {
      id: `horton_focus_${Date.now()}`,
      type: 'horton',
      content: `Let's talk about **${item.title}** — tell me a bit more and I'll add the detail to this item.`,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, opener])
  }
  
  // V3 Hierarchy item state tracking (edit/reject)
  // Map of "type:title" to ItemState
  const [v3ItemStates, setV3ItemStates] = useState<Map<string, ItemState>>(new Map())
  
  // Rejected items with optional comments (passed to LLM as context)
  const [v3RejectedItems, setV3RejectedItems] = useState<RejectedItem[]>([])
  
  // Track if items are being committed
  const [isCommittingItems, setIsCommittingItems] = useState(false)
  
  // Commit tracking (tombstone/EOF)
  const [committedAt, setCommittedAt] = useState<string | null>(null)
  const [committedItems, setCommittedItems] = useState<Array<{id: string; type: string; title: string}>>([])
  const [missingItems, setMissingItems] = useState<Array<{id: string; type: string; title: string}>>([])
  const [missingItemsExpanded, setMissingItemsExpanded] = useState(false)
  // Scheduling status: null (no action items), 'completed' (scheduled), 'skipped' (maybe later)
  const [schedulingStatus, setSchedulingStatus] = useState<'completed' | 'skipped' | null>(null)
  const [pendingSchedulableItems, setPendingSchedulableItems] = useState<Array<{id: string; type: string; title: string; priority: string; estimated_duration: number}>>([])
  
  // Confirmation modal state
  const modal = useConfirmationModal()
  
  // Check if any items are being edited (blocks conversation)
  const hasUnsavedEdits = Array.from(v3ItemStates.values()).some(s => s.status === 'editing')
  
  // Selected message for showing its suggestions/data in the right pane
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null)
  
  // Context window status
  const [contextStatus, setContextStatus] = useState<ContextStatus | null>(null)

  /** Modal for pipeline/LLM JSON (external chat-engine `meta.debug`). */
  const [pipelineDebugModal, setPipelineDebugModal] = useState<{
    open: boolean
    payload: Record<string, unknown> | null
  }>({ open: false, payload: null })
  
  // LLM Cost tracking state — displayed via CostTracker in dev mode
  const [sessionCost, setSessionCost] = useState<{
    totalCost: number
    totalInputCost: number
    totalOutputCost: number
    totalInputTokens: number
    totalOutputTokens: number
    totalTokens: number
    requestCount: number
    lastMessageCost?: number
    lastInputCost?: number
    lastOutputCost?: number
    currentModel?: string
    lastTelemetry?: any
  }>({
    totalCost: 0,
    totalInputCost: 0,
    totalOutputCost: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalTokens: 0,
    requestCount: 0
  })
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSavingRef = useRef<boolean>(false)
  const lastSavedMessagesRef = useRef<string>('') // Track last saved state to prevent duplicate saves
  const isLoadingRef = useRef<boolean>(false) // Track loading state to avoid stale closures
  const isThinkingRef = useRef<boolean>(false) // Track thinking state to avoid stale closures
  const isRestoringRef = useRef<boolean>(false) // Track if we're restoring a chat (skip auto-save)
  const prevChatIdRef = useRef<string | null>(null) // Detect chat switches (reset per-chat ids)
  const chatIdRef = useRef<string | null>(null) // Live chatId for in-flight response guards
  const lastSavedItemStatesRef = useRef<string>('') // Track last saved item states to prevent duplicate saves

  useProvideFeedbackContext({
    pageType: 'horton_chat',
    entityType: 'conversation',
    entityId: v3ConversationId || chatId || undefined,
    extra: {
      conversation_id: v3ConversationId,
      session_id: currentSessionId,
      chat_id: chatId,
      persona: v3Persona,
      turn_number: v3TurnNumber,
      with_you_mode: withYouMode,
      from: fromPage,
      type: objectType,
      assist: assistMode,
    },
  })

  // Resolve chat_id on mount, in continuity order:
  //   1. sessionStorage (same tab) — instant, exact.
  //   2. SERVER: the user's most recent active conversation — this is what
  //      makes continuity work across a NEW device/tab (the engine session is
  //      derived deterministically from chat_id, so adopting the same chat_id
  //      resumes the same conversation anywhere).
  //   3. brand-new chat.
  useEffect(() => {
    if (chatId) return
    let cancelled = false
    const resolveChatId = async () => {
      // "Work on it in a new chat" (yanamas' Inbox): mint a FRESH conversation
      // and prefill the opener. Must run BEFORE saved-id/server-adopt, or we'd
      // resume an old chat instead of starting the intended new one.
      const intentRaw = sessionStorage.getItem('horton_new_chat_intent')
      if (intentRaw) {
        sessionStorage.removeItem('horton_new_chat_intent')
        try {
          const intent = JSON.parse(intentRaw)
          if (intent?.opener && typeof intent.opener === 'string') setInput(intent.opener)
        } catch { /* malformed intent: just start a normal new chat */ }
        const intentChatId = await mintChatId()
        if (!cancelled) {
          console.log('🆕 New chat from inbox intent:', intentChatId)
          setChatId(intentChatId)
          sessionStorage.setItem('horton_current_chat_id', intentChatId)
        }
        return
      }
      const savedChatId = sessionStorage.getItem('horton_current_chat_id')
      if (savedChatId) {
        console.log('📂 Restoring chat from sessionStorage:', savedChatId)
        if (!cancelled) setChatId(savedChatId)
        return
      }
      // Cross-device resume: adopt the user's most recent, non-ended, non-empty
      // conversation from the server so a fresh device continues where they
      // left off. Best-effort — any failure just starts a new chat.
      try {
        const resp = await fetch('/horton/chat/list', { credentials: 'include' })
        if (resp.ok) {
          const data = await resp.json()
          const recent = (data.chats || []).find(
            (c: { chat_id?: string; message_count?: number; committed_at?: unknown }) =>
              c?.chat_id && (c.message_count ?? 0) > 0 && !c.committed_at,
          )
          if (recent?.chat_id && !cancelled) {
            console.log('🔗 Resuming most recent conversation from server:', recent.chat_id)
            setChatId(recent.chat_id)
            sessionStorage.setItem('horton_current_chat_id', recent.chat_id)
            return
          }
        }
      } catch (e) {
        console.warn('resume-last-conversation failed; starting fresh', e)
      }
      if (cancelled) return
      const newChatId = await mintChatId()
      console.log('🆕 Creating new chat:', newChatId)
      if (cancelled) return
      setChatId(newChatId)
      sessionStorage.setItem('horton_current_chat_id', newChatId)
    }
    void resolveChatId()
    return () => {
      cancelled = true
    }
  }, [chatId])
  
  // Save chatId to sessionStorage whenever it changes
  useEffect(() => {
    chatIdRef.current = chatId
    if (chatId) {
      sessionStorage.setItem('horton_current_chat_id', chatId)
    }
  }, [chatId])

  // Ephemeral "currently open & un-committed conversation" marker — distinct
  // from the persistent resume pointer above. Set only while this chat is
  // actively open and not yet Saved; cleared on unmount (leave the chat), on
  // commit, and on new chat. yanamas' Inbox reads THIS to gate the active
  // conversation's held items out — so leaving the chat graduates its
  // leftovers to the Inbox (fix for the "left conversation, items didn't show"
  // bug: the old code used the persistent resume pointer, which never cleared).
  useEffect(() => {
    if (chatId && !committedAt) {
      sessionStorage.setItem('horton_active_chat_id', chatId)
    } else {
      sessionStorage.removeItem('horton_active_chat_id')
    }
    return () => { sessionStorage.removeItem('horton_active_chat_id') }
  }, [chatId, committedAt])

  // Continuity across page reloads: the engine keys conversation history on
  // session_id, so if a reload starts with a null session it mints a NEW one
  // and forgets everything. Persist the engine session/conversation ids keyed
  // by the (stable) chatId and restore them the moment chatId is known —
  // synchronously, independent of the async loadChat round-trip — so the very
  // first message after a reload resumes the same conversation.
  //
  // CRITICAL: when chatId SWITCHES (new chat, history load), the previous
  // chat's session/conversation ids must never ride along — a stale
  // conversation_id paired with a fresh chat_id is exactly the prod
  // cross-contamination bug (one chat's transcript + another chat's engine
  // session → incoherent replies). On a switch we hard-reset to whatever is
  // stored FOR THIS chatId (or null; the backend re-derives everything from
  // chat_id anyway).
  useEffect(() => {
    if (!chatId) return
    const prev = prevChatIdRef.current
    prevChatIdRef.current = chatId
    const savedSession = sessionStorage.getItem(`horton_session_${chatId}`)
    const savedConv = sessionStorage.getItem(`horton_conv_${chatId}`)
    if (prev && prev !== chatId) {
      setCurrentSessionId(savedSession || null)
      setV3ConversationId(savedConv || null)
      return
    }
    if (!currentSessionId && savedSession) setCurrentSessionId(savedSession)
    if (!v3ConversationId && savedConv) setV3ConversationId(savedConv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId])

  useEffect(() => {
    if (chatId && currentSessionId) {
      sessionStorage.setItem(`horton_session_${chatId}`, currentSessionId)
    }
  }, [chatId, currentSessionId])

  useEffect(() => {
    if (chatId && v3ConversationId) {
      sessionStorage.setItem(`horton_conv_${chatId}`, v3ConversationId)
    }
  }, [chatId, v3ConversationId])

  // Pre-populate input with suggested message if coming from a specific page
  useEffect(() => {
    if (!fromPage || !objectType || messages.length > 0 || input !== '') return

    let decodedHabitTitle: string | null = null
    if (habitTitleParam) {
      try {
        decodedHabitTitle = decodeURIComponent(habitTitleParam)
      } catch {
        decodedHabitTitle = habitTitleParam
      }
    }

    if (fromPage === 'habits' && objectType === 'habit' && assistMode === 'micro') {
      const label = decodedHabitTitle ? ` "${decodedHabitTitle}"` : ' this habit'
      setInput(
        `Help me break down${label} into small micro-steps and optional action items I can add to my list. Start with the smallest viable actions.`
      )
      return
    }

    if (fromPage === 'habits' && objectType === 'habit') {
      setInput('I want to design a new habit.')
      return
    }

    const objectName =
      objectType === 'dream'
        ? 'dream'
        : objectType === 'aspiration'
          ? 'aspiration'
          : objectType === 'goal'
            ? 'goal'
            : 'item'
    setInput(`I want to create a new ${objectName}.`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPage, objectType, messages.length, assistMode, habitTitleParam])

  // Get contextual welcome message
  const getContextualWelcome = () => {
    let decodedHabitTitle: string | null = null
    if (habitTitleParam) {
      try {
        decodedHabitTitle = decodeURIComponent(habitTitleParam)
      } catch {
        decodedHabitTitle = habitTitleParam
      }
    }

    if (assistMode === 'micro' && fromPage === 'habits' && objectType === 'habit') {
      return {
        title: 'AI Assist — habits',
        message: decodedHabitTitle
          ? `You're asking for bite-sized actions for “${decodedHabitTitle}”. Adjust the prompt in the box below, then send when you're ready — I can suggest micro-steps and optional action items.`
          : `Ask me to break a habit into micro-steps and optional action items. Edit the prompt below anytime.`,
        showBackButton: true,
        highlightedText: null,
        highlightedPage: null,
        emoji: '✨',
        backUrl: habitIdParam ? `/habits/${habitIdParam}` : '/habits',
        backLabel: habitIdParam ? 'Back to habit' : 'Back to Habits',
      }
    }

    if (!fromPage || !objectType) {
      return {
        title: `Welcome to ${BRAND.assistant}!`,
        message: "Share what's on your mind, and I'll help you organize it into your life structure.",
        showBackButton: false,
        highlightedText: null
      }
    }

    const objectName =
      objectType === 'dream'
        ? 'dream'
        : objectType === 'aspiration'
          ? 'aspiration'
          : objectType === 'goal'
            ? 'goal'
            : objectType === 'habit'
              ? 'habit'
              : 'item'

    const pageName =
      fromPage === 'dreams'
        ? 'Dreams'
        : fromPage === 'aspirations'
          ? 'Aspirations'
          : fromPage === 'goals'
            ? 'Goals'
            : fromPage === 'tasks'
              ? 'Tasks'
              : fromPage === 'habits'
                ? 'Habits'
                : fromPage === 'action-items'
                  ? 'Action Items'
                  : fromPage

    const emoji =
      objectType === 'dream'
        ? '✨'
        : objectType === 'aspiration'
          ? '🚀'
          : objectType === 'goal'
            ? '🎯'
            : objectType === 'habit'
              ? '🔁'
              : '📋'

    return {
      title: `Welcome to ${BRAND.assistant}!`,
      message: `Hey! I understand you want to create a new ${objectName}. Let's work together to make it happen! Share your thoughts and I'll help you organize it into your ${pageName} page.`,
      highlightedText: `create a new ${objectName}`,
      highlightedPage: pageName,
      emoji: emoji,
      showBackButton: true,
      backUrl: `/${fromPage}`,
      backLabel: `Back to ${pageName}`
    }
  }

  // Load chat history on mount (after chatId is generated)
  useEffect(() => {
    if (chatId) {
      // Small delay to ensure chatId is set
      const timer = setTimeout(async () => {
        await loadChatHistory()
        loadUserPersonality()
        
        // If this chat was restored from sessionStorage, try to load it
        const savedChatId = sessionStorage.getItem('horton_current_chat_id')
        if (savedChatId === chatId && messages.length === 0) {
          console.log('🔄 Attempting to restore conversation:', chatId)
          try {
            await loadChat(chatId)
          } catch (error) {
            console.log('ℹ️ No saved conversation found for this chat ID')
          }
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [chatId])
  
  // Map emotional approach + conversation style to v3 persona
  const getV3PersonaFromProfile = (emotionalApproach: string, conversationStyle: string): string => {
    if (emotionalApproach === 'love_first' && conversationStyle === 'balanced') return 'supportive_best_friend'
    if (emotionalApproach === 'context_first' && conversationStyle === 'practical') return 'calm_older_sibling'
    if (emotionalApproach === 'emotional_first' && conversationStyle === 'exploratory') return 'quiet_mentor'
    // Default fallback based on emotional approach
    if (emotionalApproach === 'love_first') return 'supportive_best_friend'
    if (emotionalApproach === 'context_first') return 'calm_older_sibling'
    if (emotionalApproach === 'emotional_first') return 'quiet_mentor'
    return 'supportive_best_friend'
  }

  // Map v3 persona back to emotional approach + conversation style
  const getProfileFromV3Persona = (persona: string): { emotionalApproach: string, conversationStyle: string } => {
    switch (persona) {
      case 'supportive_best_friend':
        return { emotionalApproach: 'love_first', conversationStyle: 'balanced' }
      case 'calm_older_sibling':
        return { emotionalApproach: 'context_first', conversationStyle: 'practical' }
      case 'quiet_mentor':
        return { emotionalApproach: 'emotional_first', conversationStyle: 'exploratory' }
      default:
        return { emotionalApproach: 'love_first', conversationStyle: 'balanced' }
    }
  }

  // Load user's personality preferences
  const loadUserPersonality = async () => {
    try {
      const profile = await profileService.getUserProfile()
      setUserEmotionalApproach(profile.horton_emotional_approach)
      setUserConversationStyle(profile.horton_conversation_style)
      
      // Set v3Persona based on profile preferences
      const mappedPersona = getV3PersonaFromProfile(
        profile.horton_emotional_approach,
        profile.horton_conversation_style
      )
      setV3Persona(mappedPersona)
    } catch (error) {
      console.error('Error loading user personality:', error)
      // Use defaults if unable to load
    }
  }
  
  // Handle v3 persona change - also save to profile
  const handleV3PersonaChange = async (newPersona: string) => {
    setV3Persona(newPersona)
    
    // Also update the profile so it stays in sync
    const { emotionalApproach, conversationStyle } = getProfileFromV3Persona(newPersona)
    try {
      await profileService.updateHortonPersonality(emotionalApproach, conversationStyle)
      setUserEmotionalApproach(emotionalApproach)
      setUserConversationStyle(conversationStyle)
    } catch (error) {
      console.error('Error saving personality to profile:', error)
      // Still keep the local change
    }
  }
  
  // Handle personality selection (legacy method)
  const handlePersonalitySelect = async (emotionalApproach: string, conversationStyle: string) => {
    try {
      // Save to backend
      await profileService.updateHortonPersonality(emotionalApproach, conversationStyle)
      
      // Update local state
      setUserEmotionalApproach(emotionalApproach)
      setUserConversationStyle(conversationStyle)
      
      // Also update v3Persona
      setV3Persona(getV3PersonaFromProfile(emotionalApproach, conversationStyle))
      
      console.log('✅ Personality updated:', { emotionalApproach, conversationStyle })
    } catch (error) {
      console.error('Error saving personality:', error)
      // Still update local state for this session
      setUserEmotionalApproach(emotionalApproach)
      setUserConversationStyle(conversationStyle)
    }
  }
  
  // Auto-scroll (only when there are messages)
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, currentThinking])

  // Update refs when state changes (to avoid stale closures)
  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])
  
  useEffect(() => {
    isThinkingRef.current = isThinking
  }, [isThinking])

  // Auto-save chat after each message (debounced to prevent race conditions)
  // NOTE: Only trigger on messages changes, NOT on lifeMapData changes
  // Life map updates should not trigger saves - they're already saved with the message
  // IMPORTANT: Only save when NOT streaming (isLoading === false) to prevent saves during streaming updates
  useEffect(() => {
    // Don't save while streaming - wait until streaming is complete
    if (isLoading || isThinking) {
      return
    }
    
    if (messages.length > 0 && chatId) {
      // Create a signature based ONLY on messages (not lifeMapData)
      // Life map updates come with message responses, so they're already saved with the message
      // Use full message content hash to detect actual content changes (not just array updates)
      // Include thinking_summary in hash to detect when thinking is finalized
      const messageContentHash = messages.map(m => 
        `${m.id}:${m.content || ''}:${m.thinking_summary || ''}:${m.type}`
      ).join('|')
      const currentState = JSON.stringify({
        messageCount: messages.length,
        messageContentHash: messageContentHash
      })
      
      // Skip if state hasn't actually changed (prevents React Strict Mode double-saves)
      if (currentState === lastSavedMessagesRef.current) {
        return
      }
      
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      
      // Debounce: wait 2 seconds after streaming completes before saving
      // This gives time for all streaming updates to finish
      saveTimeoutRef.current = setTimeout(() => {
        // Double-check we're still not streaming (using refs to avoid stale closures)
        if (isLoadingRef.current || isThinkingRef.current) {
          return
        }
        
        // Check state again before saving (might have changed during debounce)
        // We need to get the latest messages - but we can't access them in the closure
        // So we'll rely on the state comparison and the fact that the effect will re-run
        // if messages change during the debounce period
        
        // Only save if state is still different from last saved
        // Note: We can't re-compute the hash here because messages might have changed
        // The effect will re-run if messages change, so this is safe
        console.log('💾 Auto-saving chat (messages changed, streaming complete)')
        autoSaveChat().then(() => {
          // Update last saved state only on success
          // We'll compute the state hash in autoSaveChat to ensure accuracy
          console.log('✅ Auto-save completed')
        }).catch((err) => {
          // Don't update on error, allow retry
          console.error('❌ Auto-save failed:', err)
        })
      }, 2000) // 2 seconds debounce after streaming completes
      
      // Cleanup timeout on unmount
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current)
        }
      }
    }
  }, [messages, chatId, isLoading, isThinking]) // Include isLoading and isThinking to prevent saves during streaming

  // Auto-save when item states or rejected items change (separate from message auto-save)
  useEffect(() => {
    // Only save if we have a chatId and there are messages
    if (!chatId || messages.length === 0) {
      return
    }
    
    // Don't save while loading/streaming
    if (isLoading || isThinking) {
      return
    }
    
    // Don't save during chat restoration
    if (isRestoringRef.current) {
      return
    }
    
    // Compute current item states hash to avoid duplicate saves
    const itemStatesObj: Record<string, ItemState> = {}
    v3ItemStates.forEach((value, key) => {
      itemStatesObj[key] = value
    })
    const currentItemStatesHash = JSON.stringify({
      itemStates: itemStatesObj,
      rejectedItems: v3RejectedItems
    })
    
    // Skip save if item states haven't actually changed
    if (currentItemStatesHash === lastSavedItemStatesRef.current) {
      return
    }
    
    // Debounce saves when item states change
    const saveTimeout = setTimeout(() => {
      console.log('💾 Auto-saving chat (item states changed)')
      autoSaveChat().then(() => {
        lastSavedItemStatesRef.current = currentItemStatesHash
        console.log('✅ Item state auto-save completed')
      }).catch((err) => {
        console.error('❌ Item state auto-save failed:', err)
      })
    }, 1000) // 1 second debounce for item state changes
    
    return () => clearTimeout(saveTimeout)
  }, [v3ItemStates, v3RejectedItems, chatId]) // Trigger when item states or rejected items change

  const autoSaveChat = async () => {
    // Prevent concurrent saves
    if (isSavingRef.current) {
      console.log('Save already in progress, skipping...')
      return
    }
    
    // Don't save while streaming
    if (isLoadingRef.current || isThinkingRef.current) {
      console.log('Skipping save - still streaming...')
      return
    }
    
    if (!chatId || messages.length === 0) {
      return
    }
    
    // Compute the current state hash to update lastSavedMessagesRef on success
    const messageContentHash = messages.map(m => 
      `${m.id}:${m.content || ''}:${m.thinking_summary || ''}:${m.type}`
    ).join('|')
    const currentState = JSON.stringify({
      messageCount: messages.length,
      messageContentHash: messageContentHash
    })
    
    isSavingRef.current = true
    
    try {
      const messagesToSave = messages.map((msg, idx) => ({
        message_index: idx,
        message_id: msg.id,
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
        user_message: msg.type === 'user' ? msg.content : '',
        horton_response: msg.type === 'horton' ? msg.content : '',
        thinking: msg.thinking,
        thinking_summary: msg.thinking_summary,
        thinking_original_length: msg.thinking?.length,
        thinking_summary_length: msg.thinking_summary?.length,
        life_map: msg.lifeMap,
        suggestions: msg.suggestions,
        suggestion_versions: msg.suggestion_versions,
        classification: msg.classification,
        confidence: msg.confidence,
        // The engine's turn row id, so "listen" still works after a reload:
        // the transcript is what history is rebuilt from, and without this
        // the control silently disappears on every stored reply.
        engine_turn_id: msg.engine_turn_id,
        // V3 Dream Engine data for historical access
        v3_composed_response: msg.v3_composed_response,
        v3_agent_outputs: msg.v3_agent_outputs,
        timestamp: msg.timestamp?.toISOString()
      })).filter(msg => msg.user_message || msg.horton_response)

      // Use chatId as fallback for session_id if currentSessionId is not set
      const sessionIdToUse = currentSessionId || chatId || `session_${Date.now()}`
      
      // Include session_id in headers for unauthenticated users
      const saveHeaders: HeadersInit = { 'Content-Type': 'application/json' }
      if (sessionIdToUse) {
        saveHeaders['X-Session-ID'] = sessionIdToUse
      }

      // Convert item states map to serializable object
      const itemStatesObj: Record<string, ItemState> = {}
      v3ItemStates.forEach((value, key) => {
        itemStatesObj[key] = value
      })

      const response = await fetch('/horton/chat/save', {
        method: 'POST',
        headers: saveHeaders,
        credentials: 'include',
        body: JSON.stringify({
          chat_id: chatId,
          session_id: sessionIdToUse,
          messages: messagesToSave,
          current_life_map: lifeMapData,
          // Save rejected items and item states for persistence
          rejected_items: v3RejectedItems,
          item_states: itemStatesObj,
          // Commit tracking (tombstone/EOF)
          committed_at: committedAt,
          committed_items: committedItems,
          // Scheduling status tracking
          scheduling_status: schedulingStatus,
          pending_schedulable_items: pendingSchedulableItems,
          // V3 conversation tracking
          v3_conversation_id: v3ConversationId,
          v3_turn_number: v3TurnNumber
        })
      })
      
      if (!response.ok) {
        throw new Error(`Save failed: ${response.status} ${response.statusText}`)
      }
      
      await response.json()
      console.log('✅ Chat saved successfully')
      // Update last saved state only on success
      lastSavedMessagesRef.current = currentState
    } catch (error) {
      console.error('Error auto-saving chat:', error)
      // Don't update lastSavedMessagesRef on error, allow retry
      throw error
    } finally {
      isSavingRef.current = false
    }
  }

  const loadChatHistory = async () => {
    try {
      // Generate a default session_id if one doesn't exist yet
      // This ensures we can load chat history even on first mount
      const sessionIdToUse = currentSessionId || chatId || `session_${Date.now()}`
      
      // Include session_id in headers for unauthenticated users
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (sessionIdToUse) {
        headers['X-Session-ID'] = sessionIdToUse
      }
      
      console.log('📋 Loading chat history with session:', sessionIdToUse)
      const response = await fetch('/horton/chat/list', {
        credentials: 'include',
        headers
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ Chat history loaded:', data)
        setChatSessions(data.chats || [])
        if (data.chats && data.chats.length > 0) {
          console.log(`📚 Found ${data.chats.length} saved chats`)
        } else {
          console.log('📭 No saved chats found')
        }
      } else {
        const errorText = await response.text()
        console.error('❌ Chat history load failed:', response.status, errorText)
        if (response.status === 401) {
          console.warn('⚠️ Unauthorized - will retry after session is established')
        }
      }
    } catch (error) {
      console.error('❌ Error loading chat history:', error)
    }
  }

  const loadChat = async (chatIdToLoad: string) => {
    stopSpeaking()   // switching conversations stops the reply mid-sentence
    // Set restoring flag to prevent auto-save during restoration
    isRestoringRef.current = true
    
    try {
      // Include session_id in headers for unauthenticated users
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (currentSessionId) {
        headers['X-Session-ID'] = currentSessionId
      }
      
      const response = await fetch(`/horton/chat/${chatIdToLoad}`, {
        credentials: 'include',
        headers
      })
      
      if (response.ok) {
        const data = await response.json()
        const chat = data.chat
        
        // Restore messages with V3 data
        const restoredMessages: ChatMessage[] = []
        let accumulatedComposedResponse: ComposedResponse | null = null
        
        chat.messages.forEach((msg: any, idx: number) => {
          // Use message_id if available, otherwise generate one
          const msgId = msg.message_id || (msg.role === 'user' ? `user_${idx}` : `horton_${idx}`)
          
          if (msg.user_message || msg.role === 'user') {
            restoredMessages.push({
              id: msgId,
              type: 'user',
              content: msg.user_message || msg.content,
              timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
            })
          }
          
          if (msg.horton_response || msg.role === 'assistant') {
            const hortonMsg: ChatMessage = {
              id: msgId,
              type: 'horton',
              content: msg.horton_response || msg.content,
              timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              thinking: msg.thinking || msg.thinking_summary,
              thinking_summary: msg.thinking_summary,
              lifeMap: msg.life_map,
              suggestions: msg.suggestions,
              suggestion_versions: msg.suggestion_versions,
              classification: msg.classification,
              confidence: msg.confidence,
              engine_turn_id: typeof msg.engine_turn_id === 'number'
                ? msg.engine_turn_id : undefined,
              // Restore V3 data for historical access
              v3_composed_response: msg.v3_composed_response,
              v3_agent_outputs: msg.v3_agent_outputs
            }
            restoredMessages.push(hortonMsg)
            
            // Accumulate items from ALL messages in history (not just last)
            if (msg.v3_composed_response) {
              if (!accumulatedComposedResponse) {
                // First response - initialize
                accumulatedComposedResponse = msg.v3_composed_response
              } else {
                // Subsequent responses - merge items
                const existingItems = accumulatedComposedResponse.items_summary || []
                const newItems = msg.v3_composed_response.items_summary || []
                
                // Combine items, avoiding duplicates based on type+title
                const itemsMap = new Map()
                
                // Add existing items
                existingItems.forEach(item => {
                  const key = `${item.type}:${item.title}`
                  itemsMap.set(key, item)
                })
                
                // Add new items (overwrites if same key)
                newItems.forEach((item: any) => {
                  const key = `${item.type}:${item.title}`
                  itemsMap.set(key, item)
                })
                
                const mergedItems = Array.from(itemsMap.values())
                
                // Accumulate all items, recommendations, questions, and thoughts
                const existingRecs = accumulatedComposedResponse.recommendations || []
                const newRecs = msg.v3_composed_response.recommendations || []
                const mergedRecs = [...new Set([...existingRecs, ...newRecs])]
                
                const existingQuestions = accumulatedComposedResponse.clarifying_questions || []
                const newQuestions = msg.v3_composed_response.clarifying_questions || []
                const mergedQuestions = [...new Set([...existingQuestions, ...newQuestions])]
                
                const existingThoughts = accumulatedComposedResponse.thought_process || []
                const newThoughts = msg.v3_composed_response.thought_process || []
                const mergedThoughts = [...existingThoughts, ...newThoughts]
                
                accumulatedComposedResponse = {
                  ...msg.v3_composed_response,
                  items_summary: mergedItems,
                  items_hierarchy: msg.v3_composed_response.items_hierarchy || accumulatedComposedResponse.items_hierarchy || [],
                  recommendations: mergedRecs.length > 0 ? mergedRecs : undefined,
                  clarifying_questions: mergedQuestions.length > 0 ? mergedQuestions : undefined,
                  thought_process: mergedThoughts.length > 0 ? mergedThoughts : undefined
                }
              }
            }
          }
        })
        
        console.log('📚 Loaded chat history:', {
          messageCount: restoredMessages.length,
          // Cast: TS doesn't see the forEach-callback assignments (TS#9998)
          // and would otherwise narrow this to its `null` initializer.
          accumulatedItemsCount: (accumulatedComposedResponse as ComposedResponse | null)?.items_summary?.length || 0,
          chatId: chat.chat_id
        })
        
        setMessages(restoredMessages)
        // Rebuild the conversation agenda on reload: pin to the first
        // multi-area horton message; trail from the LAST message that
        // carried one (the engine re-sends the full session trail per turn).
        {
          let pinned = false
          let latestTrail: any[] | null = null
          restoredMessages.forEach((rm: any) => {
            const comp = rm.v3_composed_response
            if (!comp) return
            if (Array.isArray(comp.agenda_trail) && comp.agenda_trail.length > 0) {
              latestTrail = comp.agenda_trail
            }
            if (!pinned) {
              const roots = (comp.items_summary || []).filter(
                (i: any) => !i.parent && !i.parent_id
              )
              if (roots.length >= 3) {
                setAgendaRoots(roots.map((r: any) => ({ title: r.title, type: r.type })))
                setAgendaMsgId(rm.id)
                pinned = true
              }
            }
          })
          if (latestTrail) setAgendaTrail(latestTrail)
        }
        // Persist the per-chat ids BEFORE switching chatId: the chat-switch
        // effect resets session/conversation state from these keys, so
        // writing them first makes the reset land on THIS chat's ids
        // instead of null.
        if (chat.session_id) {
          sessionStorage.setItem(`horton_session_${chat.chat_id}`, chat.session_id)
        }
        if (chat.v3_conversation_id) {
          sessionStorage.setItem(`horton_conv_${chat.chat_id}`, chat.v3_conversation_id)
        }
        setChatId(chat.chat_id)
        // Persist loaded chat ID to sessionStorage so it survives refresh
        sessionStorage.setItem('horton_current_chat_id', chat.chat_id)
        // Lifecoach multi-chat: the loaded chat becomes the active one, so
        // the pin effect resumes THIS thread instead of snapping back.
        if (isLifecoach) rememberLifecoachChat(chat.chat_id)
        setCurrentSessionId(chat.session_id)
        if (chat.v3_conversation_id) setV3ConversationId(chat.v3_conversation_id)
        if (chat.v3_turn_number) setV3TurnNumber(chat.v3_turn_number)
        setLifeMapData(chat.current_life_map || null)
        setHasSplitPane(restoredMessages.length > 0)
        setShowChatHistory(false)
        
        // Restore the accumulated V3 response for display
        if (accumulatedComposedResponse) {
          setV3ComposedResponse(accumulatedComposedResponse)
        }
        
        // Restore rejected items and item states
        if (chat.rejected_items && Array.isArray(chat.rejected_items)) {
          setV3RejectedItems(chat.rejected_items)
          console.log(`🚫 Restored ${chat.rejected_items.length} rejected items`)
        }
        
        if (chat.item_states && typeof chat.item_states === 'object') {
          const restoredStates = new Map<string, ItemState>()
          Object.entries(chat.item_states).forEach(([key, value]) => {
            restoredStates.set(key, value as ItemState)
          })
          setV3ItemStates(restoredStates)
          console.log(`📝 Restored ${restoredStates.size} item states`)
        }
        
        // Restore commit tracking (tombstone/EOF)
        // Server-side check for missing items (scalable - uses batch SQL queries)
        if (chat.committed_at) {
          setCommittedAt(chat.committed_at)
          console.log(`✅ Chat was committed at: ${chat.committed_at}`)
          
          if (chat.committed_items && Array.isArray(chat.committed_items)) {
            setCommittedItems(chat.committed_items)
            console.log(`📋 ${chat.committed_items.length} items were committed`)
          }
          
          // Use server-provided missing items (already checked via batch SQL)
          if (chat.missing_items && Array.isArray(chat.missing_items)) {
            setMissingItems(chat.missing_items)
            if (chat.missing_items.length > 0) {
              console.log(`⚠️ ${chat.missing_items.length} committed items are missing from the database`)
            }
          } else {
            setMissingItems([])
          }
          // Restore scheduling status
          if (chat.scheduling_status) {
            setSchedulingStatus(chat.scheduling_status)
            console.log(`📅 Scheduling status: ${chat.scheduling_status}`)
          }
          if (chat.pending_schedulable_items && Array.isArray(chat.pending_schedulable_items)) {
            setPendingSchedulableItems(chat.pending_schedulable_items)
            console.log(`📅 ${chat.pending_schedulable_items.length} pending schedulable items`)
          }
        } else {
          // Reset commit tracking for non-committed chats
          setCommittedAt(null)
          setCommittedItems([])
          setMissingItems([])
          setSchedulingStatus(null)
          setPendingSchedulableItems([])
        }
        
        // Clear selection to show latest
        setSelectedMessageId(null)
        
        // Update lastSavedItemStatesRef to prevent immediate re-save after restore
        const restoredItemStatesObj: Record<string, ItemState> = {}
        if (chat.item_states && typeof chat.item_states === 'object') {
          Object.entries(chat.item_states).forEach(([key, value]) => {
            restoredItemStatesObj[key] = value as ItemState
          })
        }
        lastSavedItemStatesRef.current = JSON.stringify({
          itemStates: restoredItemStatesObj,
          rejectedItems: chat.rejected_items || []
        })
      }
    } catch (error) {
      console.error('Error loading chat:', error)
    } finally {
      // Clear restoring flag after a short delay to allow state updates to settle
      setTimeout(() => {
        isRestoringRef.current = false
      }, 100)
    }
  }

  const sendMessage = async () => {
    const message = input.trim()
    // Prevent concurrent requests - if already loading or thinking, ignore
    if (!message || isLoading || isThinking) {
      console.log('⏸️  Message send blocked - already processing:', { isLoading, isThinking })
      return
    }
    
    // Block if there are unsaved edits
    if (hasUnsavedEdits) {
      console.log('⏸️  Message send blocked - unsaved edits in progress')
      modal.alert('Unsaved Edits', 'Please save or cancel your item edits before sending a new message.', 'warning')
      return
    }

    // If this conversation was already committed, ask the user what to do.
    // Lifecoach has no per-conversation commit/new-chat model (one perpetual
    // thread), so this gate never applies there.
    if (committedAt && !isLifecoach) {
      const continueHere = await modal.confirm(
        'This conversation has been saved',
        <div className="space-y-3">
          <p className="text-secondary">
            You already saved items from this conversation to your Life Map.
            Starting a <strong>new conversation</strong> is recommended — it keeps
            your topics focused and saves on processing time.
          </p>
          <p className="text-sm text-muted">
            You can also continue here, but new items will accumulate alongside
            previously saved ones.
          </p>
        </div>,
        {
          confirmText: 'Continue Here',
          cancelText: 'Start New Chat',
          variant: 'warning' as ModalVariant,
        }
      )
      if (!continueHere) {
        newChat()
        return
      }
      // User chose to continue — reset committed state so new items can be saved
      setCommittedAt(null)
      setCommittedItems([])
      setMissingItems([])
    }

    // With You: persist this user message as an entry against the active session
    if (withYouMode && withYouSessionId) {
      withYouService
        .recordEntry({ session_id: withYouSessionId, raw_text: input.trim(), source: 'manual_open' })
        .catch((err) => console.warn('[with_you] recordEntry failed', err))
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: message,
      timestamp: new Date()
    }
    
    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)
    
    // Split pane after first message
    if (!hasSplitPane) {
      setHasSplitPane(true)
    }

    // Create placeholder for yanamas' response
    const hortonMessageId = `horton_${Date.now()}`
    const hortonMessage: ChatMessage = {
      id: hortonMessageId,
      type: 'horton',
      content: '',
      timestamp: new Date(),
      session_id: currentSessionId || undefined
    }
    
    setMessages(prev => [...prev, hortonMessage])
    setCurrentHortonMessageId(hortonMessageId)
    setCurrentThinking('')
    setIsThinking(true)
    
    // Reset phase progress
    setCurrentPhase(null)
    setPhaseHistory([])

    // Use yanamas V3 Dream Engine if enabled
    if (useV3Engine) {
      try {
        console.log(`🚀 Using ${BRAND.assistant} V3 Dream Engine`)
        
        // Simulated phase progress for V3 (runs in parallel with API call)
        const phaseDefinitions = [
          { phase: 0, name: "Starting", icon: "🚀", description: "Initializing Dream Engine..." },
          { phase: 1, name: "Understanding", icon: "🧠", description: "Analyzing your message..." },
          { phase: 2, name: "Planning", icon: "🗺️", description: "Mapping out the approach..." },
          { phase: 3, name: "Building", icon: "🔧", description: "Creating your dream hierarchy..." },
          { phase: 4, name: "Composing", icon: "✍️", description: "Crafting your response..." },
        ]
        
        let phaseIndex = 0
        let phasesDoneAt = 0
        const runStartedAt = Date.now()
        // Warm "still working" lines shown once the simulated phases finish but
        // the reply is still in flight, so a long turn never looks frozen.
        const REASSURANCES = [
          "This one's rich — I'm mapping out all the pieces…",
          "Organizing your goals, habits, and the people involved…",
          "Connecting this to what already matters to you…",
          "Almost there — putting your map together…",
          "Thanks for hanging in — a big share takes a moment…",
        ]
        const phaseInterval = setInterval(() => {
          if (phaseIndex < phaseDefinitions.length) {
            const prevPhase = phaseIndex > 0 ? phaseDefinitions[phaseIndex - 1] : null
            if (prevPhase) {
              setPhaseHistory(prev => [...prev, prevPhase.name])
            }
            setCurrentPhase(phaseDefinitions[phaseIndex])
            phaseIndex++
          } else {
            // Phases done, reply still coming: rotate a reassurance line (every
            // ~5s) with a live elapsed counter instead of freezing at 5/5.
            if (!phasesDoneAt) phasesDoneAt = Date.now()
            const held = Date.now() - phasesDoneAt
            const total = Math.round((Date.now() - runStartedAt) / 1000)
            const line = REASSURANCES[Math.floor(held / 5000) % REASSURANCES.length]
            setCurrentPhase({
              phase: 4,
              name: held > 15000 ? 'Still working' : 'Composing',
              icon: held > 15000 ? '⏳' : '✨',
              description: `${line}${total > 8 ? ` · ${total}s` : ''}`,
              working: true,
            })
          }
        }, 1200) // Advance phase every 1.2 seconds
        
        // Start with first phase immediately
        setCurrentPhase(phaseDefinitions[0])
        
        // Build conversation history
        const history = messages.map(m => ({
          role: m.type === 'user' ? 'user' as const : 'assistant' as const,
          content: m.content,
        }))

        // Build rejected items context to pass to LLM
        const rejectedItemsContext = v3RejectedItems.length > 0 ? {
          rejected_items: v3RejectedItems.map(ri => ({
            type: ri.type,
            title: ri.title,
            reason: ri.rejectionComment || 'User rejected without comment'
          })),
          note: 'The user has rejected these suggested items. Please avoid suggesting similar items.'
        } : undefined

        const request: DreamerRequest = {
          message,
          session_id: currentSessionId || undefined,
          conversation_id: v3ConversationId || undefined,
          // Stable browser chat id → backend derives a stable engine session,
          // so a reload resumes THIS conversation instead of starting over.
          chat_id: chatId || undefined,
          turn_number: v3TurnNumber,
          persona: v3Persona,
          history,
          // Pass rejected items as additional context
          ...(rejectedItemsContext && { rejected_context: rejectedItemsContext }),
          // Shaping focus: bind this turn to the thread the user is "making real"
          // so the engine enriches it instead of extracting parallel items.
          ...(clarifyingFocus && { clarifying_thread_id: clarifyingFocus.id }),
          ...(refiningProposal && !clarifyingFocus && { clarifying_item: buildClarifyingItemPayload(refiningProposal) }),
        }

        const response = await hortonV3Service.runDreamer(request)
        
        // Stop phase simulation
        clearInterval(phaseInterval)

        // Confirm/refresh which engine handled this turn (in case the mount
        // probe raced or failed). Cheap, idempotent.
        if (response.runtime && response.runtime !== runtimeName) {
          setRuntimeName(response.runtime)
        }
        // Lifecoach: refresh the Life Map (consolidation may have run) and
        // accumulate this turn's spend.
        if (response.runtime === 'lifecoach' || isLifecoach) {
          setLifeMapRefresh((n) => n + 1)
          // Server figure wins; a turn whose ledger add failed sends null and
          // the last known value stays rather than dropping to zero.
          if (typeof response.cost_lifetime === 'number') {
            setLifecoachCost(response.cost_lifetime)
          }
        }
        
        if (response.debug) {
          console.log('🔧 Debug info received:', response.debug)
        }

        console.log('✅ V3 Response received:', {
          success: response.success,
          hasResponse: !!response.response,
          hasComposed: !!response.composed_response,
          latency: response.latency_ms,
          contextStatus: response.context_status,
        })

        // Update context status if provided
        if (response.context_status) {
          setContextStatus(response.context_status)
          console.log('📊 Context Status:', {
            status: response.context_status.status,
            percentage: response.context_status.percentage_used,
            remaining: response.context_status.remaining_tokens,
          })
        }

        // Update session tracking — but ONLY if the user is still in the
        // chat this request was sent from. A reply landing after a "New
        // chat"/history switch must not stamp the previous chat's ids onto
        // the current one (prod cross-contamination bug).
        if (chatIdRef.current === chatId) {
          if (response.session_id) setCurrentSessionId(response.session_id)
          if (response.conversation_id) setV3ConversationId(response.conversation_id)
        }
        setV3TurnNumber(prev => prev + 1)
        // One-shot refine focus: the enrichment turn just landed.
        setRefiningProposal(null)

        // Update LLM cost tracking from telemetry
        const telem = response.telemetry
        if (telem && telem.total_tokens > 0) {
          console.log('💰 Telemetry received:', telem)
          setSessionCost(prev => ({
            totalCost: prev.totalCost + (telem.total_cost_usd || 0),
            totalInputCost: prev.totalInputCost,
            totalOutputCost: prev.totalOutputCost,
            totalInputTokens: prev.totalInputTokens + (telem.total_input_tokens || 0),
            totalOutputTokens: prev.totalOutputTokens + (telem.total_output_tokens || 0),
            totalTokens: prev.totalTokens + (telem.total_tokens || 0),
            requestCount: prev.requestCount + 1,
            lastMessageCost: telem.total_cost_usd,
            currentModel: telem.nodes?.[0] ? `${telem.nodes[0].provider}/${telem.nodes[0].model}` : prev.currentModel,
            lastTelemetry: telem,
          }))
        } else if (response.composed_response?.cost_info) {
          const ci = response.composed_response.cost_info
          setSessionCost(prev => ({
            totalCost: prev.totalCost + (ci.message_cost || 0),
            totalInputCost: prev.totalInputCost,
            totalOutputCost: prev.totalOutputCost,
            totalInputTokens: prev.totalInputTokens,
            totalOutputTokens: prev.totalOutputTokens,
            totalTokens: prev.totalTokens + (ci.tokens_used || 0),
            requestCount: prev.requestCount + 1,
            lastMessageCost: ci.message_cost,
            currentModel: ci.model ? `${ci.service}/${ci.model}` : prev.currentModel,
          }))
        }

        // Update the message with the response and store V3 data for historical access
        setMessages(prev => prev.map(msg => 
          msg.id === hortonMessageId 
            ? { 
                ...msg, 
                content: response.response || 'I processed your request.',
                thinking_summary: response.composed_response?.thought_process?.join('\n'),
                v3_composed_response: response.composed_response,
                v3_agent_outputs: response.agent_outputs,
                v3_debug:
                  response.debug && typeof response.debug === 'object' && !Array.isArray(response.debug)
                    ? (response.debug as Record<string, unknown>)
                    : undefined,
                context_status: response.context_status,
                telemetry: telem || undefined,
                engine_turn_id: typeof response.turn_id === 'number' ? response.turn_id : undefined,
              }
            : msg
        ))

        // Voice: speak this reply when the person prefers to talk. Fire and
        // forget — a failure downgrades the control to its reason, and the
        // text is on screen either way.
        if (voiceOn && typeof response.turn_id === 'number') {
          void playReply(response.turn_id, true)      // auto: the app started it
        }

        // Conversation agenda: trail every turn; the first big multi-area
        // turn pins the checklist to its message.
        const respTrail = (response.composed_response as any)?.agenda_trail
        if (Array.isArray(respTrail)) setAgendaTrail(respTrail)
        {
          const roots = (response.composed_response?.items_summary || []).filter(
            (i: any) => !i.parent && !i.parent_id
          )
          if (!agendaMsgId && roots.length >= 3) {
            setAgendaRoots(roots.map((r: any) => ({ title: r.title, type: r.type })))
            setAgendaMsgId(hortonMessageId)
          }
        }

        // Update the composed response for hierarchy display
        // Merge items from this response with existing items
        if (response.composed_response) {
          // Hoist to a narrowed const so the setState callback (which runs
          // outside this flow analysis) keeps the non-undefined type.
          const composedResponse = response.composed_response

          console.log('🔍 New response received:', {
            hasComposedResponse: !!composedResponse,
            newItemsCount: composedResponse.items_summary?.length || 0,
            newItems: composedResponse.items_summary?.map((i: any) => `${i.type}:${i.title}`)
          })

          setV3ComposedResponse(prevResponse => {
            // If no previous response, use new one
            if (!prevResponse) {
              console.log('📝 First response, initializing with', composedResponse.items_summary?.length || 0, 'items')
              return composedResponse
            }

            // Merge: accumulate items_summary, recommendations, questions, and thought_process
            // across all messages to preserve context throughout the conversation
            const existingItems = prevResponse.items_summary || []
            const newItems = composedResponse.items_summary || []
            
            console.log('🔄 Merging items:', {
              existingCount: existingItems.length,
              newCount: newItems.length,
              existingItems: existingItems.map(i => `${i.type}:${i.title}`),
              newItems: newItems.map(i => `${i.type}:${i.title}`)
            })
            
            // Combine items, avoiding duplicates based on type+title
            const itemsMap = new Map()
            
            // Add existing items
            existingItems.forEach(item => {
              const key = `${item.type}:${item.title}`
              itemsMap.set(key, item)
            })
            
            // Add new items (overwrites if same key)
            newItems.forEach(item => {
              const key = `${item.type}:${item.title}`
              itemsMap.set(key, item)
            })
            
            const mergedItems = Array.from(itemsMap.values())
            
            console.log('✅ Merge complete:', {
              mergedCount: mergedItems.length,
              mergedItems: mergedItems.map(i => `${i.type}:${i.title}`)
            })
            
            // Merge recommendations - combine old and new, avoiding duplicates
            const existingRecs = prevResponse.recommendations || []
            const newRecs = composedResponse.recommendations || []
            const mergedRecs = [...new Set([...existingRecs, ...newRecs])]

            // Merge clarifying questions - combine old and new, avoiding duplicates
            const existingQuestions = prevResponse.clarifying_questions || []
            const newQuestions = composedResponse.clarifying_questions || []
            const mergedQuestions = [...new Set([...existingQuestions, ...newQuestions])]

            // Merge thought process - keep both old and new thoughts
            const existingThoughts = prevResponse.thought_process || []
            const newThoughts = composedResponse.thought_process || []
            const mergedThoughts = [...existingThoughts, ...newThoughts]

            return {
              ...composedResponse,
              items_summary: mergedItems,
              // Keep items_hierarchy from latest if it exists
              items_hierarchy: composedResponse.items_hierarchy || prevResponse.items_hierarchy || [],
              // Preserve and accumulate recommendations, questions, and thoughts
              recommendations: mergedRecs.length > 0 ? mergedRecs : undefined,
              clarifying_questions: mergedQuestions.length > 0 ? mergedQuestions : undefined,
              thought_process: mergedThoughts.length > 0 ? mergedThoughts : undefined
            }
          })
        }

        // Auto-select this message to highlight it, but keep accumulated items visible
        setSelectedMessageId(hortonMessageId)
        
        // Split pane to show items
        setHasSplitPane(true)

        setIsLoading(false)
        setIsThinking(false)
        setCurrentThinking('')
        setCurrentHortonMessageId(null)
        // Clear phase progress on completion
        setCurrentPhase(null)
        setPhaseHistory([])
        inputRef.current?.focus()
        return
      } catch (error: any) {
        console.error('V3 Error:', error)
        // Clear any running phase interval on error
        setMessages(prev => prev.map(msg => 
          msg.id === hortonMessageId 
            ? { ...msg, content: `⚠️ Error: ${error.message}` }
            : msg
        ))
        setIsLoading(false)
        setIsThinking(false)
        setCurrentThinking('')
        // Clear phase progress on error
        setCurrentPhase(null)
        setPhaseHistory([])
        inputRef.current?.focus()
        return
      }
    }

    // Legacy streaming endpoint
    try {
      const response = await fetch('/horton/process/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message,
          session_id: currentSessionId,
          conversation_history: []
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      if (!response.body) {
        throw new Error('Response has no body')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let thinkingText = ''
      let lastCompleteResult: any = null // Store last complete result for error recovery

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.trim() === '') continue
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6).trim()
              if (!jsonStr) continue
              const data = JSON.parse(jsonStr)
              
              // Log all SSE events for debugging
              console.log('📡 SSE Event received:', data.type, data.content ? `content: ${data.content.substring(0, 50)}...` : '', data)

              // Handle phase events for progress display
              if (data.type === 'phases') {
                // Initial phases info - could cache if needed
                console.log('📋 Phases initialized:', data.data)
              } else if (data.type === 'phase') {
                // Phase transition
                console.log(`🔄 Phase ${data.phase}: ${data.icon} ${data.name} - ${data.description}`)
                // Add previous phase to history
                setCurrentPhase(prev => {
                  if (prev && prev.phase < data.phase) {
                    setPhaseHistory(history => [...history, prev.name])
                  }
                  return {
                    phase: data.phase,
                    name: data.name,
                    icon: data.icon,
                    description: data.description
                  }
                })
              } else if (data.type === 'node') {
                // Node completion - update description
                console.log(`⚡ Node: ${data.node} - ${data.description}`)
                setCurrentPhase(prev => prev ? { ...prev, description: data.description } : null)
              } else if (data.type === 'thinking_start') {
                console.log('💭 Thinking started')
                setIsThinking(true)
                thinkingText = ''
                setCurrentThinking('')
                // Initialize thinking field in message
                setMessages(prev => prev.map(msg => 
                  msg.id === hortonMessageId 
                    ? { ...msg, thinking: '' }
                    : msg
                ))
              } else if (data.type === 'thinking') {
                thinkingText += data.content
                console.log('🧠 Thinking token received:', data.content?.substring(0, 50), 'Total length:', thinkingText.length)
                // Update message with accumulated thinking for real-time display
                setMessages(prev => prev.map(msg => 
                  msg.id === hortonMessageId 
                    ? { ...msg, thinking: thinkingText }
                    : msg
                ))
              } else if (data.type === 'thinking_end') {
                console.log('✅ Thinking ended, total length:', thinkingText.length)
                setIsThinking(false)
                // Store final thinking in message
                setMessages(prev => prev.map(msg => 
                  msg.id === hortonMessageId 
                    ? { ...msg, thinking: thinkingText }
                    : msg
                ))
              } else if (data.type === 'thinking_summary') {
                console.log('📝 Thinking summary received:', data.content?.substring(0, 100))
                // Store summarized thinking
                setMessages(prev => prev.map(msg => 
                  msg.id === hortonMessageId 
                    ? { 
                        ...msg, 
                        thinking_summary: data.content,
                        thinking_original_length: data.original_length,
                        thinking_summary_length: data.summary_length
                      }
                    : msg
                ))
              } else if (data.type === 'complete') {
                const result = data.data
                lastCompleteResult = result // Store for error recovery
                
                console.log('✅ Stream complete, updating message and life map:', {
                  hasResponse: !!result.response,
                  hasLifeMap: !!result.life_map,
                  lifeMapItemCount: result.life_map ? Object.keys(result.life_map.hierarchy_items || {}).length : 0,
                  hasThinking: !!thinkingText,
                  thinkingLength: thinkingText.length
                })
                
                // Update message with response - preserve thinking if it exists
                setMessages(prev => prev.map(msg => {
                  if (msg.id === hortonMessageId) {
                    const updated = { 
                      ...msg, 
                      content: result.response || 'No response received',
                      lifeMap: result.life_map,
                      classification: result.classification,
                      confidence: result.classification?.primary?.confidence
                    }
                    // Preserve thinking if it was set
                    if (thinkingText && thinkingText.trim().length > 0) {
                      updated.thinking = thinkingText
                    }
                    console.log('📝 Updated message:', {
                      id: updated.id,
                      hasThinking: !!updated.thinking,
                      thinkingLength: updated.thinking?.length || 0,
                      hasThinkingSummary: !!updated.thinking_summary
                    })
                    return updated
                  }
                  return msg
                }))
                
                // Guard: never adopt a session id from a reply that belongs
                // to a chat the user has since navigated away from.
                if (result.session_id && chatIdRef.current === chatId) {
                  setCurrentSessionId(result.session_id)
                }

                // Update life map (only from the response - no polling/fallback)
                if (result.life_map) {
                  console.log('📊 Updating life map with', Object.keys(result.life_map.hierarchy_items || {}).length, 'item types')
                  setLifeMapData(result.life_map)
                } else {
                  console.warn('⚠️ No life_map in complete event')
                }
                // Note: If life_map is not in the response, we don't fetch it separately.
                // The life map is specific to this conversation and only updates with new messages.
                
                // Update LLM cost tracking from telemetry or cost_info
                if (result.telemetry && result.telemetry.total_tokens > 0) {
                  const t = result.telemetry
                  setSessionCost(prev => ({
                    totalCost: prev.totalCost + (t.total_cost_usd || 0),
                    totalInputCost: prev.totalInputCost,
                    totalOutputCost: prev.totalOutputCost,
                    totalInputTokens: prev.totalInputTokens + (t.total_input_tokens || 0),
                    totalOutputTokens: prev.totalOutputTokens + (t.total_output_tokens || 0),
                    totalTokens: prev.totalTokens + (t.total_tokens || 0),
                    requestCount: prev.requestCount + 1,
                    lastMessageCost: t.total_cost_usd,
                    currentModel: t.nodes?.[0] ? `${t.nodes[0].provider}/${t.nodes[0].model}` : prev.currentModel,
                    lastTelemetry: t,
                  }))
                } else if (result.cost_info) {
                  const ci = result.cost_info
                  setSessionCost(prev => ({
                    totalCost: prev.totalCost + (ci.message_cost || 0),
                    totalInputCost: prev.totalInputCost + (ci.input_cost || 0),
                    totalOutputCost: prev.totalOutputCost + (ci.output_cost || 0),
                    totalInputTokens: prev.totalInputTokens + (ci.input_tokens || 0),
                    totalOutputTokens: prev.totalOutputTokens + (ci.output_tokens || 0),
                    totalTokens: prev.totalTokens + (ci.tokens_used || 0),
                    requestCount: prev.requestCount + 1,
                    lastMessageCost: ci.message_cost,
                    lastInputCost: ci.input_cost,
                    lastOutputCost: ci.output_cost,
                    currentModel: `${ci.service}/${ci.model}`,
                  }))
                }
              } else if (data.type === 'done') {
                setIsThinking(false)
                setCurrentThinking('')
                setCurrentHortonMessageId(null)
                // Clear phase progress
                setCurrentPhase(null)
                setPhaseHistory([])
              } else if (data.type === 'error') {
                const errorMessage = data.message || data.content || 'Unknown error'
                console.error('❌ Stream error:', errorMessage)
                // Don't throw - instead update the message with error info
                // But preserve any response/life map we already received
                setMessages(prev => prev.map(msg => {
                  if (msg.id === hortonMessageId) {
                    // If we have a response, append error; otherwise show error
                    const existingContent = msg.content || ''
                    const hasContent = existingContent && existingContent.trim() !== ''
                    return {
                      ...msg,
                      content: hasContent 
                        ? `${existingContent}\n\n⚠️ Note: An error occurred during processing: ${errorMessage}`
                        : `⚠️ Error: ${errorMessage}. Please try again.`,
                      error: true
                    }
                  }
                  return msg
                }))
                // Still try to get life map from last complete result if available
                if (lastCompleteResult && lastCompleteResult.life_map) {
                  console.log('📊 Recovering life map from last complete result')
                  setLifeMapData(lastCompleteResult.life_map)
                }
                // Don't break - let the stream finish to get 'done' event
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line)
            }
          }
        }
      }

      setIsLoading(false)
      setCurrentPhase(null)
      setPhaseHistory([])
      inputRef.current?.focus()
    } catch (error: any) {
      console.error('Error:', error)
      setMessages(prev => prev.map(msg => 
        msg.id === hortonMessageId 
          ? { ...msg, content: `⚠️ Error: ${error.message}` }
          : msg
      ))
      setIsLoading(false)
      setIsThinking(false)
      setCurrentThinking('')
      setCurrentPhase(null)
      setPhaseHistory([])
      inputRef.current?.focus()
    }
  }

  // NOTE: refreshLifeMap() has been removed.
  // The life map is specific to each conversation and only updates when:
  // 1. A new message is sent and the backend returns a life_map in the response
  // 2. A chat is loaded from history (loadChat function)
  // 3. A suggestion version is loaded (loadSuggestionVersion function)
  // There is no need for polling or separate fetching - the life map comes with the message response.

  const loadSuggestionVersion = (messageId: string, versionIndex: number) => {
    const message = messages.find(m => m.id === messageId)
    if (!message || !message.suggestion_versions || !message.suggestion_versions[versionIndex]) {
      return
    }
    
    const version = message.suggestion_versions[versionIndex]
    if (version.life_map) {
      setLifeMapData(version.life_map)
    }
  }

  const toggleSection = (type: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(type)) {
        newSet.delete(type)
      } else {
        newSet.add(type)
      }
      return newSet
    })
  }


  // Server-authoritative conversation id (unique per user). Falls back to a
  // local id only if the server is unreachable, so a new chat always works.
  const mintChatId = async (): Promise<string> => {
    try {
      const resp = await fetch('/api/horton-v3/conversation/new', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (resp.ok) {
        const data = await resp.json()
        if (data?.conversation_id) return data.conversation_id as string
      }
    } catch (e) {
      console.warn('server conversation id unavailable; using local id', e)
    }
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  const newChat = async () => {
    stopSpeaking()   // a reply must not keep talking into the next chat
    setMessages([])
    setLifeMapData(null)
    setCurrentThinking('')
    setIsThinking(false)
    setHasSplitPane(false)
    setExpandedSections(new Set())
    // Ask the server for a fresh, unique conversation id.
    const newChatId = await mintChatId()
    // Lifecoach multi-chat: mark the fresh chat active BEFORE setChatId so
    // the pin effect doesn't snap back to the previous thread.
    if (isLifecoach) rememberLifecoachChat(newChatId)
    setChatId(newChatId)
    sessionStorage.setItem('horton_current_chat_id', newChatId)
    setCurrentSessionId(null)
    // Reset V3 state
    setV3ComposedResponse(null)
    setV3ConversationId(null)
    setV3TurnNumber(1)
    // A fresh chat isn't committed, and carries no held items yet.
    setCommittedAt(null)
    setCommittedItems([])
    setHeldThreads([])
    setNoticedCandidates([])
    setHeldPeople([])
    setClarifyingFocus(null)
    setRefiningProposal(null)
    // Reset cost tracking for new chat
    setSessionCost({
      totalCost: 0,
      totalInputCost: 0,
      totalOutputCost: 0,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalTokens: 0,
      requestCount: 0
    })
    
    // Show subtle toast notification
    setToastMessage('New chat started • Previous saved')
    setTimeout(() => setToastMessage(null), 3000)
  }

  // Handle accepting a hierarchy item
  const handleAcceptItem = async (item: HierarchyItem, hierarchyType: string, skipSimilarCheck: boolean = false) => {
    try {
      console.log('✅ Accepting item:', item, 'Type:', hierarchyType)
      
      // Collect all items from life map to enable parent creation
      const allItemsInConversation: HierarchyItem[] = []
      if (lifeMapData) {
        Object.values(lifeMapData).forEach((items: any) => {
          if (Array.isArray(items)) {
            allItemsInConversation.push(...items)
          }
        })
      }
      
      // Accept item with semantic similarity detection and parent creation
      const createdItem = await acceptHierarchyItem(item, hierarchyType, {
        checkDuplicates: !skipSimilarCheck,
        ensureParentExists: true,
        allItemsInConversation,
        onParentCreated: (parentId, parentType) => {
          console.log(`✅ Auto-created parent ${parentType} with ID: ${parentId}`)
        },
        onSimilarItemsFound: async (foundSimilarItems) => {
          // Show dialog and wait for user decision
          return new Promise((resolve) => {
            setSimilarItems(foundSimilarItems)
            setPendingItem({ item, hierarchyType })
            setShowSimilarDialog(true)
            
            // Store the resolve function so we can call it from button handlers
            ;(window as any).__similarItemsResolve = resolve
          })
        },
        similarityThreshold: 0.75
      })
      console.log('✅ Item created successfully:', createdItem)
      
      // Update life map to mark item as accepted
      setLifeMapData((prev: any) => {
        if (!prev) return prev
        
        const updated = { ...prev }
        Object.keys(updated).forEach(key => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map((i: any) => {
              if (i.id === item.id || (i.details?.clean_summary === item.details?.clean_summary && i.details?.action === item.details?.action)) {
                return { ...i, status: 'confirmed', id: createdItem.id || i.id }
              }
              return i
            })
          }
        })
        return updated
      })
      
      // Get item ID and type from created item
      // Handle different response structures: {id: ...}, {data: {id: ...}}, or nested
      // The service returns the goal/aspiration/dream object directly with an id field
      let itemId: number | undefined
      
      // Try multiple possible structures
      if (createdItem?.id !== undefined && createdItem?.id !== null) {
        itemId = typeof createdItem.id === 'number' ? createdItem.id : parseInt(String(createdItem.id))
      } else if (createdItem?.data?.id !== undefined && createdItem?.data?.id !== null) {
        itemId = typeof createdItem.data.id === 'number' ? createdItem.data.id : parseInt(String(createdItem.data.id))
      } else if (createdItem?.success && createdItem?.data?.id !== undefined && createdItem?.data?.id !== null) {
        // Handle {success: true, data: {id: ...}} structure
        itemId = typeof createdItem.data.id === 'number' ? createdItem.data.id : parseInt(String(createdItem.data.id))
      }
      
      if (!itemId || isNaN(itemId)) {
        console.error('❌ Could not extract item ID from created item:', createdItem)
        throw new Error('Failed to extract item ID from created item. Please try again.')
      }
      
      console.log('📦 Extracted item ID:', itemId, 'from createdItem:', createdItem)
      
      const itemTypeMap: Record<string, string> = {
        'GOALS_OBJECTIVES': 'goal',
        'ASPIRATIONS_AMBITIONS': 'goal',
        'VISION_PURPOSE': 'dream',
        'DREAMS': 'dream',
        'TASKS_ACTIONS': 'task',
        'ACTION_ITEMS': 'action_item'
      }
      const itemType = itemTypeMap[hierarchyType] || 'goal'
      console.log('✅ Item created successfully:', { itemType, itemId })
    } catch (error: any) {
      console.error('❌ Error accepting item:', error)
      
      // Don't show error if user cancelled
      if (error.message === 'Item creation cancelled by user') {
        return
      }
      
      // Show user-friendly error message
      if (error.message && (error.message.includes('already exists') || error.message.includes('similar'))) {
        modal.alert('Similar Item Found', `${error.message}\n\nPlease check your existing items.`, 'warning')
      } else {
        modal.alert('Failed to Create Item', `${error.message || 'Unknown error'}\n\nPlease try again or check your network connection.`, 'error')
      }
    }
  }
  
  // Handle similar items dialog - proceed with creation
  const handleSimilarDialogProceed = () => {
    setShowSimilarDialog(false)
    const resolve = (window as any).__similarItemsResolve
    if (resolve) {
      resolve(true) // User chose to proceed
      delete (window as any).__similarItemsResolve
    }
  }
  
  // Handle similar items dialog - cancel
  const handleSimilarDialogCancel = () => {
    setShowSimilarDialog(false)
    const resolve = (window as any).__similarItemsResolve
    if (resolve) {
      resolve(false) // User chose to cancel
      delete (window as any).__similarItemsResolve
    }
    setPendingItem(null)
    setSimilarItems([])
  }
  
  /**
   * Handle item state change from DreamHierarchyDisplay
   */
  const handleItemStateChange = (itemKey: string, state: ItemState) => {
    setV3ItemStates(prev => new Map(prev).set(itemKey, state))
    console.log(`📝 Item state changed: ${itemKey} -> ${state.status}`)
  }

  /**
   * Handle rejected items change from DreamHierarchyDisplay
   */
  const handleRejectedItemsChange = (items: RejectedItem[]) => {
    setV3RejectedItems(items)
    console.log(`🚫 Rejected items updated: ${items.length} items`)
  }

  /**
   * Commit all items to the dream hierarchy
   * This creates all non-rejected items with their edits
   */
  const handleCommitItems = async () => {
    // First, check which items already exist in the database
    setIsCommittingItems(true) // Show loading state during pre-check
    
    // Count all items and check for existing ones
    let totalItems = 0
    let rejectedCount = 0
    let existingCount = 0
    let newCount = 0
    const existingItemsList: string[] = []
    const newItemsList: string[] = []
    
    // Cache fetched items to avoid repeated API calls
    const itemsCache: Record<string, any[]> = {}
    
    // Helper to get items of a type (with caching)
    const getItemsOfType = async (itemType: string): Promise<any[]> => {
      if (itemsCache[itemType]) {
        return itemsCache[itemType]
      }
      
      let items: any[] = []
      try {
        if (itemType === 'dream') {
          items = await dreamService.getDreams()
        } else if (itemType === 'aspiration') {
          items = await aspirationService.getAspirations()
        } else if (itemType === 'goal') {
          items = await goalService.getGoals()
        } else if (itemType === 'task') {
          items = await taskService.getTasks()
        } else if (itemType === 'action_item') {
          items = await actionItemService.getActionItems()
        } else if (itemType === 'skill') {
          items = await skillService.getSkills()
        } else if (itemType === 'habit') {
          items = await habitService.getHabits()
        } else if (itemType === 'roadblock') {
          items = await roadblockService.getRoadblocks()
        }
        console.log(`📋 Fetched ${items.length} existing ${itemType}(s) from database`)
      } catch (error) {
        console.warn(`Error fetching ${itemType}s:`, error)
      }
      
      itemsCache[itemType] = items
      return items
    }
    
    // Helper to check if an item exists in the database
    const checkItemExists = async (title: string, itemType: string): Promise<boolean> => {
      const items = await getItemsOfType(itemType)
      
      // Check for exact or very similar title match
      const normalizedTitle = title.toLowerCase().trim()
      const exists = items.some((item: any) => {
        const existingTitle = (item.title || '').toLowerCase().trim()
        return existingTitle === normalizedTitle || 
               (existingTitle.length > 5 && normalizedTitle.length > 5 && (
                 existingTitle.includes(normalizedTitle) || 
                 normalizedTitle.includes(existingTitle)
               ))
      })
      
      if (exists) {
        console.log(`  ✓ Found existing ${itemType}: "${title}"`)
      }
      
      return exists
    }
    
    const countAndCheckItemsRecursively = async (item: any, itemType: string) => {
      const key = `${itemType}:${item.title}`
      const state = v3ItemStates.get(key)
      
      if (state?.status === 'rejected') {
        rejectedCount++
      } else {
        totalItems++
        
        // Check if this item already exists
        const exists = await checkItemExists(item.title, itemType)
        if (exists) {
          existingCount++
          existingItemsList.push(`${itemType}: ${item.title}`)
        } else {
          newCount++
          newItemsList.push(`${itemType}: ${item.title}`)
        }
      }
      
      // Count children recursively
      const childrenTypes = ['goals', 'skills', 'habits', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'aspirations']
      for (const childType of childrenTypes) {
        const children = item[childType] || item.children?.[childType.replace(/s$/, '')] || []
        const singularType = childType.replace(/s$/, '')
        if (Array.isArray(children) && children.length > 0) {
          for (const child of children) {
            await countAndCheckItemsRecursively(child, singularType)
          }
        }
      }
      
      // Also check for action_items in fields
      const fieldActionItems = item.fields?.action_items || []
      if (Array.isArray(fieldActionItems) && fieldActionItems.length > 0) {
        for (const ai of fieldActionItems) {
          await countAndCheckItemsRecursively(ai, 'action_item')
        }
      }
    }
    
    const isPersonType = (t: string) => t === 'identity' || t === 'person'

    try {
      // Count from items_summary (skip identity/person — they go to My World, not hierarchy)
      if (v3ComposedResponse?.items_summary) {
        for (const item of v3ComposedResponse.items_summary) {
          if (isPersonType(item.type)) continue
          await countAndCheckItemsRecursively(item, item.type || 'goal')
        }
      }
      
      // Count from items_hierarchy
      if (v3ComposedResponse?.items_hierarchy) {
        for (const item of v3ComposedResponse.items_hierarchy) {
          if (isPersonType(item.type)) continue
          await countAndCheckItemsRecursively(item, item.type || 'dream')
        }
      }
    } catch (error) {
      console.error('Error during pre-commit check:', error)
    }
    
    setIsCommittingItems(false) // Done with pre-check
    
    // Build the confirmation message with details about existing items
    let confirmMessage = `📋 Summary:\n\n`
    confirmMessage += `• ${newCount} new item(s) will be added\n`
    
    if (existingCount > 0) {
      confirmMessage += `• ${existingCount} item(s) already in your Life Map (will be linked, not duplicated)\n`
      if (existingItemsList.length <= 5) {
        confirmMessage += `\n  Already in your Life Map:\n`
        existingItemsList.forEach(item => {
          confirmMessage += `    ✓ ${item}\n`
        })
      } else {
        confirmMessage += `\n  Examples of existing items:\n`
        existingItemsList.slice(0, 5).forEach(item => {
          confirmMessage += `    ✓ ${item}\n`
        })
        confirmMessage += `    ... and ${existingItemsList.length - 5} more\n`
      }
    }
    
    if (rejectedCount > 0) {
      confirmMessage += `• ${rejectedCount} skipped item(s) will NOT be included\n`
    }
    
    // Build hierarchy for preview
    let previewHierarchy = v3ComposedResponse?.items_hierarchy || []
    const previewSummary = v3ComposedResponse?.items_summary || []
    
    if (previewHierarchy.length === 0 && previewSummary.length > 0) {
      previewHierarchy = buildHierarchyFromFlatItems(previewSummary)
    }
    
    // Build set of existing item keys
    const existingItemKeys = new Set(existingItemsList.map(item => {
      // Parse "type: title" format back to "type:title" key
      const match = item.match(/^(\w+):\s*(.+)$/)
      return match ? `${match[1]}:${match[2]}` : item
    }))
    
    // Convert to preview tree format for the advanced view
    let previewItems = convertToPreviewItems(previewHierarchy, v3ItemStates, existingItemKeys)
    
    // If hierarchy conversion resulted in items without children (flat), use grouped view instead
    const hasAnyChildren = previewItems.some(item => item.children && item.children.length > 0)
    if (!hasAnyChildren && previewSummary.length > 0) {
      console.log('📋 Falling back to grouped flat view for preview')
      previewItems = convertFlatItemsToPreview(previewSummary, v3ItemStates, existingItemKeys)
    }
    
    // Collect identity/person items for display
    const identityPersonItems: Array<{title: string, type: string, description: string}> = []
    for (const item of previewSummary) {
      if (item.type === 'identity' || item.type === 'person') {
        identityPersonItems.push({
          title: item.title || '',
          type: item.type,
          description: item.description || '',
        })
      }
    }

    // Collect schedulable items (tasks, habits, skills, roadblocks) for the preview
    const PREVIEW_SCHEDULABLE_TYPES = ['task', 'action_item', 'habit', 'skill', 'roadblock']
    const schedulableItemsToAdd: Array<{title: string, type: string, status: string}> = []
    const collectSchedulableItems = (items: any[], source: string) => {
      for (const item of items) {
        const itemType = item.type || 'goal'
        const key = `${itemType}:${item.title}`
        const state = v3ItemStates.get(key)
        
        if (state?.status !== 'rejected') {
          if (PREVIEW_SCHEDULABLE_TYPES.includes(itemType)) {
            schedulableItemsToAdd.push({
              title: state?.editedFields?.title || item.title,
              type: itemType,
              status: existingItemKeys.has(key) ? 'existing' : 'new'
            })
          }
          
          // Also check nested action_items in fields
          if (item.fields?.action_items && Array.isArray(item.fields.action_items)) {
            for (const ai of item.fields.action_items) {
              const aiKey = `action_item:${ai.title}`
              const aiState = v3ItemStates.get(aiKey)
              if (aiState?.status !== 'rejected') {
                schedulableItemsToAdd.push({
                  title: aiState?.editedFields?.title || ai.title,
                  type: 'action_item',
                  status: existingItemKeys.has(aiKey) ? 'existing' : 'new'
                })
              }
            }
          }
        }
        
        // Recurse into children
        const childArrays = ['goals', 'skills', 'habits', 'tasks', 'action_items', 'aspirations', 'roadblocks']
        for (const arrayName of childArrays) {
          const children = item[arrayName] || item.children?.[arrayName.replace(/s$/, '')] || []
          if (Array.isArray(children) && children.length > 0) {
            collectSchedulableItems(children, `${source}.${arrayName}`)
          }
        }
      }
    }
    
    // Collect from both hierarchy and summary
    if (previewHierarchy.length > 0) {
      collectSchedulableItems(previewHierarchy, 'hierarchy')
    }
    if (previewSummary.length > 0) {
      collectSchedulableItems(previewSummary, 'summary')
    }
    
    // Deduplicate by title+type
    const uniqueSchedulableItems = Array.from(
      new Map(schedulableItemsToAdd.map(t => [`${t.type}:${t.title}`, t])).values()
    )
    
    console.log('🎯 Preview items for modal:', previewItems.length, 'root items,', uniqueSchedulableItems.length, 'schedulable items')
    
    // Group schedulable items by type for display
    const itemTypeEmoji: Record<string, string> = {
      task: '📋', action_item: '✓', habit: '🔄', skill: '🎯', roadblock: '🧱'
    }
    const itemTypeLabel: Record<string, string> = {
      task: 'Task', action_item: 'Action Item', habit: 'Habit', skill: 'Skill', roadblock: 'Roadblock'
    }
    const itemTypeBadgeClass: Record<string, string> = {
      task: 'bg-brand-subtle text-brand',
      action_item: 'bg-brand-subtle text-brand',
      habit: 'bg-ok/20 text-ok',
      skill: 'bg-warn/20 text-warn',
      roadblock: 'bg-bad/20 text-bad',
    }

    // Create a stateful component for the modal content with tabs
    const SavePreviewContent = () => {
      const [activeTab, setActiveTab] = React.useState<'items' | 'people' | 'hierarchy'>('items')

      // Group items by type for sectioned display
      const groupedItems = uniqueSchedulableItems.reduce<Record<string, typeof uniqueSchedulableItems>>((acc, item) => {
        const group = item.type === 'action_item' ? 'task' : item.type
        if (!acc[group]) acc[group] = []
        acc[group].push(item)
        return acc
      }, {})

      const typeDisplayOrder = ['task', 'habit', 'skill', 'roadblock']

      return (
        <div className="w-full max-h-[80vh] overflow-y-auto">
          {/* Identity & People banner (always visible if present) */}
          {identityPersonItems.length > 0 && (
            <div className="mb-4 p-3 bg-brand-subtle rounded-lg border border-brand-subtle">
              <p className="text-xs font-medium text-brand uppercase tracking-wide mb-2">
                People mentioned
              </p>
              <div className="space-y-1.5">
                {identityPersonItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span>{item.type === 'identity' ? '👤' : '👥'}</span>
                    <span className="font-medium text-primary">{item.title}</span>
                    {item.description && (
                      <span className="text-muted text-xs truncate">— {item.description}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-brand mt-2">
                Will be saved to My World when you confirm
              </p>
            </div>
          )}

          {/* Tab buttons */}
          <div className="flex gap-1 p-1 bg-surface-alt rounded-lg mb-4">
            <button
              onClick={() => setActiveTab('items')}
              className={`flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5
                ${activeTab === 'items'
                  ? 'bg-surface text-ok shadow-sm'
                  : 'text-secondary hover:text-primary'
                }`}
            >
              <span>📋</span>
              <span>Items ({uniqueSchedulableItems.length})</span>
            </button>
            {identityPersonItems.length > 0 && (
              <button
                onClick={() => setActiveTab('people')}
                className={`flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5
                  ${activeTab === 'people'
                    ? 'bg-surface text-brand shadow-sm'
                    : 'text-secondary hover:text-primary'
                  }`}
              >
                <span>👥</span>
                <span>People ({identityPersonItems.length})</span>
              </button>
            )}
            <button
              onClick={() => setActiveTab('hierarchy')}
              className={`flex-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5
                ${activeTab === 'hierarchy'
                  ? 'bg-surface text-brand shadow-sm'
                  : 'text-secondary hover:text-primary'
                }`}
            >
              <span>🌳</span>
              <span>All ({newCount + existingCount})</span>
            </button>
          </div>

          {/* Tab content */}
          <div className="rounded-lg border border-soft overflow-hidden">
            {activeTab === 'items' ? (
              <div className="p-4 bg-surface max-h-64 overflow-y-auto">
                {uniqueSchedulableItems.length === 0 ? (
                  <div className="text-center py-6">
                    <span className="text-3xl mb-2 block">🎯</span>
                    <p className="text-sm text-muted">
                      No schedulable items in this batch — just dreams, goals, and context.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {typeDisplayOrder
                      .filter(type => groupedItems[type]?.length > 0)
                      .map(type => (
                        <div key={type}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm">{itemTypeEmoji[type]}</span>
                            <span className="text-xs font-semibold text-secondary uppercase tracking-wide">
                              {type === 'task' ? 'Tasks' : type === 'habit' ? 'Habits' : type === 'skill' ? 'Skills' : 'Roadblocks'}
                              {' '}({groupedItems[type].length})
                            </span>
                          </div>
                          <ul className="space-y-1.5">
                            {groupedItems[type].map((item, idx) => (
                              <li key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-alt transition-colors">
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${itemTypeBadgeClass[item.type]}`}>
                                  {itemTypeLabel[item.type]}
                                </span>
                                <span className="text-secondary flex-1 text-sm truncate">{item.title}</span>
                                {item.status === 'existing' ? (
                                  <span className="text-xs px-2 py-1 bg-brand-subtle text-brand rounded-full font-medium flex-shrink-0">
                                    saved
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-1 bg-ok/20 text-ok rounded-full font-medium flex-shrink-0">
                                    new
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            ) : activeTab === 'people' ? (
              <div className="p-4 bg-surface max-h-64 overflow-y-auto">
                <div className="space-y-3">
                  {identityPersonItems.map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-default">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{item.type === 'identity' ? '👤' : '👥'}</span>
                        <span className="font-medium text-primary">{item.title}</span>
                        <span className="text-xs px-2 py-0.5 bg-brand-subtle text-brand rounded-full">
                          {item.type === 'identity' ? 'You' : 'Person'}
                        </span>
                      </div>
                      {item.description && (
                        <p className="text-sm text-secondary ml-8">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-brand text-center mt-3">
                  People will be saved to My World when you confirm.
                </p>
              </div>
            ) : (
              <div className="bg-surface">
                <CommitPreviewTree
                  items={previewItems}
                  maxHeight="280px"
                  showCounts={true}
                />
              </div>
            )}
          </div>

          <p className="text-xs text-muted text-center mt-4">
            These will be added to your Life Map — schedule whenever you're ready.
          </p>
        </div>
      )
    }
    
    // Show confirmation modal with tabbed interface
    const confirmed = await modal.confirm(
      'Save to Your Life Map',
      <SavePreviewContent />,
      { confirmText: 'Save These Items', cancelText: 'Not Yet', size: 'full' }
    )
    
    if (!confirmed) {
      return
    }
    
    setIsCommittingItems(true)
    
    try {
      console.log('🎯 Starting item commit...')
      
      // Build hierarchy from items_summary - this creates a nested structure with proper parent-child relationships
      let itemsHierarchy = v3ComposedResponse?.items_hierarchy || []
      const itemsSummary = v3ComposedResponse?.items_summary || []

      // --- Rationing intake (shadow + create-but-flag enforcement) -----
      // Route non-rejected items through the rationing gate. The gate's
      // applied_decision drives behavior with NO extra flag in the FE:
      //  - enforce OFF -> every decision is 'legacy_pass_through' (active);
      //    we just render the teaching summary (shadow).
      //  - enforce ON  -> 'park' items are created with status='parked'
      //    (saved, excluded from active surfaces) and we link them back so
      //    the parking lot can activate them later.
      // Failures here are non-blocking: we always continue saving below.
      let rationingSummaryCopy: string | null = null
      const intakeIdByKey = new Map<string, number>()      // `${type}:${title}` -> intake_item_id
      try {
        const intakeItems: RationingIntakeItemInput[] = []
        const intakeKeys: string[] = []                    // parallel to intakeItems, by item_index
        for (const item of itemsSummary) {
          const itemType = (item.type || '').toLowerCase()
          if (itemType === 'identity' || itemType === 'person') continue
          const stateKey = `${itemType}:${item.title}`
          const state = v3ItemStates.get(stateKey)
          if (state?.status === 'rejected') continue
          const title = state?.editedFields?.title || item.title
          const intakeItem = toRationingIntakeItem({
            type: itemType,
            title,
            description: state?.editedFields?.description || item.description,
            life_area: state?.editedFields?.life_area || item.life_area,
            fields: { ...item.fields, ...(state?.editedFields || {}) },
          })
          if (intakeItem) {
            intakeItems.push(intakeItem)
            intakeKeys.push(`${itemType}:${title}`)
          }
        }
        const intakeResult = await acceptIntakeBatch(
          intakeItems,
          newIdempotencyKey(),
          'chat_save_modal',
        )
        if (intakeResult?.summary_copy) {
          rationingSummaryCopy = intakeResult.summary_copy
        }
        for (const decision of intakeResult?.items || []) {
          const key = intakeKeys[decision.item_index]
          if (!key) continue
          intakeIdByKey.set(key, decision.intake_item_id)
        }
      } catch (e) {
        console.warn('[rationing] intake skipped (non-blocking):', e)
      }

      if (itemsHierarchy.length === 0 && itemsSummary.length > 0) {
        itemsHierarchy = buildHierarchyFromFlatItems(itemsSummary)
        console.log('🏗️ Built hierarchy from flat items:', itemsHierarchy.length, 'root items')
      }

      // The intake gate's 'park' decision is recorded (telemetry; the
      // rationing ledger keeps its link via intakeIdByKey) but NO LONGER
      // stamps the nodes: the person reviewed these items and chose Save,
      // and "when a user adds a lifemap item manually, they have already
      // made up their mind and it does not have to be gated anywhere else"
      // (owner, 2026-09-11). Items the gate would have parked are created
      // exactly like the rest. The stamping block that stood here is gone.
      
      const createdItems: Array<{ id: string; type: string; title: string; isExisting?: boolean; resolverConfidence?: string; resolverScore?: number; fields?: any }> = []
      const errors: string[] = []
      const processedRoots = new Set<string>()
      let totalNewlyCreated = 0
      let totalReusedExisting = 0
      
      // Helper to check if an item (or any of its descendants) is rejected
      const hasRejectedItems = (item: any, itemType: string): boolean => {
        const itemKey = `${itemType}:${item.title}`
        const state = v3ItemStates.get(itemKey)
        if (state?.status === 'rejected') {
          return true
        }
        
        // Check children
        const childrenTypes = ['aspirations', 'goals', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'skills', 'habits']
        for (const childType of childrenTypes) {
          const children = item[childType] || item.children?.[childType.replace(/s$/, '')] || []
          const singularType = childType.replace(/s$/, '')
          if (Array.isArray(children) && children.length > 0) {
            for (const child of children) {
              if (hasRejectedItems(child, singularType)) {
                return true
              }
            }
          }
        }
        
        // Check action_items in fields
        const fieldActionItems = item.fields?.action_items || []
        if (Array.isArray(fieldActionItems) && fieldActionItems.length > 0) {
          for (const ai of fieldActionItems) {
            const aiKey = `action_item:${ai.title}`
            const aiState = v3ItemStates.get(aiKey)
            if (aiState?.status === 'rejected') {
              return true
            }
          }
        }
        
        return false
      }
      
      // Helper to filter out rejected items from a subtree
      const filterRejectedItems = (item: any, itemType: string): any | null => {
        const itemKey = `${itemType}:${item.title}`
        const state = v3ItemStates.get(itemKey)
        
        // If this item is rejected, return null (skip it)
        if (state?.status === 'rejected') {
          console.log(`⏭️ Filtering out rejected item: ${itemKey}`)
          return null
        }
        
        // Apply edits if any
        let filteredItem = { ...item }
        if (state?.editedFields) {
          // Build the rich recurrence object if we have recurrence_rule
          // This is the planner format that the backend expects
          let recurrenceObject = state.editedFields.recurrence_rule || item.fields?.recurrence

          // Single editable-field contract (see ItemEdit in DreamHierarchyDisplay):
          // copy EVERY edited field into `fields` — no per-field whitelist that
          // could silently drop edit-form writes (habit cadence, skill levels, …).
          // title/description/life_area live at the top level of the item, and
          // recurrence/recurrence_rule collapse into the rich `recurrence` object.
          const {
            title: _title,
            description: _description,
            life_area: _lifeArea,
            recurrence: _recurrence,
            recurrence_rule: _recurrenceRule,
            ...editedFieldBag
          } = state.editedFields

          filteredItem = {
            ...item,
            title: state.editedFields.title || item.title,
            description: state.editedFields.description || item.description,
            life_area: state.editedFields.life_area || item.life_area,
            fields: {
              ...item.fields,
              ...editedFieldBag,
              recurrence: recurrenceObject  // Use the rich recurrence object
            }
          }
        }
        
        // Filter children
        const childrenTypes = ['aspirations', 'goals', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'skills', 'habits']
        for (const childType of childrenTypes) {
          const children = item[childType] || item.children?.[childType.replace(/s$/, '')] || []
          const singularType = childType.replace(/s$/, '')
          if (Array.isArray(children) && children.length > 0) {
            const filteredChildren = children
              .map((child: any) => filterRejectedItems(child, singularType))
              .filter((child: any) => child !== null)
            
            filteredItem[childType] = filteredChildren
            if (filteredItem.children) {
              filteredItem.children[childType.replace(/s$/, '')] = filteredChildren
            }
          }
        }
        
        // Filter action_items in fields
        if (item.fields?.action_items && Array.isArray(item.fields.action_items)) {
          const filteredActionItems = item.fields.action_items
            .filter((ai: any) => {
              const aiKey = `action_item:${ai.title}`
              const aiState = v3ItemStates.get(aiKey)
              if (aiState?.status === 'rejected') {
                console.log(`⏭️ Filtering out rejected action_item: ${ai.title}`)
                return false
              }
              return true
            })
          
          filteredItem.fields = {
            ...filteredItem.fields,
            action_items: filteredActionItems
          }
        }
        
        return filteredItem
      }
      
      // Process each root item in the hierarchy using acceptV3EntireSubtree
      // Identity/person items are handled separately via the graph API below.
      for (const rootItem of itemsHierarchy) {
        // items_hierarchy roots are typed DreamNode but can be any node type
        // at runtime (flat rebuilds, world entries) — treat as a plain string.
        const rootType: string = rootItem.type || 'dream'
        if (rootType === 'identity' || rootType === 'person') continue
        const rootKey = `${rootType}:${rootItem.title}`
        
        // Skip if already processed
        if (processedRoots.has(rootKey)) {
          continue
        }
        processedRoots.add(rootKey)
        
        // Check if root item itself is rejected
        const rootState = v3ItemStates.get(rootKey)
        if (rootState?.status === 'rejected') {
          console.log(`⏭️ Skipping rejected root item: ${rootKey}`)
          continue
        }
        
        // Filter out rejected items from the subtree
        const filteredRoot = filterRejectedItems(rootItem, rootType)
        if (!filteredRoot) {
          continue
        }
        
        console.log(`🌳 Processing root subtree: ${rootType} "${filteredRoot.title}"`)
        
        try {
          // Use acceptV3EntireSubtree to create the entire subtree with proper parent-child relationships
          const result = await acceptV3EntireSubtree(
            itemsHierarchy,
            filteredRoot,
            rootType,
            {
              skipDuplicateCheck: false,
              onItemCreated: (type, id, title) => {
                console.log(`✅ Item processed: ${type} "${title}" (ID: ${id})`)
              },
              similarityThreshold: 0.8
            }
          )
          
          // Add all created items to our list
          for (const item of result.createdItems) {
            createdItems.push({
              id: String(item.id),
              type: item.type,
              title: item.title,
              isExisting: item.isExisting,
              resolverConfidence: item.resolverConfidence,
              resolverScore: item.resolverScore
            })
          }
          
          // Track stats
          totalNewlyCreated += result.stats?.newlyCreated || 0
          totalReusedExisting += result.stats?.reusedExisting || 0
          
          if (!result.success && result.errors.length > 0) {
            errors.push(...result.errors)
          }
        } catch (error: any) {
          console.error(`❌ Error creating subtree for ${rootKey}:`, error)
          errors.push(`${rootItem.title}: ${error.message}`)
        }
      }
      
      // Save person/identity items to the life graph via the graph API
      const personItems = (v3ComposedResponse?.items_summary || [])
        .filter((item: any) => item.type === 'identity' || item.type === 'person')
      if (personItems.length > 0) {
        // Fetch existing graph nodes to avoid creating duplicates
        let existingPersonTitles: Set<string> = new Set()
        try {
          const graphResp = await fetch('/api/horton-v3/life-graph', { credentials: 'include' })
          if (graphResp.ok) {
            const graphData = await graphResp.json()
            const nodes = graphData?.flat?.nodes || []
            for (const n of nodes) {
              if (n.type === 'identity' || n.type === 'person') {
                existingPersonTitles.add((n.title || '').trim().toLowerCase())
              }
            }
          }
        } catch { /* proceed without dedup */ }

        for (const personItem of personItems) {
          const pType = personItem.type || 'person'
          const pKey = `${pType}:${personItem.title}`
          const pState = v3ItemStates.get(pKey)
          if (pState?.status === 'rejected') {
            console.log(`⏭️ Skipping rejected person item: ${pKey}`)
            continue
          }
          const title = pState?.editedFields?.title || personItem.title
          if (existingPersonTitles.has(title.trim().toLowerCase())) {
            console.log(`⏭️ Skipping existing person: "${title}"`)
            createdItems.push({ id: '', type: pType, title, isExisting: true })
            totalReusedExisting++
            continue
          }
          const description = pState?.editedFields?.description || personItem.description || ''
          const rawFields = personItem.metadata || personItem.fields || {}
          const PERSON_META_KEYS = ['age', 'occupation', 'school', 'grade', 'aliases', 'relationship_type', 'interests', 'hobbies', 'strengths', 'nickname']
          const metadata: Record<string, any> = {}
          for (const k of PERSON_META_KEYS) {
            if (rawFields[k] != null) metadata[k] = rawFields[k]
          }
          if (pState?.editedFields) {
            Object.assign(metadata, pState.editedFields)
          }
          try {
            const resp = await fetch('/api/horton-v3/life-graph/nodes', {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ node_type: pType, title, description, metadata }),
            })
            if (resp.ok) {
              const created = await resp.json()
              console.log(`✅ Created ${pType} node: "${title}" (ID: ${created.id})`)
              createdItems.push({ id: created.id, type: pType, title })
              totalNewlyCreated++
            } else {
              console.error(`❌ Failed to create ${pType} "${title}": ${resp.status}`)
              errors.push(`${pType} "${title}": server returned ${resp.status}`)
            }
          } catch (err: any) {
            console.error(`❌ Error creating ${pType} "${title}":`, err)
            errors.push(`${pType} "${title}": ${err.message}`)
          }
        }
      }

      // Deduplicate createdItems by type:title, preferring isExisting=true if duplicate
      // This handles cases where the same item might be processed multiple times
      const seenItems = new Map<string, typeof createdItems[0]>()
      for (const item of createdItems) {
        const key = `${item.type}:${item.title}`
        const existing = seenItems.get(key)
        if (!existing) {
          seenItems.set(key, item)
        } else if (item.isExisting && !existing.isExisting) {
          // If this one is existing and the previous wasn't, use this one
          seenItems.set(key, item)
        }
        // Otherwise keep the first one
      }
      const deduplicatedItems = Array.from(seenItems.values())

      // ── Empty-goal detection (goal-journey backstop, plan Part B) ──────────
      // A goal saved with NO actionable children is a dead-end. Rather than
      // resurrect the old "one fake hook task" pattern, we guide the user to a
      // real, approvable goal journey. Detect goals in the saved tree that have
      // zero children, and map each to the id we just created for it.
      const collectGoalNodes = (nodes: any[], out: any[] = []): any[] => {
        for (const n of nodes || []) {
          if ((n?.type || '') === 'goal') out.push(n)
          const kids = [
            ...(n?.goals || []), ...(n?.aspirations || []), ...(n?.tasks || []),
            ...(n?.skills || []), ...(n?.habits || []), ...(n?.action_items || []),
            ...(n?.roadblocks || []),
            ...(n?.children ? Object.values(n.children).flat() : []),
          ]
          if (kids.length) collectGoalNodes(kids, out)
        }
        return out
      }
      const goalNodeIsEmpty = (g: any): boolean => {
        const arrs = ['tasks', 'skills', 'habits', 'action_items', 'roadblocks', 'goals', 'aspirations']
        const direct = arrs.reduce((s, k) => s + (Array.isArray(g?.[k]) ? g[k].length : 0), 0)
        const obj = g?.children ? (Object.values(g.children).flat() as any[]).length : 0
        return direct + obj === 0
      }
      const savedGoalIdByTitle = new Map<string, string>()
      for (const it of deduplicatedItems) {
        if ((it.type || '') === 'goal' && it.id) {
          savedGoalIdByTitle.set((it.title || '').trim().toLowerCase(), String(it.id))
        }
      }
      // Grounding context for empty-goal journeys: the user's own words in this
      // conversation. Without it, an empty goal ("Stay healthy") reaches the
      // JourneyEngine with a bare title and gets a generic path. We stamp this
      // onto the goal's description so the breakdown reflects what was actually
      // said (house unclean, no shower in days, feeling stuck).
      // ChatMessage uses `type` ('user' | 'horton'), NOT `role`. (This was the
      // bug that left the context empty and stopped every enrichment PUT firing.)
      const emptyGoalContext = messages
        .filter((m) => m.type === 'user')
        .map((m) => (m.content || '').trim())
        .filter(Boolean)
        .join('\n')
        .slice(0, 1500)

      const emptyGoalTargets: Array<{ id: string; title: string; needsContext: boolean }> = []
      for (const g of collectGoalNodes(itemsHierarchy)) {
        if (v3ItemStates.get(`goal:${g.title}`)?.status === 'rejected') continue
        if (!goalNodeIsEmpty(g)) continue
        const id = savedGoalIdByTitle.get((g.title || '').trim().toLowerCase())
        if (id && !emptyGoalTargets.some((e) => e.id === id)) {
          const desc = (g.description || '').trim()
          const needsContext = desc.length <= (g.title || '').trim().length
          emptyGoalTargets.push({ id, title: g.title, needsContext })
        }
      }
      console.log(`🎯 Empty goals needing a path: ${emptyGoalTargets.length}`, emptyGoalTargets)

      // Retain the conversation context on EVERY saved item (goal/task/habit/skill/…)
      // whose own description is thin — so its edit page AND horton-motivator have
      // the context of what the user actually shared, not just a bare title.
      const ENRICH_TYPE_ENDPOINT: Record<string, string> = {
        dream: 'dreams', aspiration: 'aspirations', goal: 'goals', task: 'tasks',
        action_item: 'action-items', habit: 'habits', skill: 'skills', roadblock: 'roadblocks',
      }
      const descByKey = new Map<string, string>()
      for (const s of (itemsSummary || [])) {
        const k = `${(s.type || '').toLowerCase()}:${(s.title || '').trim().toLowerCase()}`
        const d = ((s.description ?? (s as any).fields?.description) || '').toString().trim()
        descByKey.set(k, d)
      }
      const itemsToEnrich: Array<{ id: string; endpoint: string }> = []
      for (const it of deduplicatedItems) {
        if (!it.id || it.isExisting) continue
        const type = (it.type || '').toLowerCase()
        const endpoint = ENRICH_TYPE_ENDPOINT[type]
        if (!endpoint) continue
        const title = (it.title || '').trim()
        const desc = descByKey.get(`${type}:${title.toLowerCase()}`) || ''
        if (desc.length <= title.length) {
          itemsToEnrich.push({ id: String(it.id), endpoint })
        }
      }

      // A newly-saved habit/task may deserve today's attention — nudge Today's
      // Focus to refresh on next visit so the user doesn't have to recompute.
      if (deduplicatedItems.some((i) => i.type === 'habit' || i.type === 'task' || i.type === 'action_item')) {
        sessionStorage.setItem('horton_refresh_todays_focus', 'true')
      }

      // Record the durable rationing<->hierarchy link so the parking lot
      // can later activate parked items (flip status) without title
      // guessing. Non-blocking; matches created items back to their
      // intake row by `${type}:${title}`.
      if (intakeIdByKey.size > 0) {
        try {
          const links: RationingHierarchyLink[] = []
          for (const item of deduplicatedItems) {
            if (!item.id || item.isExisting) continue
            const intakeId = intakeIdByKey.get(`${(item.type || '').toLowerCase()}:${item.title}`)
            if (intakeId != null) {
              links.push({
                intake_item_id: intakeId,
                hierarchy_item_id: item.id,
                hierarchy_item_type: item.type,
              })
            }
          }
          if (links.length > 0) await linkHierarchyItems(links)
        } catch (e) {
          console.warn('[rationing] link-back skipped (non-blocking):', e)
        }
      }

      // Recalculate stats from deduplicated items
      const actualNewlyCreated = deduplicatedItems.filter(item => !item.isExisting).length
      const actualReusedExisting = deduplicatedItems.filter(item => item.isExisting).length
      
      console.log(`✅ Commit complete:`)
      console.log(`   📊 Total items (raw): ${createdItems.length}`)
      console.log(`   📊 Total items (deduplicated): ${deduplicatedItems.length}`)
      console.log(`   🆕 Newly created: ${actualNewlyCreated}`)
      console.log(`   ♻️ Reused existing: ${actualReusedExisting}`)
      console.log(`   ❌ Errors: ${errors.length}`)
      
      // Use deduplicated items for display
      const displayItems = deduplicatedItems
      
      if (errors.length > 0) {
        modal.alert(
          'Partial Success',
          <div className="space-y-3">
            <p>{actualNewlyCreated} items created, {actualReusedExisting} already existed.</p>
            <div className="bg-bad/10 p-3 rounded-lg">
              <div className="font-medium text-bad mb-2">Errors:</div>
              <ul className="text-xs text-bad space-y-1">
                {errors.slice(0, 5).map((err, idx) => (
                  <li key={idx}>• {err}</li>
                ))}
                {errors.length > 5 && <li>...and {errors.length - 5} more</li>}
              </ul>
            </div>
          </div>,
          'warning'
        )
      }
      
      // Check for schedulable items — tasks, habits, skills, roadblocks (NOT goals, dreams, aspirations)
      const SCHEDULABLE_TYPES = ['task', 'habit', 'skill', 'roadblock'] as const
      const schedulableItems = displayItems
        .filter(item => (SCHEDULABLE_TYPES as readonly string[]).includes(item.type) && !item.isExisting)
        .map(item => ({
          id: item.id,
          type: item.type as 'task' | 'habit' | 'skill' | 'roadblock',
          title: item.title,
          priority: (item.fields?.priority as 'low' | 'medium' | 'high' | 'critical') || 'medium',
          estimated_duration: item.fields?.duration_minutes || (item.type === 'skill' ? 45 : 30),
          is_recurring: item.fields?.is_recurring || item.type === 'habit',
          recurrence_pattern: item.fields?.recurrence_pattern,
          recurrence_rule: item.fields?.recurrence_rule,
        }))
      
      // Build detailed success message
      let successMessage = `✅ Saved to Your Life Map!\n\n`
      if (actualNewlyCreated > 0) {
        successMessage += `🆕 ${actualNewlyCreated} new item(s) added\n`
      }
      if (actualReusedExisting > 0) {
        const reusedNames = deduplicatedItems
          .filter(item => item.isExisting)
          .map(item => `"${item.title}"`)
          .slice(0, 3)
        const reuseDetail = reusedNames.length > 0 ? ` — ${reusedNames.join(', ')}` : ''
        successMessage += `♻️ ${actualReusedExisting} item(s) already existed (linked, not duplicated)${reuseDetail}\n`
      }
      
      if (schedulableItems.length > 0) {
        // Count items by type for a clean summary
        const typeCounts: Record<string, number> = {}
        for (const item of displayItems) {
          typeCounts[item.type] = (typeCounts[item.type] || 0) + 1
        }
        
        // Build a friendly summary like "1 dream, 2 goals, 5 tasks"
        const typeOrder = ['dream', 'goal', 'skill', 'habit', 'task', 'action_item', 'roadblock']
        const summaryParts = typeOrder
          .filter(type => typeCounts[type])
          .map(type => {
            const count = typeCounts[type]
            const label = type === 'action_item' ? 'action item' : type
            return `${count} ${label}${count > 1 ? 's' : ''}`
          })
        
        // Find the top-level item (dream or highest in hierarchy)
        const topLevelItem = displayItems.find(item => item.type === 'dream') ||
                           displayItems.find(item => item.type === 'aspiration') ||
                           displayItems.find(item => item.type === 'goal') ||
                           displayItems[0]
        
        const shouldSchedule = await modal.confirm(
          'Plan Saved! ✨',
          <div className="space-y-4">
            {/* Simple success message */}
            <div className="text-secondary">
              {topLevelItem && (
                <p className="font-medium text-primary mb-2">
                  "{topLevelItem.title}"
                </p>
              )}
              <p className="text-sm">
                Saved to your Life Map: {summaryParts.join(', ')}
              </p>
            </div>

            {/* Teaching, saved-not-lost summary from the rationing gate */}
            {rationingSummaryCopy && (
              <p className="text-sm text-secondary bg-brand-subtle/50 p-3 rounded-lg">
                {rationingSummaryCopy}
              </p>
            )}

            {/* Only show skipped if user explicitly rejected items */}
            {v3RejectedItems.length > 0 && (
              <p className="text-xs text-muted">
                {v3RejectedItems.length} item{v3RejectedItems.length > 1 ? 's' : ''} skipped (as you requested)
              </p>
            )}
            
            {/* Scheduling prompt */}
            <div className="bg-brand-subtle p-4 rounded-lg border border-brand-subtle">
              <div className="font-medium text-brand mb-1">
                📅 Ready to schedule?
              </div>
              <p className="text-sm text-brand">
                {schedulableItems.length} item{schedulableItems.length > 1 ? 's' : ''} can be added to your calendar
              </p>
              {schedulableItems.some(i => i.type !== 'task') && (
                <p className="text-xs text-brand/70 mt-1">
                  {[...new Set(schedulableItems.map(i => i.type))].join(', ')}
                </p>
              )}
            </div>
          </div>,
          { confirmText: 'Schedule Now', cancelText: 'Maybe Later', size: 'md' }
        )
        if (shouldSchedule) {
          // User chose to schedule now
          setSchedulingStatus('completed')
          setPendingSchedulableItems([])
          navigate('/review-schedule', { 
            state: { 
              items: schedulableItems,
              fromChat: true,
              totalCreated: actualNewlyCreated,
              totalReused: actualReusedExisting
            } 
          })
        } else {
          // User chose "Maybe Later" - save the schedulable items for later
          setSchedulingStatus('skipped')
          setPendingSchedulableItems(schedulableItems.map(item => ({
            id: item.id,
            type: item.type,
            title: item.title,
            priority: item.priority,
            estimated_duration: item.estimated_duration,
            is_recurring: item.is_recurring,
            recurrence_pattern: item.recurrence_pattern,
            recurrence_rule: item.recurrence_rule,
          })))
        }
      } else if (displayItems.length > 0 && errors.length === 0) {
        // Count items by type for a clean summary
        const typeCounts: Record<string, number> = {}
        for (const item of displayItems) {
          typeCounts[item.type] = (typeCounts[item.type] || 0) + 1
        }
        
        // Build a friendly summary like "1 dream, 2 goals, 5 tasks"
        const typeOrder = ['dream', 'goal', 'skill', 'habit', 'task', 'action_item', 'roadblock']
        const summaryParts = typeOrder
          .filter(type => typeCounts[type])
          .map(type => {
            const count = typeCounts[type]
            const label = type === 'action_item' ? 'action item' : type
            return `${count} ${label}${count > 1 ? 's' : ''}`
          })
        
        // Find the top-level item (dream or highest in hierarchy)
        const topLevelItem = displayItems.find(item => item.type === 'dream') ||
                           displayItems.find(item => item.type === 'aspiration') ||
                           displayItems.find(item => item.type === 'goal') ||
                           displayItems[0]
        
        if (actualNewlyCreated === 0 && actualReusedExisting > 0) {
          // All items already existed
          modal.alert(
            'Already in Your Hierarchy',
            <div className="space-y-3">
              <p className="text-secondary">
                These items are already in your Life Map — no duplicates added.
              </p>
              {topLevelItem && (
                <p className="text-sm text-muted">
                  "{topLevelItem.title}" and related items
                </p>
              )}
            </div>,
            'info',
            'md'
          )
        } else {
          // Normal success
          modal.alert(
            'Plan Saved! ✨',
            <div className="space-y-3">
              {/* Simple success message */}
              <div className="text-secondary">
                {topLevelItem && (
                  <p className="font-medium text-primary mb-2">
                    "{topLevelItem.title}"
                  </p>
                )}
                <p className="text-sm">
                  Saved to your Life Map: {summaryParts.join(', ')}
                </p>
              </div>

              {/* Teaching, saved-not-lost summary from the rationing gate */}
              {rationingSummaryCopy && (
                <p className="text-sm text-secondary bg-brand-subtle/50 p-3 rounded-lg">
                  {rationingSummaryCopy}
                </p>
              )}

              {/* Only show skipped if user explicitly rejected items */}
              {v3RejectedItems.length > 0 && (
                <p className="text-xs text-muted">
                  {v3RejectedItems.length} item{v3RejectedItems.length > 1 ? 's' : ''} skipped (as you requested)
                </p>
              )}
            </div>,
            'success',
            'md'
          )
        }
      }
      
      // Mark the chat as committed (tombstone/EOF)
      const now = new Date().toISOString()
      setCommittedAt(now)
      setCommittedItems(displayItems.map(item => ({
        id: item.id,
        type: item.type,
        title: item.title
      })))
      setMissingItems([]) // No missing items right after commit
      
      console.log(`🏁 Chat marked as committed at ${now} with ${displayItems.length} items`)
      
      // Clear the item states after successful commit
      setV3ItemStates(new Map())
      setV3RejectedItems([])
      
      // Save immediately to persist the commit status
      setTimeout(() => {
        autoSaveChat()
      }, 100)

      // Calibration breadcrumb (fire-and-forget, never blocks or throws).
      void hortonGuidanceService.sendProfileEvents([{ event_kind: 'proposal_saved', chat_id: chatId ?? undefined }])

      // ── Connection surfacing ("doing this also helps X") ──────────────────
      // Ask (fire-and-forget) which held/saved thoughts the just-saved items
      // move forward; yanamas mentions any real ones in the chat.
      void surfaceConnectionsAfterSave(
        displayItems
          .filter((it) => it.title && !it.isExisting)
          .slice(0, 10)
          .map((it) => ({ type: it.type || 'goal', title: it.title })),
      )

      // ── Goal-journey backstop redirect (plan Part B) ───────────────────────
      // A single empty goal on a turn with nothing to schedule → take the user
      // straight to that goal with its journey breakdown open so they can
      // approve a real path (the goal is already saved, so skipping loses
      // nothing). Multiple empty goals surface durably in yanamas' Inbox
      // "Needs a path" (backend needs-journey feed), so we just point there.
      // Ground each empty goal's journey: stamp the conversation context onto its
      // description BEFORE the breakdown runs, so the JourneyEngine reflects what
      // the user actually said rather than a generic path. Awaited so the
      // single-goal redirect below opens an already-grounded breakdown.
      if (emptyGoalContext && itemsToEnrich.length > 0) {
        await Promise.all(
          itemsToEnrich.map((it) =>
            fetch(`/api/hierarchy/${it.endpoint}/${it.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ description: `What I shared with ${BRAND.assistant}:\n${emptyGoalContext}` }),
            }).catch(() => { /* non-fatal: item still saved, just less context */ })
          )
        )
      }

      if (emptyGoalTargets.length === 1 && schedulableItems.length === 0) {
        navigate(`/goals/${emptyGoalTargets[0].id}?action=breakdown&from=chat`)
      } else if (emptyGoalTargets.length > 1) {
        setToastMessage(
          `${emptyGoalTargets.length} goals need a path — find them in ${BRAND.assistant}' Inbox`
        )
      }

    } catch (error: any) {
      console.error('❌ Error saving items:', error)
      modal.alert('Failed to Save Items', error.message, 'error')
    } finally {
      setIsCommittingItems(false)
    }
  }
  
  // Handle rejecting a hierarchy item (legacy)
  const handleRejectItem = (item: HierarchyItem, hierarchyType: string) => {
    console.log('❌ Rejecting item:', item, 'Type:', hierarchyType)
    
    // Remove item from life map
    setLifeMapData((prev: any) => {
      if (!prev) return prev
      
      const updated = { ...prev }
      Object.keys(updated).forEach(key => {
        if (Array.isArray(updated[key])) {
          updated[key] = updated[key].filter((i: any) => {
            return !(i.id === item.id || (i.details?.clean_summary === item.details?.clean_summary && i.details?.action === item.details?.action))
          })
        }
      })
      return updated
    })
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-surface" role="main" aria-label={`${BRAND.assistant} chat`}>
      {/* Header */}
      <div className="bg-surface border-b border-soft px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between flex-shrink-0 gap-2">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-shrink">
          <img src={branding.icon} alt={`${BRAND.assistant}`} className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0 rounded-lg" />
          <div className="min-w-0">
            <h1 className="text-sm sm:text-lg font-semibold text-primary truncate">
              {BRAND.assistant}
            </h1>
            <p className="text-app-micro sm:text-xs text-muted hidden sm:block">
              {useV3Engine 
                ? 'Transform dreams into goals'
                : "Share your thoughts"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {/* Persona Selector - visible on all screen sizes */}
          <select
            value={v3Persona}
            onChange={(e) => handleV3PersonaChange(e.target.value)}
            aria-label={`Choose ${BRAND.assistant}'s coaching style`}
            className="text-xs border border-strong rounded-lg px-2 py-1.5 bg-surface text-secondary min-h-touch"
          >
            <option value="supportive_best_friend">💙 Friend</option>
            <option value="calm_older_sibling">🤝 Sibling</option>
            <option value="quiet_mentor">🧘 Mentor</option>
          </select>
          {isLifecoach && (
            <Button
              onClick={toggleFace}
              variant={faceOn ? 'default' : 'outline'}
              size="sm"
              aria-pressed={faceOn}
              aria-label={faceOn ? 'Hide the face and show the conversation' : 'Show the face'}
              title={faceOn ? 'Face is on — turn it off to see only the conversation' : 'Show the face'}
              className="min-h-touch min-w-touch px-2 sm:px-3"
              data-testid="face-toggle"
            >
              <ScanFace className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Face</span>
            </Button>
          )}

          {getContextualWelcome().showBackButton && (
            <Button
              onClick={() => navigate(getContextualWelcome().backUrl || DEFAULT_APP_HOME)}
              variant="outline"
              size="sm"
              aria-label="Go back"
              className="min-h-touch min-w-touch px-2 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Back</span>
            </Button>
          )}
          {/* Lifecoach opens the gisted chats TABLE; compass keeps its own
              history sidebar (different backing store). */}
          <Button
            onClick={() => (isLifecoach
              ? setShowLifecoachChats(!showLifecoachChats)
              : setShowChatHistory(!showChatHistory))}
            variant="outline"
            size="sm"
            aria-label={isLifecoach ? 'Show chats' : (showChatHistory ? 'Hide chat history' : 'Show chat history')}
            className="min-h-touch min-w-touch px-2 sm:px-3"
          >
            <History className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">{isLifecoach ? 'Chats' : 'History'}</span>
          </Button>
          {messages.length > 0 && (
            <Button
              onClick={newChat}
              variant="outline"
              size="sm"
              aria-label="Start new chat"
              className="min-h-touch min-w-touch px-2 sm:px-3"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">New</span>
            </Button>
          )}
        </div>
      </div>

      {/* Context Window Warning Banner */}
      {contextStatus && contextStatus.status !== 'ok' && (
        <div className={`px-6 py-3 border-b flex items-start gap-3 ${
          contextStatus.status === 'critical' 
            ? 'bg-bad/10 border-bad/30' 
            : contextStatus.status === 'red'
            ? 'bg-warn/10 border-warn/30'
            : 'bg-warn/10 border-warn/30'
        }`}>
          {contextStatus.status === 'critical' ? (
            <AlertTriangle className="w-5 h-5 text-bad flex-shrink-0 mt-0.5" />
          ) : contextStatus.status === 'red' ? (
            <AlertCircle className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
          ) : (
            <Info className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className={`text-sm font-medium ${
              contextStatus.status === 'critical' 
                ? 'text-bad' 
                : contextStatus.status === 'red'
                ? 'text-warn'
                : 'text-warn'
            }`}>
              {contextStatus.user_message}
            </div>
            <div className="mt-1 text-xs text-secondary">
              Context usage: {contextStatus.percentage_used}%
            </div>
          </div>
          {contextStatus.status === 'critical' && !isLifecoach && (
            <Button
              onClick={newChat}
              size="sm"
              className="bg-bad hover:bg-bad text-on-brand flex-shrink-0"
            >
              Start New Chat
            </Button>
          )}
        </div>
      )}

      {/* Lifecoach multi-chat table: engine-gisted title + one-liner +
          hover paragraph summary, sortable + paginated. */}
      {isLifecoach && (
        <LifecoachChatsPanel
          open={showLifecoachChats}
          activeChatId={chatId}
          onClose={() => setShowLifecoachChats(false)}
          onSelect={(id) => {
            setShowLifecoachChats(false)
            if (id === chatId) return
            rememberLifecoachChat(id)
            void loadChat(id)
          }}
          onNewChat={() => {
            setShowLifecoachChats(false)
            void newChat()
          }}
        />
      )}

      {/* Chat History Sidebar */}
      {showChatHistory && (
        <div className="fixed right-0 top-16 w-full sm:w-80 h-[calc(100vh-4rem)] bg-surface border-l border-soft shadow-2xl z-[100] overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-primary">Chat History</h3>
              <button
                onClick={() => setShowChatHistory(false)}
                className="p-1 hover:bg-surface-alt rounded transition-colors"
                aria-label="Close chat history"
              >
                <X className="w-4 h-4 text-muted" />
              </button>
            </div>
            {chatSessions.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-muted mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted">No saved chats yet</p>
                <p className="text-xs text-muted mt-2">Start a conversation and it will be saved here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {chatSessions.map((chat: any) => (
                  <div
                    key={chat.chat_id}
                    className={`p-3 border rounded-lg hover:bg-surface-alt cursor-pointer transition-colors
                      ${chat.committed_at 
                        ? 'border-ok/30 bg-ok/5' 
                        : 'border-soft'}`}
                    onClick={() => loadChat(chat.chat_id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-primary flex-1">{chat.title}</span>
                      {chat.committed_at && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-ok/20 text-ok rounded-full flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Saved
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span>{chat.message_count} messages</span>
                      {chat.started_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Created: {new Date(chat.started_at).toLocaleString([], { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                      {chat.last_activity && chat.last_activity !== chat.started_at && (
                        <span className="text-brand">
                          Updated: {new Date(chat.last_activity).toLocaleString([], { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lifecoach mobile tabs: just Chat + Life Map (no items/held). */}
      {isLifecoach && (
        <div className="flex lg:hidden border-b border-soft bg-surface flex-shrink-0" role="tablist" aria-label="Chat sections">
          {([
            { id: 'chat' as ChatTab, label: 'Chat', icon: <MessageSquare className="w-4 h-4" /> },
            { id: 'lifemap' as ChatTab, label: 'Life Map', icon: <MapIcon className="w-4 h-4" /> },
          ]).map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 min-h-touch py-2.5 text-sm font-medium transition-colors
                ${activeTab === tab.id
                  ? 'text-brand border-b-2 border-brand'
                  : 'text-muted hover:text-secondary'}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Mobile Tab Bar — only shown when there are items to display */}
      {hasSplitPane && !isLifecoach && (
        <div className="flex lg:hidden border-b border-soft bg-surface flex-shrink-0" role="tablist" aria-label="Chat sections">
          {([
            { id: 'chat' as ChatTab, label: 'Chat', icon: <MessageSquare className="w-4 h-4" /> },
            { id: 'items' as ChatTab, label: 'Items', icon: <ListTodo className="w-4 h-4" /> },
            { id: 'held' as ChatTab, label: 'Held', icon: <Archive className="w-4 h-4" /> },
            { id: 'lifemap' as ChatTab, label: 'Life Map', icon: <MapIcon className="w-4 h-4" /> },
          ]).map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 min-h-touch py-2.5 text-sm font-medium transition-colors
                ${activeTab === tab.id
                  ? 'text-brand border-b-2 border-brand'
                  : 'text-muted hover:text-secondary'}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Main Content — tabbed on mobile, split pane on desktop */}
      <div className={`flex flex-col lg:flex-row flex-1 overflow-hidden min-h-0 ${showSplit ? '' : 'justify-center'}`}>
        {/* Chat Pane — full width on mobile (when chat tab active), 35% on desktop when split */}
        <div className={`
          ${showSplit ? 'w-full lg:w-[35%]' : 'w-full max-w-3xl mx-auto'}
          flex flex-col bg-default lg:border-r border-soft min-h-0
          ${showSplit && activeTab !== 'chat' ? 'hidden lg:flex' : ''}
        `}>
          {/* Live Thinking Section - Removed per user request, only showing summarized version per message */}
          
          {/* Messages — the left page of the pager when the face is on; the
              face is the right page. With the face off the pager is one
              plain page and this is the transcript exactly as it always was. */}
          <HortonFacePager
            page={facePage}
            onPage={setFacePage}
            face={showFace ? (
              <HortonFacePage
                face={faceId}
                state={faceState}
                showing={faceShown}
                latest={latestReply}
                speaking={speakingTurn !== null}
                onTap={() => setFacePage('chat')}
                onListen={(turnId) => void playReply(turnId)}
                onStop={stopSpeaking}
              />
            ) : null}
          >
          <div className="flex-1 overflow-y-auto space-y-4 min-h-0" aria-live="polite" aria-relevant="additions">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-start pt-4 sm:pt-12 px-3 sm:px-6 min-h-full text-center">
                <img src={branding.icon} alt={`${BRAND.assistant}`} className="w-12 h-12 sm:w-20 sm:h-20 mb-2 sm:mb-4" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary mb-1 sm:mb-2">
                  {getContextualWelcome().title}
                </h2>
                {getContextualWelcome().highlightedText ? (
                  <div className="mb-3 sm:mb-6 max-w-lg space-y-2 sm:space-y-4">
                    <div className="relative">
                      <div className="hidden sm:block absolute -inset-1 bg-brand-subtle rounded-2xl blur-sm opacity-50"></div>
                      <div className="relative bg-surface backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-soft shadow-sm">
                        <p className="text-secondary text-sm sm:text-lg leading-relaxed">
                          Hey! I understand you want to{' '}
                          <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg bg-brand-subtle text-brand font-semibold border border-brand-subtle">
                            <span className="text-sm sm:text-lg">{getContextualWelcome().emoji}</span>
                            <span>create a new {getContextualWelcome().highlightedText?.split('create a new ')[1]}</span>
                          </span>
                        </p>
                      </div>
                    </div>
                    <p className="text-secondary text-xs sm:text-base leading-relaxed px-2">
                      Share your thoughts and I'll help you organize it into your{' '}
                      <span className="text-brand font-semibold">
                        {getContextualWelcome().highlightedPage}
                      </span>
                      {' '}page.
                    </p>
                  </div>
                ) : (
                  <p className="text-secondary mb-3 sm:mb-6 max-w-md text-xs sm:text-base">
                    {getContextualWelcome().message}
                  </p>
                )}
                
                {/* One-time onboarding hint (G15) */}
                {showOnboardingHint && (
                  <div className="w-full max-w-lg mb-3 sm:mb-6 relative rounded-xl border border-brand-subtle bg-brand-subtle p-3 sm:p-4 text-left">
                    <button
                      onClick={dismissOnboarding}
                      className="absolute top-2 right-2 text-brand hover:text-brand-hover"
                      aria-label="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-start gap-2.5">
                      <Info className="h-5 w-5 text-brand flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-primary">
                          How {BRAND.assistant} works
                        </p>
                        <p className="text-xs sm:text-sm text-brand leading-relaxed">
                          Everything {BRAND.assistant} suggests is a <strong>proposal</strong> until you accept it. You'll see each idea as a card you can review, edit, or dismiss before it becomes part of your plan.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Help Scenarios - compact on mobile */}
                <div className="w-full max-w-4xl mt-2 sm:mt-8 mb-3 sm:mb-6">
                  <div className="text-center mb-2 sm:mb-6">
                    <h3 className="text-sm sm:text-lg font-semibold text-primary mb-0.5 sm:mb-2">
                      Quick Help 🚀
                    </h3>
                    <p className="hidden sm:block text-sm text-secondary">
                      Common situations where I can help you out! Pick one to get started quickly ✨
                    </p>
                  </div>

                  {/* Mobile: horizontal scroll chips */}
                  <div className="flex sm:hidden gap-2 overflow-x-auto pb-2 -mx-3 px-3 snap-x" role="list" aria-label="Quick help topics">
                    <button
                      onClick={() => setInput("I want to set a new goal but I'm not sure how to make it SMART and achievable.")}
                      className="flex items-center gap-1.5 min-h-touch px-4 py-2.5 border border-brand-subtle rounded-full text-sm font-medium text-brand whitespace-nowrap flex-shrink-0 snap-start active:bg-brand-subtle"
                    >
                      <Target className="h-4 w-4" /> Goal Setting
                    </button>
                    <button
                      onClick={() => setInput("I'm facing obstacles and challenges that are blocking my progress. Can you help me overcome them?")}
                      className="flex items-center gap-1.5 min-h-touch px-4 py-2.5 border border-brand-subtle rounded-full text-sm font-medium text-brand whitespace-nowrap flex-shrink-0 snap-start active:bg-brand-subtle"
                    >
                      <Heart className="h-4 w-4" /> Obstacles
                    </button>
                    <button
                      onClick={() => setInput("I'm feeling unmotivated and need some encouragement to keep going.")}
                      className="flex items-center gap-1.5 min-h-touch px-4 py-2.5 border border-brand-subtle rounded-full text-sm font-medium text-brand whitespace-nowrap flex-shrink-0 snap-start active:bg-brand-subtle"
                    >
                      <Sparkles className="h-4 w-4" /> Motivation
                    </button>
                    <button
                      onClick={() => setInput("I want to learn a new skill but don't know where to start or how to structure my learning.")}
                      className="flex items-center gap-1.5 min-h-touch px-4 py-2.5 border border-brand-subtle rounded-full text-sm font-medium text-brand whitespace-nowrap flex-shrink-0 snap-start active:bg-brand-subtle"
                    >
                      <BookOpen className="h-4 w-4" /> Learning
                    </button>
                    <button
                      onClick={() => setInput("I'm struggling with time management and balancing my work and personal life.")}
                      className="flex items-center gap-1.5 min-h-touch px-4 py-2.5 border border-brand-subtle rounded-full text-sm font-medium text-brand whitespace-nowrap flex-shrink-0 snap-start active:bg-brand-subtle"
                    >
                      <Coffee className="h-4 w-4" /> Time Mgmt
                    </button>
                    <button
                      onClick={() => setInput("Hey! I just want to have a conversation and see what insights you can provide.")}
                      className="flex items-center gap-1.5 min-h-touch px-4 py-2.5 border border-brand-subtle rounded-full text-sm font-medium text-brand whitespace-nowrap flex-shrink-0 snap-start active:bg-brand-subtle"
                    >
                      <MessageSquare className="h-4 w-4" /> Just Chat
                    </button>
                  </div>

                  {/* Desktop: full card grid */}
                  <div className="hidden sm:grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <button
                      onClick={() => setInput("I want to set a new goal but I'm not sure how to make it SMART and achievable.")}
                      className="h-auto p-4 flex flex-col items-start border-2 border-brand-subtle hover:bg-brand-subtle rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center mb-2">
                        <Target className="h-5 w-5 mr-2 text-brand" />
                        <div className="text-app-body font-semibold text-primary">Goal Setting Help</div>
                      </div>
                      <div className="text-app-body text-brand">Get guidance on setting SMART goals that actually work for you</div>
                    </button>
                    
                    <button
                      onClick={() => setInput("I'm facing obstacles and challenges that are blocking my progress. Can you help me overcome them?")}
                      className="h-auto p-4 flex flex-col items-start border-2 border-brand-subtle hover:bg-brand-subtle rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center mb-2">
                        <Heart className="h-5 w-5 mr-2 text-brand" />
                        <div className="text-app-body font-semibold text-primary">Overcoming Obstacles</div>
                      </div>
                      <div className="text-app-body text-brand">Help with roadblocks, challenges, and getting unstuck</div>
                    </button>
                    
                    <button
                      onClick={() => setInput("I'm feeling unmotivated and need some encouragement to keep going.")}
                      className="h-auto p-4 flex flex-col items-start border-2 border-brand-subtle hover:bg-brand-subtle rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center mb-2">
                        <Sparkles className="h-5 w-5 mr-2 text-brand" />
                        <div className="text-app-body font-semibold text-primary">Motivation Boost</div>
                      </div>
                      <div className="text-app-body text-brand">Get encouragement, motivation, and a fresh perspective</div>
                    </button>
                    
                    <button
                      onClick={() => setInput("I want to learn a new skill but don't know where to start or how to structure my learning.")}
                      className="h-auto p-4 flex flex-col items-start border-2 border-brand-subtle hover:bg-brand-subtle rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center mb-2">
                        <BookOpen className="h-5 w-5 mr-2 text-brand" />
                        <div className="text-app-body font-semibold text-primary">Learning Path</div>
                      </div>
                      <div className="text-app-body text-brand">Create a structured learning plan for any skill</div>
                    </button>
                    
                    <button
                      onClick={() => setInput("I'm struggling with time management and balancing my work and personal life.")}
                      className="h-auto p-4 flex flex-col items-start border-2 border-brand-subtle hover:bg-brand-subtle rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center mb-2">
                        <Coffee className="h-5 w-5 mr-2 text-brand" />
                        <div className="text-app-body font-semibold text-primary">Time Management</div>
                      </div>
                      <div className="text-app-body text-brand">Help with productivity, scheduling, and work-life balance</div>
                    </button>
                    
                    <button
                      onClick={() => setInput("Hey! I just want to have a conversation and see what insights you can provide.")}
                      className="h-auto p-4 flex flex-col items-start border-2 border-brand-subtle hover:bg-brand-subtle rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center mb-2">
                        <MessageSquare className="h-5 w-5 mr-2 text-brand" />
                        <div className="text-app-body font-semibold text-primary">Just Chat</div>
                      </div>
                      <div className="text-app-body text-brand">Have a friendly conversation about anything on your mind</div>
                    </button>
                  </div>
                </div>
                
                {getContextualWelcome().showBackButton && (
                  <Button
                    onClick={() => navigate(getContextualWelcome().backUrl || DEFAULT_APP_HOME)}
                    variant="outline"
                    className="mb-4"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {getContextualWelcome().backLabel}
                  </Button>
                )}
              </div>
            ) : (
              <div className="px-6 py-6 space-y-4">
                {messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`space-y-3 transition-all duration-200 rounded-lg p-2 -m-2 cursor-pointer ${
                      selectedMessageId === message.id 
                        ? 'bg-brand-subtle ring-2 ring-brand' 
                        : 'hover:bg-surface-alt'
                    }`}
                    onClick={() => {
                      // Select this message to show its data in the right pane
                      // Note: Just select the message, don't change v3ComposedResponse
                      // The accumulated items will remain visible
                      setSelectedMessageId(message.id)
                    }}
                    title={message.type === 'horton' && message.v3_composed_response 
                      ? 'Click to view suggestions and recommendations from this response' 
                      : 'Click to select this message'}
                  >
                    {/* Selection indicator intentionally removed — suggestions panel is simplified */}
                    
                    {/* Thinking Block (collapsible) */}
                    {message.type === 'horton' && (message.thinking_summary || (message.thinking && message.thinking.trim().length > 0)) && (
                      <details className="ml-4 mb-3 bg-brand-subtle border-2 border-brand-subtle rounded-lg overflow-hidden shadow-sm">
                        <summary className="cursor-pointer px-4 py-3 font-semibold text-sm text-brand-hover hover:bg-brand-subtle-border flex items-center gap-2 transition-colors">
                          <img src={branding.icon} alt={`${BRAND.assistant}`} className="w-5 h-5 rounded" />
                          <span className="flex-1">
                            How {BRAND.assistant} understood this
                          </span>
                          <span className="text-xs text-brand">Click to expand</span>
                        </summary>
                        <div className="px-4 py-4 text-sm text-primary bg-surface/80 border-t-2 border-brand-subtle leading-relaxed">
                          {message.thinking_summary ? (
                            <div className="text-primary">
                              <MarkdownMessage content={message.thinking_summary} />
                              {message.thinking && message.thinking.length > (message.thinking_summary?.length || 0) && (
                                <details className="mt-4 pt-4 border-t border-brand-subtle">
                                  <summary className="cursor-pointer text-xs font-semibold text-brand hover:text-brand-hover">
                                    Show more detail
                                  </summary>
                                  <div className="mt-2 text-xs text-secondary bg-surface-alt p-3 rounded border border-soft max-h-96 overflow-y-auto">
                                    <MarkdownMessage content={message.thinking} />
                                  </div>
                                </details>
                              )}
                            </div>
                          ) : message.thinking ? (
                            <div className="text-primary">
                              <MarkdownMessage content={message.thinking} />
                            </div>
                          ) : null}
                        </div>
                      </details>
                    )}

                    {/* Message Content */}
                    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] p-4 rounded-lg ${
                          message.type === 'user'
                            ? 'bg-surface-alt text-primary'
                            : (message as any).error
                              ? 'bg-bad/10 text-primary border border-bad/30'
                              : 'bg-brand-subtle text-primary border border-brand-subtle'
                        }`}
                      >
                        {(message as any).error && message.type === 'horton' ? (
                          <div className="space-y-2" role="alert">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-bad flex-shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <p className="text-sm font-medium text-bad">Something went wrong</p>
                                <p className="text-sm text-bad mt-1">
                                  {message.content?.replace(/⚠️\s*(Error|Note):?\s*/g, '') || 'An error occurred.'}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => { setInput(messages.filter(m => m.type === 'user').pop()?.content || ''); }}
                              className="flex items-center gap-1.5 min-h-touch px-4 py-2 text-sm font-medium text-bad bg-bad/10 rounded-lg hover:bg-bad/20 transition-colors"
                            >
                              <RefreshCw className="w-4 h-4" /> Retry
                            </button>
                          </div>
                        ) : message.type === 'horton' ? (
                          <MarkdownMessage content={message.content || '...'} />
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{message.content || '...'}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <p className="text-xs opacity-70">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                          {message.type === 'horton' && typeof message.engine_turn_id === 'number' && (
                            <button
                              type="button"
                              onClick={() => (speakingTurn === message.engine_turn_id
                                ? stopSpeaking()
                                : void playReply(message.engine_turn_id as number))}
                              className="text-xs opacity-70 hover:opacity-100 underline decoration-dotted"
                              aria-label={speakingTurn === message.engine_turn_id
                                ? 'Stop reading this reply' : 'Read this reply aloud'}
                            >
                              {speakingTurn === message.engine_turn_id ? '■ stop' : '▶ listen'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {message.type === 'horton' && message.id === agendaMsgId && agendaRoots && (
                      <div className="mt-2 rounded-lg border border-border bg-surface-alt px-4 py-3">
                        <div className="text-xs font-medium text-muted mb-2">
                          Everything you named — we'll take them one at a time
                        </div>
                        <ul className="space-y-1">
                          {agendaRoots.map((root) => {
                            const st = agendaStatusFor(root.title)
                            return (
                              <li key={root.title} className="flex items-center gap-2 text-sm">
                                <span aria-hidden>
                                  {st === 'committed' ? '✅' : st === 'discussed' ? '✔️' : st === 'current' ? '👉' : '○'}
                                </span>
                                <span
                                  className={
                                    st === 'committed' || st === 'discussed'
                                      ? 'line-through text-muted'
                                      : st === 'current'
                                        ? 'font-medium text-brand'
                                        : ''
                                  }
                                >
                                  {root.title}
                                </span>
                                {st === 'current' && (
                                  <span className="text-app-nano uppercase tracking-wide text-brand">now</span>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )}

                    {message.type === 'horton' &&
                      message.v3_debug &&
                      Object.keys(message.v3_debug).length > 0 && (
                        <div
                          className="ml-4 mt-1 flex justify-start"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs gap-1.5 h-8"
                            aria-label="Open pipeline debug for this turn"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPipelineDebugModal({
                                open: true,
                                payload: message.v3_debug ?? null,
                              })
                            }}
                          >
                            <Code2 className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            Debug
                          </Button>
                        </div>
                      )}

                    {/* Suggestion Versions Button (if available) */}
                    {message.type === 'horton' && message.suggestion_versions && message.suggestion_versions.length > 0 && (
                      <div className="ml-4 space-y-2">
                        <div className="text-xs font-semibold text-secondary mb-2">
                          Load Version:
                        </div>
                        {message.suggestion_versions.map((_version: any, idx: number) => (
                          <Button
                            key={idx}
                            onClick={() => loadSuggestionVersion(message.id, idx)}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                          >
                            <Sparkles className="w-3 h-3 mr-2" />
                            Version {idx + 1}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start" role="status" aria-live="polite" aria-label={`${BRAND.assistant} is thinking`}>
                    <div className="bg-brand-subtle border border-brand-subtle p-4 rounded-lg shadow-sm w-full max-w-[280px]">
                      {/* Phase Progress Indicator */}
                      {currentPhase ? (
                        <div className="space-y-3">
                          {/* Current Phase */}
                          <div className="flex items-center gap-3">
                            <span className="text-2xl animate-pulse">{currentPhase.icon}</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm text-primary">
                                  {currentPhase.name}
                                </span>
                                <span className="text-xs text-brand bg-brand-subtle px-2 py-0.5 rounded-full">
                                  {currentPhase.working ? 'working…' : `Phase ${currentPhase.phase + 1}/5`}
                                </span>
                              </div>
                              <p className="text-xs text-secondary mt-0.5">
                                {currentPhase.description}
                              </p>
                            </div>
                            <Loader2 className="w-4 h-4 animate-spin text-brand" />
                          </div>

                          {/* Phase Progress Bar — a solid fill while advancing, a
                              pulsing indeterminate bar while we wait on a slow reply. */}
                          <div className="w-full bg-soft rounded-full h-1.5 overflow-hidden">
                            {currentPhase.working ? (
                              <div className="bg-brand h-1.5 rounded-full w-full animate-pulse" />
                            ) : (
                              <div
                                className="bg-brand h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${((currentPhase.phase + 1) / 5) * 100}%` }}
                              />
                            )}
                          </div>
                          
                          {/* Completed Phases */}
                          {phaseHistory.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {phaseHistory.map((phase, idx) => (
                                <span 
                                  key={idx}
                                  className="text-xs px-2 py-0.5 bg-ok/20 text-ok rounded-full"
                                >
                                  ✓ {phase}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2" role="status" aria-live="polite">
                          <Loader2 className="w-4 h-4 animate-spin text-brand" aria-hidden="true" />
                          <span className="text-sm text-secondary">Processing...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
          </HortonFacePager>

          {/* LLM Cost Tracker — only visible with ?debug=true */}
          {searchParams.get('debug') === 'true' && (
            <CostTracker sessionCost={sessionCost} />
          )}

          {/* Input Area */}
          <div className="border-t-0 border-soft p-2 sm:p-4 flex-shrink-0" data-testid="chat-input-area">
            {/* Held-items banner (owner ask 2026-07-06): whenever this
                conversation has held thoughts, say so RIGHT HERE by the chat —
                the Held tab alone was too easy to miss. Always on while items
                are held; tapping it opens the Held tab. */}
            {heldCount > 0 && hasSplitPane && rightPaneTab !== 'held' && activeTab !== 'held' && (
              <button
                className="mb-2 w-full px-3 py-2 bg-brand-subtle border border-brand-subtle rounded-lg flex items-center justify-between gap-2 text-left hover:bg-brand-subtle/70 transition-colors"
                data-testid="held-banner"
                onClick={() => {
                  // Desktop (lg+): the Held pane is the RIGHT pane while the
                  // chat stays put — set activeTab back to 'chat' (it can be
                  // stale after a mobile→desktop resize) and point the right
                  // pane at Held. Mobile: switch the single visible pane ONLY
                  // (touching rightPaneTab here would suppress this banner
                  // forever once the user tabs back to Chat).
                  if (window.matchMedia('(min-width: 1024px)').matches) {
                    setActiveTab('chat')
                    setRightPaneTab('held')
                  } else {
                    setActiveTab('held')
                  }
                }}
              >
                <span className="text-xs text-brand truncate">
                  🧵 {BRAND.assistant} is holding {heldCount} {heldCount === 1 ? 'thought' : 'thoughts'} from this conversation
                  {heldThreads[0] ? (
                    <span className="text-brand/70"> — “{heldThreads[0].title}”{heldCount > 1 ? ` and ${heldCount - 1} more` : ''}</span>
                  ) : null}
                </span>
                <span className="text-xs font-medium text-brand shrink-0">See them →</span>
              </button>
            )}
            {withYouMode && (
              <WithYouChips
                disabled={isLoading}
                onPick={(text) => setInput(text)}
              />
            )}
            {clarifyingFocus && (
              <div className="mb-2 px-3 py-2 bg-soft border border-soft rounded-lg flex items-center justify-between gap-2">
                <span className="text-xs text-secondary truncate">
                  Shaping: <span className="font-medium text-primary">{clarifyingFocus.title}</span>
                  <span className="text-muted"> — tell me more, then add it</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <button
                    className="text-xs px-2 py-1 rounded bg-brand text-white hover:bg-brand transition-colors"
                    onClick={finalizeClarifying}
                  >
                    Add it now
                  </button>
                  <button
                    className="text-xs text-muted hover:text-secondary"
                    onClick={stopClarifying}
                  >
                    Done
                  </button>
                </span>
              </div>
            )}
            {refiningProposal && !clarifyingFocus && (
              <div className="mb-2 px-3 py-2 bg-soft border border-soft rounded-lg flex items-center justify-between gap-2">
                <span className="text-xs text-secondary truncate">
                  Refining: <span className="font-medium text-primary">{refiningProposal.title}</span>
                  <span className="text-muted"> — your next message adds detail to this item</span>
                </span>
                <button
                  className="text-xs text-muted hover:text-secondary shrink-0"
                  onClick={() => setRefiningProposal(null)}
                >
                  Done
                </button>
              </div>
            )}
            <div className="flex space-x-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={voice.isListening ? 'Listening…' : (withYouMode ? `${BRAND.assistant} is here. Just a sentence is enough…` : "Share what's on your mind...")}
                aria-label={`Message to ${BRAND.assistant}`}
                className={`min-h-touch sm:min-h-15 resize-none text-sm sm:text-base ${voice.isListening ? 'ring-2 ring-brand/40 border-brand' : ''}`}
                data-testid="chat-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    sendMessage()
                  }
                }}
              />
              {speechError && (
                <span className="absolute -top-6 right-0 text-app-micro text-muted"
                      role="status">
                  voice: {speechError}
                </span>
              )}
              {voice.isSupported && (
                <Button
                  onClick={voice.toggle}
                  size="sm"
                  variant={voice.isListening ? 'destructive' : 'outline'}
                  aria-label={voice.isListening ? 'Stop listening' : 'Voice input'}
                  title={voice.isListening ? 'Stop listening' : 'Voice input'}
                  className={`h-touch sm:h-15 px-3 sm:px-4 ${voice.isListening ? 'animate-pulse' : ''}`}
                >
                  {voice.isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
              )}
              {/* Voice OUT. Beside the mic because they are one mode: talk to
                  it, and it talks back. Per device — see `voiceOn`. */}
              <Button
                onClick={toggleVoice}
                size="sm"
                variant={voiceOn ? 'default' : 'outline'}
                aria-pressed={voiceOn}
                aria-label={voiceOn ? 'Stop reading replies aloud' : 'Read replies aloud'}
                title={voiceOn ? 'Replies are read aloud' : 'Read replies aloud'}
                className={`h-touch sm:h-15 px-3 sm:px-4 ${speakingTurn !== null ? 'animate-pulse' : ''}`}
              >
                {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              {voiceOn && voiceOptions.length > 1 && (
                <select
                  value={chosenVoice}
                  onChange={(e) => pickVoice(e.target.value)}
                  aria-label="Speaking voice"
                  title="Speaking voice"
                  className="h-touch sm:h-15 rounded-md border border-soft bg-surface px-2 text-xs text-secondary"
                >
                  {voiceOptions.map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label} ({v.language})
                    </option>
                  ))}
                </select>
              )}
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                size="sm"
                aria-label={isLoading ? 'Sending message' : 'Send message'}
                className="h-touch sm:h-15 px-3 sm:px-4"
                data-testid="chat-send-button"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Lifecoach Right Pane — the Consolidated Life Map, always beside chat. */}
        {isLifecoach && (
          <div className={`
            w-full lg:w-[65%] flex flex-col bg-default lg:border-l border-t lg:border-t-0 border-soft min-h-0 lg:h-auto overflow-hidden
            ${activeTab === 'chat' ? 'hidden lg:flex' : ''}
          `}>
            {lifecoachCost !== null && (
              <div className="px-3 py-1.5 border-b border-soft bg-surface flex-shrink-0 text-app-micro font-mono text-muted"
                   title="Everything compass has spent on you, including nightly consolidation. Persists across reloads; cleared by a reset.">
                Total compass cost: ${lifecoachCost.toFixed(6)}
              </div>
            )}
            <div className="flex-1 min-h-0">
              <LifecoachLifeMap refreshSignal={lifeMapRefresh} onCost={setLifecoachCost} />
            </div>
          </div>
        )}

        {/* Right Pane — hidden on mobile unless items/lifemap tab is active */}
        {hasSplitPane && !isLifecoach && (
          <div className={`
            w-full lg:w-[65%] flex flex-col bg-default lg:border-l border-t lg:border-t-0 border-soft min-h-0 lg:h-auto overflow-auto
            ${activeTab === 'chat' ? 'hidden lg:flex' : ''}
          `}>
            {/* Desktop tab bar for right pane */}
            <div className="hidden lg:flex border-b border-soft bg-surface flex-shrink-0">
              {([
                { id: 'items' as const, label: 'Items from this conversation', icon: <ListTodo className="w-4 h-4" /> },
                { id: 'held' as const, label: 'Held', icon: <Archive className="w-4 h-4" /> },
                { id: 'lifemap' as const, label: 'Consolidated Life Map', icon: <MapIcon className="w-4 h-4" /> },
              ]).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setRightPaneTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors
                    ${rightPaneTab === tab.id
                      ? 'text-brand border-b-2 border-brand'
                      : 'text-muted hover:text-secondary'}`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.id === 'held' && heldCount > 0 && (
                    <span className="ml-1 text-app-nano px-1.5 py-0.5 rounded-full bg-brand text-white">{heldCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* V3 Dream Hierarchy Display — Items tab */}
            {useV3Engine && (activeTab === 'items' || (activeTab === 'chat' && rightPaneTab === 'items')) ? (
              <>
                {/* V3 Header */}
                <div className="px-6 py-4 border-b border-soft bg-surface flex-shrink-0">
                  <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
                    <Star className="w-5 h-5 text-warn" />
                    Suggestions
                  </h2>
                  <p className="text-xs text-muted mt-1">
                    Items {BRAND.assistant} has picked up from your conversation
                  </p>
                </div>

                {/* Save Status Banner */}
                {committedAt && (
                  <div className={`px-4 py-3 border-b ${missingItems.length > 0 
                    ? 'bg-warn/10 border-warn/30' 
                    : 'bg-ok/10 border-ok/30'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`text-xl ${missingItems.length > 0 ? '⚠️' : '✅'}`}>
                        {missingItems.length > 0 ? '⚠️' : '✅'}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-primary">
                          {missingItems.length > 0 
                            ? 'Some saved items are missing' 
                            : 'Items saved to your Life Map'}
                        </div>
                        <div className="text-xs text-secondary mt-1">
                          Added on {new Date(committedAt).toLocaleString()}
                          {' • '}{committedItems.length} item(s)
                        </div>
                        
                        {/* Missing Items Accordion */}
                        {missingItems.length > 0 && (
                          <div className="mt-3 bg-warn/15 rounded-lg overflow-hidden">
                            {/* Accordion Header - Always Visible */}
                            <button
                              onClick={() => setMissingItemsExpanded(!missingItemsExpanded)}
                              className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-warn/20 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-warn">
                                  {missingItems.length} item(s) deleted from hierarchy
                                </span>
                              </div>
                              <ChevronDown 
                                className={`w-4 h-4 text-warn transition-transform duration-200 ${
                                  missingItemsExpanded ? 'rotate-180' : ''
                                }`} 
                              />
                            </button>
                            
                            {/* Accordion Content - Collapsible */}
                            <div className={`overflow-hidden transition-all duration-200 ${
                              missingItemsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="px-3 pb-3 border-t border-warn/30">
                                {/* Instructions */}
                                <div className="mt-2.5 text-xs text-warn bg-warn/10 p-2 rounded mb-3">
                                  <strong>What happened?</strong> These items were saved from this chat but later removed from your Life Map.
                                  <br /><strong>To fix:</strong> Click "Re-add" to restore them, or leave as-is if you intentionally removed them.
                                </div>
                                
                                {/* Missing Items List */}
                                <ul className="text-xs text-warn space-y-1 mb-3">
                                  {missingItems.slice(0, 5).map((item, idx) => (
                                    <li key={idx} className="flex items-center gap-2">
                                      <span className="opacity-60 capitalize">{item.type}:</span>
                                      <span className="font-medium">{item.title}</span>
                                    </li>
                                  ))}
                                  {missingItems.length > 5 && (
                                    <li className="opacity-70 italic">...and {missingItems.length - 5} more</li>
                                  )}
                                </ul>
                                
                                {/* Re-add Button */}
                                <button
                                  onClick={async () => {
                                    const confirmed = await modal.confirm(
                                      'Re-add Missing Items?',
                                      <div className="space-y-3">
                                        <p>Would you like to re-add the {missingItems.length} missing item(s) to your Life Map?</p>
                                        <p className="text-sm text-muted">This will look for matching items from this conversation and add them back.</p>
                                      </div>,
                                      { confirmText: 'Re-add Items', cancelText: 'Not Now' }
                                    )
                                    if (confirmed) {
                                      setCommittedAt(null)
                                      setCommittedItems([])
                                      setMissingItems([])
                                      setMissingItemsExpanded(false)
                                    }
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium bg-warn/20 text-warn rounded hover:bg-warn/30 transition-colors"
                                >
                                  Re-add Missing Items
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Pending Scheduling - show if user skipped scheduling */}
                        {schedulingStatus === 'skipped' && pendingSchedulableItems.length > 0 && (
                          <div className="mt-3 p-3 bg-brand-subtle rounded-lg">
                            <div className="flex items-center gap-2 text-sm font-medium text-brand mb-2">
                              <Calendar className="w-4 h-4" />
                              {pendingSchedulableItems.length} action item(s) ready to schedule
                            </div>
                            <p className="text-xs text-brand mb-3">
                              You chose to schedule these later. Would you like to schedule them now?
                            </p>
                            <button
                              onClick={() => {
                                setSchedulingStatus('completed')
                                navigate('/review-schedule', { 
                                  state: { 
                                    items: pendingSchedulableItems,
                                    fromChat: true,
                                    totalCreated: pendingSchedulableItems.length,
                                    totalReused: 0
                                  } 
                                })
                              }}
                              className="px-3 py-1.5 text-xs font-medium bg-brand text-on-brand rounded hover:bg-brand-hover transition-colors flex items-center gap-1.5"
                            >
                              <Calendar className="w-3 h-3" />
                              Schedule Now
                            </button>
                          </div>
                        )}
                        
                        {/* Scheduling completed indicator */}
                        {schedulingStatus === 'completed' && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-ok">
                            <Check className="w-3 h-3" />
                            Items have been scheduled
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Holding content moved to the dedicated Held tab (owner ask
                    2026-07-05): the proposals editor gets the whole pane. */}

                {/* V3 Content */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                  <DreamHierarchyDisplay
                    composedResponse={v3ComposedResponse || undefined}
                    itemStates={v3ItemStates}
                    onItemStateChange={handleItemStateChange}
                    rejectedItems={v3RejectedItems}
                    onRejectedItemsChange={handleRejectedItemsChange}
                    onCommitItems={handleCommitItems}
                    isCommitting={isCommittingItems}
                    hasUnsavedEdits={hasUnsavedEdits}
                    isCommitted={!!committedAt}
                    committedAt={committedAt}
                    onRefineItem={(it) => {
                      setRefiningProposal(it)
                      setActiveTab('chat')
                      inputRef.current?.focus()
                    }}
                  />
                </div>
              </>
            ) : null}

            {/* Held tab — EVERYTHING the assistant is holding for this conversation
                in one place (owner ask 2026-07-05: the two stacked holding
                sections cluttered the items pane): held threads with their
                needs-more-info always visible, top-level proposals to refine,
                and noticed/captured people & places. */}
            {useV3Engine && (activeTab === 'held' || (activeTab === 'chat' && rightPaneTab === 'held')) && (
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="px-6 py-4 border-b border-soft bg-surface">
                  <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
                    <Archive className="w-5 h-5 text-warn" /> Held for you
                  </h2>
                  <p className="text-xs text-muted mt-1">
                    Things {BRAND.assistant} is holding from this conversation — nothing is lost, nothing is a commitment yet.
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  {/* 1. Held threads — details always visible (needs-more-info). */}
                  {heldThreads.length > 0 && (
                    <div className="rounded-lg border border-soft bg-surface p-3 space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wide text-muted">
                        Held thoughts
                      </div>
                      {heldThreads.map(t => {
                        const typeLabel = t.possible_lifemap_type
                          ? (THREAD_TYPE_LABEL[t.possible_lifemap_type] || `a ${t.possible_lifemap_type}`)
                          : null
                        return (
                          <div key={t.id} className="rounded border border-soft bg-surface-alt">
                            <div className="flex items-center justify-between gap-2 text-sm px-2 py-1.5">
                              <span className="truncate">🧵 {t.title}</span>
                              <span className="flex gap-1 shrink-0">
                                <button className="text-xs px-2 py-0.5 rounded bg-brand text-white" onClick={() => makeThreadReal(t)}>Make it real</button>
                                <button className="text-xs px-2 py-0.5 rounded border border-soft" onClick={() => dismissThread(t.id)}>Dismiss</button>
                              </span>
                            </div>
                            <div className="px-3 pb-2 pt-1 text-xs space-y-1 border-t border-soft">
                              {t.what_horton_heard && <p className="text-secondary">{t.what_horton_heard}</p>}
                              {typeLabel && <p className="text-muted">Could become {typeLabel}.</p>}
                              {t.missing_to_make_actionable && t.missing_to_make_actionable.length > 0 && (
                                <p className="text-muted">
                                  To make it real, {BRAND.assistant} would ask about: {t.missing_to_make_actionable.slice(0, 3).join(', ')}.
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {/* 2. Proposed in this conversation — pick one to refine. */}
                  {!committedAt && (() => {
                    const proposed = getTopLevelHolding().filter(h => h.source === 'proposed')
                    if (proposed.length === 0) return null
                    return (
                      <div className="rounded-lg border border-soft bg-surface p-3 space-y-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted">
                          Proposed in this conversation
                        </div>
                        <p className="text-xs text-muted">Pick one to add detail in chat before you save.</p>
                        <div className="flex flex-wrap gap-1.5">
                          {proposed.map(h => (
                            <button
                              key={h.key}
                              onClick={() => focusHolding(h)}
                              className="text-xs px-2 py-1 rounded-full border border-soft bg-surface hover:bg-surface-alt transition-colors flex items-center gap-1.5 max-w-[220px]"
                              title={h.title}
                            >
                              <span className="text-app-nano uppercase tracking-wide text-muted shrink-0">{h.type}</span>
                              <span className="text-primary truncate">{h.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                  {/* 3. People & places noticed / captured this conversation. */}
                  {(noticedCandidates.length > 0 || heldPeople.length > 0) && (
                    <div className="rounded-lg border border-soft bg-surface p-3 space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wide text-muted">
                        People &amp; places {BRAND.assistant} noticed
                      </div>
                      {noticedCandidates.map(c => (
                        <div key={c.id} className="flex items-center justify-between gap-2 text-sm">
                          <span className="truncate">{c.kind === 'person' ? '🧑' : '📍'} {c.name_or_alias}{c.relationship_as_user_stated ? ` — ${c.relationship_as_user_stated}` : ''}</span>
                          <span className="flex gap-1 shrink-0">
                            <button className="text-xs px-2 py-0.5 rounded bg-brand text-white" onClick={() => actOnNoticed(c.id, 'confirm')}>Add to world</button>
                            <button className="text-xs px-2 py-0.5 rounded border border-soft" onClick={() => actOnNoticed(c.id, 'dismiss')}>No thanks</button>
                          </span>
                        </div>
                      ))}
                      {/* Captured people/places waiting inside this turn's
                          proposal. Saving the proposal adds them — or add one
                          right now; the engine drops it from the held view
                          once it exists in My People (owner ask 2026-07-06:
                          these must be resolvable, never a stuck count). */}
                      {heldPeople.map((p, i) => (
                        <div key={`${p.patch_id}:${p.kind}:${p.name_or_alias}:${i}`} className="flex items-center justify-between gap-2 text-sm">
                          <span className="truncate">{p.kind === 'person' ? '🧑' : '📍'} {p.name_or_alias}{p.relationship_as_user_stated ? ` — ${p.relationship_as_user_stated}` : ''}</span>
                          <button
                            className="text-xs px-2 py-0.5 rounded bg-brand text-white shrink-0"
                            title="Create this in My People/Places now (saving the proposal would also add it)"
                            onClick={() => addHeldPersonNow(p)}
                          >
                            Add now
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {heldCount === 0 && getTopLevelHolding().length === 0 && (
                    <p className="text-sm text-muted">
                      Nothing held right now — when a conversation carries something meaningful but unshaped, it will wait here.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Life Map tab — full life graph with current-conversation highlights */}
            {(activeTab === 'lifemap' || (activeTab === 'chat' && rightPaneTab === 'lifemap')) && useV3Engine && (
              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                <LifeMapView
                  composedResponse={v3ComposedResponse || undefined}
                />
              </div>
            )}

            {/* Legacy (non-V3) */}
            {!useV3Engine && (
              <>
                <div className="px-6 py-4 border-b border-soft bg-surface flex-shrink-0">
                  <h2 className="text-lg font-semibold text-primary mb-2">Conversation Items</h2>
                  <div className="flex gap-6 text-xs text-secondary">
                    <div className="flex items-center gap-2">
                      <span>Total Items:</span>
                      <strong className="text-primary">{lifeMapData?.total_items || 0}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Last Updated:</span>
                      <strong className="text-primary">
                        {lifeMapData?.last_synthesis
                          ? new Date(lifeMapData.last_synthesis).toLocaleTimeString()
                          : 'Never'}
                      </strong>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 min-h-0">
                  {!lifeMapData || !lifeMapData.hierarchy_items || Object.keys(lifeMapData.hierarchy_items).length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-muted">
                      <div className="text-5xl mb-4 opacity-50">🌱</div>
                      <p className="text-sm">No items yet.<br />Start a conversation to see hierarchical items appear here!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(lifeMapData.hierarchy_items)
                        .sort(([typeA], [typeB]) => {
                          const orderA = hierarchyMetadata[typeA]?.order || 999
                          const orderB = hierarchyMetadata[typeB]?.order || 999
                          return orderA - orderB
                        })
                        .map(([type, items]: [string, any]) => {
                          if (!Array.isArray(items) || items.length === 0) return null
                          const metadata = hierarchyMetadata[type] || { emoji: '📌', name: type.replace(/_/g, ' '), order: 999 }
                          const isExpanded = expandedSections.has(type)
                          return (
                            <div key={type} className="bg-surface border border-soft rounded-lg overflow-hidden shadow-sm">
                              <button
                                onClick={() => toggleSection(type)}
                                className="w-full px-5 py-4 flex items-center justify-between hover:bg-surface-alt transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{metadata.emoji}</span>
                                  <span className="font-semibold text-sm text-primary">{metadata.name}</span>
                                  <span className="px-2 py-0.5 bg-brand-subtle text-brand rounded-full text-xs font-semibold">
                                    {items.length}
                                  </span>
                                </div>
                                {isExpanded
                                  ? <ChevronDown className="w-4 h-4 text-muted" />
                                  : <ChevronRight className="w-4 h-4 text-muted" />}
                              </button>
                              {isExpanded && (
                                <div className="border-t border-soft p-4 space-y-3">
                                  {items.map((item: any, idx: number) => (
                                    <HierarchyItemCard
                                      key={item.id || idx}
                                      item={item}
                                      hierarchyType={type}
                                      onAccept={(item: any) => handleAcceptItem(item, type)}
                                      onDecline={(item: any) => handleRejectItem(item, type)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Similar Items Dialog */}
      <SimilarItemsDialog
        isOpen={showSimilarDialog}
        onClose={handleSimilarDialogCancel}
        onProceed={handleSimilarDialogProceed}
        similarItems={similarItems}
        newItemTitle={pendingItem?.item?.details?.clean_summary || pendingItem?.item?.details?.action || ''}
        hierarchyType={pendingItem?.hierarchyType || ''}
      />
      
      {/* Personality Selector */}
      <PersonalitySelector
        isOpen={showPersonalitySelector}
        onClose={() => setShowPersonalitySelector(false)}
        onSelect={handlePersonalitySelect}
        currentEmotionalApproach={userEmotionalApproach}
        currentConversationStyle={userConversationStyle}
      />

      <Dialog
        open={pipelineDebugModal.open}
        onOpenChange={(open) => {
          if (!open) setPipelineDebugModal({ open: false, payload: null })
        }}
      >
        <DialogContent className="sm:max-w-4xl w-full flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle>Pipeline debug</DialogTitle>
            <p className="text-sm text-secondary mt-1">
              Raw JSON for this turn (including <code className="text-xs bg-surface-alt px-1 rounded">llm_calls</code> when
              the external chat engine returns it).
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 flex flex-col gap-3 min-h-0">
            <pre
              className="text-xs font-mono bg-surface-alt border border-soft rounded-lg p-3 overflow-auto max-h-[min(60vh,520px)] whitespace-pre-wrap break-words text-primary"
              tabIndex={0}
            >
              {pipelineDebugModal.payload
                ? JSON.stringify(pipelineDebugModal.payload, null, 2)
                : ''}
            </pre>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPipelineDebugModal({ open: false, payload: null })}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Confirmation Modal - replaces browser alert/confirm */}
      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={modal.close}
        {...modal.modalProps}
      />
      
      {/* Subtle toast notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-surface text-primary border border-soft shadow-lg text-sm rounded-lg backdrop-blur-sm">
            <Check className="w-4 h-4 text-ok" />
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  )
}

